/*
@license
  Concept by RoarTheme (https://roartheme.co)
  Access unminified JS in assets/theme.js

  Use this event listener to run your own JS outside of this file.
  Documentation - https://roartheme.co/blogs/concept/javascript-events-for-developers

  document.addEventListener('page:loaded', function() {
    // Page has loaded and theme assets are ready
  });
*/

window.theme = window.theme || {};
window.Shopify = window.Shopify || {};

theme.config = {
  hasSessionStorage: true,
  hasLocalStorage: true,
  mqlSmall: false,
  mediaQuerySmall: 'screen and (max-width: 767px)',
  motionReduced: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  isTouch: ('ontouchstart' in window) || (navigator.maxTouchPoints > 0) || (navigator.msMaxTouchPoints > 0),
  rtl: document.documentElement.getAttribute('dir') === 'rtl' ? true : false
};

theme.supportsPassive = false;

try {
  const opts = Object.defineProperty({}, 'passive', {
    get: function() {
      theme.supportsPassive = true;
    }
  });
  window.addEventListener('testPassive', null, opts);
  window.removeEventListener('testPassive', null, opts);
} catch (e) {}

document.documentElement.classList.add(theme.config.isTouch ? 'touch' : 'no-touch');
console.log(theme.settings.themeName + ' theme (' + theme.settings.themeVersion + ') by RoarTheme | Learn more at https://roartheme.co');

(function () {
  /*============================================================================
    Things that require DOM to be ready
  ==============================================================================*/
  theme.DOMready = function (callback) {
    if (document.readyState != 'loading') callback();
    else document.addEventListener('DOMContentLoaded', callback);
  }

  theme.headerGroup = {
    rounded: () => {
      return Array.from(document.querySelectorAll('.shopify-section-group-header-group .section--rounded'));
    },
    sections: () => {
      return Array.from(document.querySelectorAll('.shopify-section-group-header-group'));
    },
    init: () => {
      let previousSection = null;
      const rounded = theme.headerGroup.rounded();
      const sections = theme.headerGroup.sections();

      sections.forEach((shopifySection, index) => {
        const section = shopifySection.querySelector('.section');
        if (section) {
          section.classList.remove('section--next-rounded');
          section.classList.remove('section--first-rounded');
          section.classList.remove('section--last-rounded');

          if (section.classList.contains('section--rounded')) {
            if (index === 0) {
              section.classList.add('section--first-rounded');
            }
  
            if (index === rounded.length - 1) {
              section.classList.add('section--last-rounded');
            }

            if (previousSection && previousSection.classList.contains('section--rounded')) {
              previousSection.classList.add('section--next-rounded');
            }
          }

          if (index === sections.length - 1) {
            const nextSection = document.querySelector('.main-content .shopify-section:first-child .section');
            if (nextSection && nextSection.classList.contains('section--rounded')) {
              section.classList.add('section--next-rounded');
            }
          }

          previousSection = section;
        }
      });
    }
  };

  theme.a11y = {
    trapFocusHandlers: {},

    getFocusableElements: (container) => {
      return Array.from(
        container.querySelectorAll(
          'summary, a[href], button:enabled, [tabindex]:not([tabindex^="-"]), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe'
        )
      );
    },

    trapFocus: (container, elementToFocus = container) => {
      const elements = theme.a11y.getFocusableElements(container);
      const first = elements[0];
      const last = elements[elements.length - 1];

      theme.a11y.removeTrapFocus();

      theme.a11y.trapFocusHandlers.focusin = (event) => {
        if (event.target !== container && event.target !== last && event.target !== first) return;

        document.addEventListener('keydown', theme.a11y.trapFocusHandlers.keydown);
      };

      theme.a11y.trapFocusHandlers.focusout = function () {
        document.removeEventListener('keydown', theme.a11y.trapFocusHandlers.keydown);
      };

      theme.a11y.trapFocusHandlers.keydown = function (event) {
        if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
        // On the last focusable element and tab forward, focus the first element.
        if (event.target === last && !event.shiftKey) {
          event.preventDefault();
          first.focus();
        }

        //  On the first focusable element and tab backward, focus the last element.
        if ((event.target === container || event.target === first) && event.shiftKey) {
          event.preventDefault();
          last.focus();
        }
      };

      document.addEventListener('focusout', theme.a11y.trapFocusHandlers.focusout);
      document.addEventListener('focusin', theme.a11y.trapFocusHandlers.focusin);

      elementToFocus.focus();

      if (elementToFocus.tagName === 'INPUT' && ['search', 'text', 'email', 'url'].includes(elementToFocus.type) && elementToFocus.value) {
        elementToFocus.setSelectionRange(0, elementToFocus.value.length);
      }
    },
    
    removeTrapFocus: (elementToFocus = null) => {
      document.removeEventListener('focusin', theme.a11y.trapFocusHandlers.focusin);
      document.removeEventListener('focusout', theme.a11y.trapFocusHandlers.focusout);
      document.removeEventListener('keydown', theme.a11y.trapFocusHandlers.keydown);

      if (elementToFocus && typeof elementToFocus.focus === 'function') elementToFocus.focus();
    }
  };

  theme.utils = {
    throttle: (callback) => {
      let requestId = null, lastArgs;
      const later = (context) => () => {
        requestId = null;
        callback.apply(context, lastArgs);
      };
      const throttled = (...args) => {
        lastArgs = args;
        if (requestId === null) {
          requestId = requestAnimationFrame(later(this));
        }
      };
      throttled.cancel = () => {
        cancelAnimationFrame(requestId);
        requestId = null;
      };
      return throttled;
    },

    debounce: (fn, wait) => {
      let timer;
      return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn.apply(this, args), wait);
      };
    },

    waitForEvent: (element, eventName) => {
      return new Promise((resolve) => {
        const done = (event) => {
          if (event.target === element) {
            element.removeEventListener(eventName, done);
            resolve(event);
          }
        };
        element.addEventListener(eventName, done);
      });
    },

    fetchConfig: (type = 'json', method = 'POST') => {
      return {
        method: method,
        headers: { 'Content-Type': 'application/json', 'Accept': `application/${type}` }
      };
    },

    // /cart/add.js returns either a single added line item (has its own
    // price/final_price) or, for a multi-item/bundle add, { items: [...] }.
    // Treat the add as "free" only when every item just added has a
    // final price of 0, so the cart drawer can stay closed for a free
    // product/sample instead of popping open for a $0 line.
    isFreeCartAdd: (parsedState) => {
      const items = Array.isArray(parsedState?.items) ? parsedState.items : [parsedState];
      return items.length > 0 && items.every((item) => (item?.final_price ?? item?.price ?? 0) === 0);
    },

    // Shared Apple-style loading behavior for hover-button and quantity-label:
    // the label never gets swapped out, and a request that resolves quickly
    // (the common case) never shows a spinner at all - only one still in
    // flight past QUIET_LOADER_DELAY gets the subtle trailing indicator,
    // cleared instantly on resolution rather than held for a minimum duration.
    handleQuietBusyChange: (el, isBusy) => {
      const QUIET_LOADER_DELAY = 1000;

      if (el._quietLoaderTimer) {
        clearTimeout(el._quietLoaderTimer);
        el._quietLoaderTimer = null;
      }

      if (isBusy) {
        el._quietLoaderTimer = setTimeout(() => {
          el._quietLoaderTimer = null;
          el.classList.add('is-quiet-busy');
        }, QUIET_LOADER_DELAY);
      }
      else {
        el.classList.remove('is-quiet-busy');
      }
    },

    cartMutationQueue: Promise.resolve(),

    queueCartMutation: (task) => {
      const mutation = theme.utils.cartMutationQueue.then(task, task);
      theme.utils.cartMutationQueue = mutation.catch(() => {});
      return mutation;
    },

    cartSectionDocuments: new WeakMap(),

    getCartSection: (sections, sectionId) => {
      if (!sections || typeof sections !== 'object') return null;
      const html = sections?.[sectionId];
      if (typeof html !== 'string' || !html) return null;

      let documents = theme.utils.cartSectionDocuments.get(sections);
      if (!documents) {
        documents = new Map();
        theme.utils.cartSectionDocuments.set(sections, documents);
      }
      let section = documents.get(sectionId);
      if (!section || section.html !== html) {
        section = { html, document: new DOMParser().parseFromString(html, 'text/html') };
        documents.set(sectionId, section);
      }
      return section.document;
    },

    getCartFromBundledSections: (sections) => {
      for (const sectionId of Object.keys(sections || {})) {
        const cartState = theme.utils.getCartSection(sections, sectionId)?.querySelector('[data-cart-state]');
        if (!cartState) continue;

        try {
          const cart = JSON.parse(cartState.textContent);
          if (Array.isArray(cart.items) && Number.isSafeInteger(cart.item_count)) return cart;
        }
        catch (error) {
          console.error(error);
        }
      }
      return null;
    },

    getCartState: async (parsedState) => {
      const sectionIds = Object.keys(parsedState.sections || {});
      if (sectionIds.some(id => !parsedState.sections[id])) {
        const query = new URLSearchParams({ sections: sectionIds.join(',') });
        const response = await fetch(`${theme.routes.root_url}?${query}`);
        if (!response.ok) throw new Error(theme.cartStrings.error);
        parsedState.sections = await response.json();
      }

      const cart = theme.utils.getCartFromBundledSections(parsedState.sections);
      if (cart) return cart;

      const response = await fetch(theme.routes.cart_url, theme.utils.fetchConfig('json', 'GET'));
      if (!response.ok) throw new Error(theme.cartStrings.error);
      return response.json();
    },

    postLink: (path, options) => {
      options = options || {};
      const method = options['method'] || 'post';
      const params = options['parameters'] || {};
    
      const form = document.createElement("form");
      form.setAttribute("method", method);
      form.setAttribute("action", path);
    
      for (const key in params) {
        const hiddenField = document.createElement("input");
        hiddenField.setAttribute("type", "hidden");
        hiddenField.setAttribute("name", key);
        hiddenField.setAttribute("value", params[key]);
        form.appendChild(hiddenField);
      }
      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    },

    postLink2: (path, options) => {
      options = options || {};
      const method = options['method'] || 'post';
      const params = options['parameters'] || {};
    
      const form = document.createElement("form");
      form.setAttribute("method", method);
      form.setAttribute("action", path);
    
      for (const key in params) {
        if (typeof params[key] === 'string') {
          const hiddenField = document.createElement("input");
          hiddenField.setAttribute("type", "hidden");
          hiddenField.setAttribute("name", key);
          hiddenField.setAttribute("value", params[key]);
          form.appendChild(hiddenField);
        }
        else {
          for (const index in params[key]) {
            for (const key2 in params[key][index]) {
              const hiddenField = document.createElement("input");
              hiddenField.setAttribute("type", "hidden");
              hiddenField.setAttribute("name", `${key}[${index}][${key2}]`);
              hiddenField.setAttribute("value", params[key][index][key2]);
              form.appendChild(hiddenField);
            }
          }
        }
      }
      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    },

    sectionId: (element) => {
      if (element.hasAttribute('data-section-id')) return element.getAttribute('data-section-id');

      element = element.classList.contains('shopify-section') ? element : element.closest('.shopify-section');
      return element.id.replace('shopify-section-', '');
    },

    imageLoaded: (imageOrArray) => {
      if (!imageOrArray) {
        return Promise.resolve();
      }
      imageOrArray = imageOrArray instanceof Element ? [imageOrArray] : Array.from(imageOrArray);
      return Promise.all(imageOrArray.map((image) => {
        return new Promise((resolve) => {
          if (image.tagName === "IMG" && image.complete || !image.offsetParent) {
            resolve();
          } else {
            image.onload = () => resolve();
          }
        });
      }));
    },

    visibleMedia: (media) => {
      return Array.from(media).find((item) => {
        const style = window.getComputedStyle(item);
        return style.display !== 'none';
      });
    },

    setScrollbarWidth: () => {
      const scrollbarWidth = window.innerWidth - document.body.clientWidth;
      document.documentElement.style.setProperty('--scrollbar-width', `${scrollbarWidth > 0 ? scrollbarWidth : 0}px`);
    },

    externalLinksNewTab: () => {
      if (theme.settings.externalLinksNewTab) {
        document.addEventListener('click', (evt) => {
          const link = evt.target.tagName === 'A' ? evt.target : evt.target.closest('a');
          if (link && link.tagName === 'A' && window.location.hostname !== new URL(link.href).hostname) {
            link.target = '_blank';
          }
        });
      }
    }
  };

  theme.HTMLUpdateUtility = {
    /**
     * Used to swap an HTML node with a new node.
     * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
     *
     * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
     */
    viewTransition: (oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) => {
      preProcessCallbacks?.forEach((callback) => callback(newContent));

      const newNodeWrapper = document.createElement('div');
      theme.HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
      const newNode = newNodeWrapper.firstChild;

      // dedupe IDs
      const uniqueKey = Date.now();
      oldNode.querySelectorAll('[id], [form]').forEach((element) => {
        element.id && (element.id = `${element.id}-${uniqueKey}`);
        element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
      });

      oldNode.parentNode.insertBefore(newNode, oldNode);
      oldNode.style.display = 'none';

      postProcessCallbacks?.forEach((callback) => callback(newNode));

      setTimeout(() => oldNode.remove(), 500);
    },

    // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
    setInnerHTML: (element, html) => {
      element.innerHTML = html;
      element.querySelectorAll('script').forEach((oldScriptTag) => {
        const newScriptTag = document.createElement('script');
        Array.from(oldScriptTag.attributes).forEach((attribute) => {
          newScriptTag.setAttribute(attribute.name, attribute.value);
        });
        newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
        oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
      });
    }
  };

  theme.Currency = {
    formatMoney: (cents, format = "") => {
      if (typeof cents === "string") {
        cents = cents.replace(".", "");
      }
      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/, formatString = format || window.themeVariables.settings.moneyFormat;
      function defaultTo(value2, defaultValue) {
        return value2 == null || value2 !== value2 ? defaultValue : value2;
      }
      function formatWithDelimiters(number, precision, thousands, decimal) {
        precision = defaultTo(precision, 2);
        thousands = defaultTo(thousands, ",");
        decimal = defaultTo(decimal, ".");
        if (isNaN(number) || number == null) {
          return 0;
        }
        number = (number / 100).toFixed(precision);
        let parts = number.split("."), dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands), centsAmount = parts[1] ? decimal + parts[1] : "";
        return dollarsAmount + centsAmount;
      }
      let value = "";
      switch (formatString.match(placeholderRegex)[1]) {
        case "amount":
          value = formatWithDelimiters(cents, 2);
          break;
        case "amount_no_decimals":
          value = formatWithDelimiters(cents, 0);
          break;
        case "amount_with_space_separator":
          value = formatWithDelimiters(cents, 2, " ", ".");
          break;
        case "amount_with_comma_separator":
          value = formatWithDelimiters(cents, 2, ".", ",");
          break;
        case "amount_with_apostrophe_separator":
          value = formatWithDelimiters(cents, 2, "'", ".");
          break;
        case "amount_no_decimals_with_comma_separator":
          value = formatWithDelimiters(cents, 0, ".", ",");
          break;
        case "amount_no_decimals_with_space_separator":
          value = formatWithDelimiters(cents, 0, " ");
          break;
        case "amount_no_decimals_with_apostrophe_separator":
          value = formatWithDelimiters(cents, 0, "'");
          break;
        default:
          value = formatWithDelimiters(cents, 2);
          break;
      }
      if (formatString.indexOf("with_comma_separator") !== -1) {
        return formatString.replace(placeholderRegex, value);
      } else {
        return formatString.replace(placeholderRegex, value);
      }
    }
  };

  theme.pubsub = {
    PUB_SUB_EVENTS: {
      cartUpdate: 'cartUpdate',
      quantityUpdate: 'quantityUpdate',
      variantChange: 'variantChange',
      cartError: 'cartError',
      discountUpdate: 'discountUpdate',
      facetUpdate: 'facetUpdate',
      quantityRules: 'quantityRules',
      quantityBoundries: 'quantityBoundries',
      optionValueSelectionChange: 'optionValueSelectionChange',
    },

    subscribers: {},

    subscribe: (eventName, callback) => {
      if (theme.pubsub.subscribers[eventName] === undefined) {
        theme.pubsub.subscribers[eventName] = [];
      }

      theme.pubsub.subscribers[eventName] = [...theme.pubsub.subscribers[eventName], callback];

      return function unsubscribe() {
        theme.pubsub.subscribers[eventName] = theme.pubsub.subscribers[eventName].filter((cb) => {
          return cb !== callback
        });
      }
    },

    publish: (eventName, data) => {
      if (theme.pubsub.subscribers[eventName]) {
        theme.pubsub.subscribers[eventName].forEach((callback) => {
          callback(data);
        })
      }
    }
  };

  // Add-to-cart feedback, shared by every add path (ProductForm, its
  // batched quick adds, ProductBundle). An add that leaves the cart drawer
  // closed - a free quick add, or a store without a drawer - used to show
  // nothing but the in-flight look (the button's grey aria-disabled style)
  // and then the same "Add to cart" again. Now:
  // - pending(button): while the request runs the button keeps the colours
  //   it had when it was pressed (no disabled look, and no fill or tint
  //   across it);
  // - added(button, { drawer }): the header bag nudges and its count pops;
  //   when no drawer opened, the button rolls to a green "Added" with a
  //   check drawing in, holds it, then rolls back to its own label, and a
  //   polite live region says so (focus stays where it is);
  // - settle(button): drops a still-pending look, e.g. on an error, so the
  //   error / sold-out state shows exactly as before.
  // State lives in data-cart-add on the button; the look, its motion and
  // the reduced-motion fallback are in apple-cart.css (end of file).
  theme.cartFeedback = (function () {
    const HOLD = 1600;
    const ROLL_BACK = 460;
    const BUMP = 900;
    const REST_PROPS = ['--cart-add-rest-bg', '--cart-add-rest-img', '--cart-add-rest-ink'];
    const timers = new WeakMap();
    let region = null;
    let bumpQueued = false;
    let bumpTimer = null;

    const strings = () => {
      const custom = theme.cartAddStrings || {};
      const english = /^en\b/i.test(document.documentElement.lang || 'en');
      const itemAdded = theme.quickOrderListStrings?.itemAdded;
      return {
        // No translated "Added" in the locale files yet: other languages
        // get the check on its own, which reads the same.
        added: custom.added ?? (english ? 'Added' : ''),
        announce: custom.addedToCart ?? (english || !itemAdded ? 'Added to cart' : itemAdded.replace('[quantity]', '1'))
      };
    };

    const liveRegion = () => {
      if (!region || !region.isConnected) {
        region = document.createElement('div');
        region.className = 'sr-only';
        region.setAttribute('role', 'status');
        region.setAttribute('aria-live', 'polite');
        document.body.appendChild(region);
      }
      return region;
    };

    const announce = (text) => {
      const live = liveRegion();
      live.textContent = '';
      // set on a later task so a second add in a row is announced again
      setTimeout(() => { live.textContent = text; }, 80);
    };

    const clearTimers = (button) => {
      (timers.get(button) || []).forEach(clearTimeout);
      timers.delete(button);
    };

    // A quick view closes itself on cartUpdate, so its confirmation goes
    // to the card button that opened it.
    const targetOf = (button) => {
      const modal = button?.closest?.('quick-view');
      const opener = modal && !modal.hasAttribute('open') ? modal.activeElement : null;
      return opener?.isConnected && opener.classList?.contains('button') ? opener : button;
    };

    const canMorph = (button) => !!button?.isConnected && button.classList.contains('button') && !!button.querySelector(':scope > .btn-text');

    const layerOf = (button) => {
      let layer = button.querySelector(':scope > .cart-add');
      if (!layer) {
        layer = document.createElement('span');
        layer.className = 'cart-add';
        layer.setAttribute('aria-hidden', 'true');
        layer.innerHTML = '<span class="cart-add__done"><svg class="cart-add__check" viewBox="3.6 5.5 12.8 9.5" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" focusable="false"><path pathLength="1" d="M4.75 10.4 8.3 13.9 15.25 6.6"/></svg><span class="cart-add__label"></span></span>';
        // the layer covers the button itself, so "Added" and its check
        // centre on it, whatever the button skin's own positioning
        if (window.getComputedStyle(button).position === 'static') button.style.position = 'relative';
        button.appendChild(layer);
      }
      return layer;
    };

    // The colours the button shows right now (resting, or hovered when it
    // was pressed), so the pending and roll-back states can hold them
    // against the grey aria-disabled/aria-busy styles.
    const captureRest = (button) => {
      if (button.hasAttribute('data-cart-add')) return;
      const style = window.getComputedStyle(button);
      button.style.setProperty('--cart-add-rest-bg', style.backgroundColor);
      button.style.setProperty('--cart-add-rest-img', style.backgroundImage);
      button.style.setProperty('--cart-add-rest-ink', style.color);
    };

    const reset = (button) => {
      clearTimers(button);
      button.removeAttribute('data-cart-add');
      REST_PROPS.forEach((prop) => button.style.removeProperty(prop));
      if (!button.getAttribute('style')) button.removeAttribute('style');
    };

    const bump = () => {
      if (bumpQueued) return;
      bumpQueued = true;
      // once per add, however many buttons one batched request confirms
      queueMicrotask(() => {
        bumpQueued = false;
        clearTimeout(bumpTimer);
        const actions = [];
        document.querySelectorAll('cart-count').forEach((count) => {
          if (count.closest('cart-drawer, .premiere-cart')) return;
          // the element holding the bag icon and its count (the header
          // link itself, or the mobile dock's icon wrapper)
          const action = count.parentElement;
          if (!action || actions.includes(action)) return;
          actions.push(action);
          // alternate the value so a bump restarts one still playing
          action.setAttribute('data-cart-bump', action.getAttribute('data-cart-bump') === 'a' ? 'b' : 'a');
        });
        bumpTimer = setTimeout(() => actions.forEach((action) => action.removeAttribute('data-cart-bump')), BUMP);
      });
    };

    return {
      pending(button) {
        // in the page before anything is said in it, so it gets announced
        liveRegion();
        if (!canMorph(button)) return;
        clearTimers(button);
        captureRest(button);
        layerOf(button);
        button.setAttribute('data-cart-add', 'pending');
      },

      settle(button) {
        if (button?.getAttribute?.('data-cart-add') === 'pending') reset(button);
      },

      added(button, { drawer = false } = {}) {
        bump();
        if (drawer) {
          this.settle(button);
          return;
        }

        const { added, announce: message } = strings();
        announce(message);

        const target = targetOf(button);
        if (target !== button) this.settle(button);
        if (!canMorph(target)) return;

        clearTimers(target);
        captureRest(target);
        const label = layerOf(target).querySelector('.cart-add__label');
        label.textContent = added;
        label.hidden = !added;
        target.setAttribute('data-cart-add', 'added');

        timers.set(target, [
          setTimeout(() => target.setAttribute('data-cart-add', 'out'), HOLD),
          setTimeout(() => reset(target), HOLD + ROLL_BACK)
        ]);
      }
    };
  })();

  theme.scrollShadow = {
    Updater: (function () {
      class Updater {
        constructor(targetElement) {
          this.cb = () => this.update(targetElement);
          this.rO = new ResizeObserver(this.cb);
          this.mO = new MutationObserver(() => this.on(this.el));
        }
        on(element) {
          if (this.el) {
            this.el.removeEventListener("scroll", this.cb);
            this.rO.disconnect();
            this.mO.disconnect();
          }
          if (!element)
            return;
          if (element.nodeName === "TABLE" && !/scroll|auto/.test(getComputedStyle(element).getPropertyValue("overflow"))) {
            this.rO.observe(element);
            element = element.tBodies[0];
          }
          element.addEventListener("scroll", this.cb);
          [element, ...element.children].forEach((el) => this.rO.observe(el));
          this.mO.observe(element, { childList: true });
          this.el = element;
        }
        update(targetElement) {
          const scrollTop = this.el.scrollTop;
          const scrollBottom = this.el.scrollHeight - this.el.offsetHeight - this.el.scrollTop;
          const scrollLeft = this.el.scrollLeft;
          const scrollRight = this.el.scrollWidth - this.el.offsetWidth - this.el.scrollLeft * (theme.config.rtl ? -1 : 1);
          let cssText = `--t: ${Math.floor(scrollTop) > 0 ? 1 : 0}; --b: ${Math.floor(scrollBottom) > 0 ? 1 : 0}; --l: ${Math.floor(scrollLeft) > 0 ? 1 : 0}; --r: ${Math.floor(scrollRight) > 0 ? 1 : 0};`;
          if (this.el.nodeName === "TBODY") {
            const clientRect = this.el.getBoundingClientRect();
            const rootClientRect = this.el.parentElement.getBoundingClientRect();
            cssText += `top: ${clientRect.top - rootClientRect.top}px; bottom: ${rootClientRect.bottom - clientRect.bottom}px; left: ${clientRect.left - rootClientRect.left}px; right: ${rootClientRect.right - clientRect.right}px;`;
          }
          this.pendingCssText = cssText;
          if (this.frame !== undefined) return;
          this.frame = requestAnimationFrame(() => {
            this.frame = undefined;
            if (targetElement.style.cssText !== this.pendingCssText) targetElement.style.cssText = this.pendingCssText;
          });
        }
      }

      return Updater;
    })()
  };
  
  theme.cookiesEnabled = function () {
    let cookieEnabled = navigator.cookieEnabled;

    if (!cookieEnabled) {
      document.cookie = 'testcookie';
      cookieEnabled = document.cookie.indexOf('testcookie') !== -1;
    }
    return cookieEnabled;
  };

  theme.isStorageSupported = function (type) {
    // Return false if we are in an iframe without access to sessionStorage
    if (window.self !== window.top) {
      return false;
    }

    const testKey = 'test';
    let storage;
    if (type === 'session') {
      storage = window.sessionStorage;
    }
    if (type === 'local') {
      storage = window.localStorage;
    }

    try {
      storage.setItem(testKey, '1');
      storage.removeItem(testKey);
      return true;
    }
    catch (error) {
      // Do nothing, this may happen in Safari in incognito mode
      return false;
    }
  };

  theme.getElementWidth = function (element, value) {
    const style = window.getComputedStyle(element);

    const text = document.createElement('span');
    text.style.fontFamily = style.fontFamily;
    text.style.fontSize = style.fontSize;
    text.style.fontWeight = style.fontWeight;
    text.style.visibility = 'hidden';
    text.style.position = 'absolute';
    text.innerHTML = value;

    document.body.appendChild(text);
    const width = text.getBoundingClientRect().width;
    text.remove();

    return width;
  };

  theme.HoverButton = (function () {
    class HoverButton {
      constructor(container) {
        this.container = container;
      }

      get btnFill() {
        return this.container.querySelector('[data-fill]');
      }

      load() {
        if (theme.config.isTouch || document.body.getAttribute('data-button-hover') === 'none') return;

        this.container.addEventListener('mouseenter', this.onEnterHandler.bind(this));
        this.container.addEventListener('mouseleave', this.onLeaveHandler.bind(this));
      }

      onEnterHandler() {
        if (this.btnFill) {
          // Ease-out so the fill starts covering the button the moment the
          // pointer arrives (Motion's default 'ease' crawled through its first
          // ~120ms, which read as lag). If the pointer comes back while the
          // fill is still sweeping out, reverse it from where it is instead of
          // snapping it back below the button and starting over.
          const reversing = this.fillLeaveEndsAt > performance.now();
          this.fillLeaveEndsAt = 0;
          Motion.animate(this.btnFill, { y: reversing ? '0%' : ['76%', '0%'] }, { duration: theme.config.motionReduced ? 0 : 0.5, easing: [0.22, 1, 0.36, 1] });
        }
      }

      onLeaveHandler() {
        if (this.btnFill) {
          // Exits a little faster than the entrance, on the same ease-out
          // curve so the fill starts clearing immediately too.
          const duration = theme.config.motionReduced ? 0 : 0.4;
          this.fillLeaveEndsAt = performance.now() + duration * 1000;
          Motion.animate(this.btnFill, { y: '-76%' }, { duration, easing: [0.22, 1, 0.36, 1] });
        }
      }

      unload() {
        // todo
      }
    }

    return HoverButton;
  })();

  theme.MagnetButton = class {
    constructor(container) {
      this.container = container;
    }

    load() {}

    unload() {}
  };

  theme.RevealButton = (function () {
    class RevealButton {
      constructor(container) {
        this.container = container;
        this.onEnter = this.onEnterHandler.bind(this);
        this.onLeave = this.onLeaveHandler.bind(this);
      }

      get btnReveal() {
        return this.container.querySelector('[data-reveal]');
      }

      load() {
        if (theme.config.isTouch) return;

        this.container.addEventListener('mouseenter', this.onEnter);
        this.container.addEventListener('mouseleave', this.onLeave);
      }

      onEnterHandler() {
        if (this.btnReveal) {
          Motion.animate(this.btnReveal, {
            opacity: 1
          }, { duration: theme.config.motionReduced ? 0 : 0.2, easing: [0.2, 0.8, 0.2, 1] });
        }
      }

      onLeaveHandler() {
        if (this.btnReveal) {
          Motion.animate(this.btnReveal, { opacity: 0 }, { duration: theme.config.motionReduced ? 0 : 0.2, easing: [0.2, 0.8, 0.2, 1] });
        }
      }

      unload() {
        this.container.removeEventListener('mouseenter', this.onEnter);
        this.container.removeEventListener('mouseleave', this.onLeave);
      }
    }

    return RevealButton;
  })();

  theme.Animation = (function () {
    class Animation {
      constructor(container) {
        this.container = container;
      }

      get immediate() {
        return this.container.hasAttribute('data-immediate');
      }
    
      get media() {
        return Array.from(this.container.querySelectorAll('img, iframe, svg, g-map'));
      }
    
      get type() {
        return this.container.hasAttribute('data-animate') ? this.container.getAttribute('data-animate') : 'fade-up';
      }
    
      get delay() {
        return this.container.hasAttribute('data-animate-delay') ? parseInt(this.container.getAttribute('data-animate-delay')) / 1000 : 0;
      }
    
      get paused() {
        return this.container.hasAttribute('paused');
      }

      beforeLoad() {
        if (this.type === 'none' || this.paused) return;

        switch (this.type) {
          case 'fade-in':
            Motion.animate(this.container, { opacity: 0 }, { duration: 0 });
            break;

          case 'fade-up':
          case 'fade-up-fast':
            Motion.animate(this.container, { transform: 'translateY(min(2rem, 90%))', opacity: 0 }, { duration: 0 });
            break;

          case 'fade-up-large':
            Motion.animate(this.container, { transform: 'translateY(90%)', opacity: 0 }, { duration: 0 });
            break;

          case 'zoom-out':
            // 1.15 (was 1.3): the expo-out below covers most of the zoom in its
            // first ~150ms, so a 30% overscale read as a lurch on full-bleed
            // banner/slideshow images - a smaller settle still reads as zoom-out.
            Motion.animate(this.container, { transform: 'scale(1.15)' }, { duration: 0 });
            break;
        }
      }

      async load() {
        if (this.type === 'none' || this.paused) return;
    
        switch (this.type) {
          case 'fade-in':
            await Motion.animate(this.container, { opacity: 1 }, { duration: 1.5, delay: this.delay, easing: [0.16, 1, 0.3, 1] }).finished;
            break;

          case 'fade-up':
            await Motion.animate(this.container, { transform: 'translateY(0)', opacity: 1 }, { duration: 1.5, delay: this.delay, easing: [0.16, 1, 0.3, 1] }).finished;
            break;

          case 'fade-up-fast':
            await Motion.animate(this.container, { transform: 'translateY(0)', opacity: 1 }, { duration: 0.9, delay: this.delay, easing: [0.16, 1, 0.3, 1] }).finished;
            break;

          case 'fade-up-large':
            await Motion.animate(this.container, { transform: 'translateY(0)', opacity: 1 }, { duration: 1, delay: this.delay, easing: [0.16, 1, 0.3, 1] }).finished;
            break;

          case 'zoom-out':
            await Motion.animate(this.container, { transform: 'scale(1)' }, { duration: 1.3, delay: this.delay, easing: [0.16, 1, 0.3, 1] }).finished;
            break;
        }

        this.container.classList.add('animated');
      }

      async reset(duration) {
        if (this.type === 'none') return;

        switch (this.type) {
          case 'fade-in':
            await Motion.animate(this.container, { opacity: 0 }, { duration: duration ? duration : 1.5, delay: this.delay, easing: duration ? 'none' : [0.16, 1, 0.3, 1] }).finished;
            break;
    
          case 'fade-up':
            await Motion.animate(this.container, { transform: 'translateY(max(-2rem, -90%))', opacity: 0 }, { duration: duration ? duration : 1.5, delay: this.delay, easing: duration ? 'none' : [0.16, 1, 0.3, 1] }).finished;
            break;

          case 'fade-up-fast':
            await Motion.animate(this.container, { transform: 'translateY(max(-2rem, -90%))', opacity: 0 }, { duration: duration ? duration : 0.9, delay: this.delay, easing: duration ? 'none' : [0.16, 1, 0.3, 1] }).finished;
            break;

          case 'fade-up-large':
            await Motion.animate(this.container, { transform: 'translateY(-90%)', opacity: 0 }, { duration: duration ? duration : 1, delay: this.delay, easing: duration ? 'none' : [0.16, 1, 0.3, 1] }).finished;
            break;
    
          case 'zoom-out':
            await Motion.animate(this.container, { transform: 'scale(0)' }, { duration: duration ? duration : 1.3, delay: this.delay, easing: duration ? 'none' : [0.16, 1, 0.3, 1] }).finished;
            break;
        }
    
        this.container.classList.remove('animated');
      }

      reload() {
        if (this.type === 'none') return;

        this.container.removeAttribute('paused');
        this.beforeLoad();
        this.load();
      }

      unload() {
        // todo
      }
    }

    return Animation;
  })();

  theme.Carousel = (function () {
    class Carousel {
      constructor(container, options, navigation) {
        this.container = container;
        this.options = options;
        this.prevButton = navigation.previous;
        this.nextButton = navigation.next;
      }

      load() {
        this.slider = new Flickity(this.container, this.options);

        this.prevButton.addEventListener('click', this.onPrevButtonClick.bind(this));
        this.nextButton.addEventListener('click', this.onNextButtonClick.bind(this));

        this.slider.on('dragStart', this.onDragStartHandler.bind(this));
        this.slider.on('select', this.onSelectHandler.bind(this));
      }

      onDragStartHandler() {
        this.prevRemoveTransform();
        this.nextRemoveTransform();
      }

      onSelectHandler() {
        if (!this.slider.slides[this.slider.selectedIndex - 1]) {
          this.prevButton.disabled = true;
          this.nextButton.disabled = false;
        }
        else if (!this.slider.slides[this.slider.selectedIndex + 1]) {
          this.prevButton.disabled = false;
          this.nextButton.disabled = true;
        }
        else {
          this.prevButton.disabled = false;
          this.nextButton.disabled = false;
        }
      }

      onPrevButtonClick(event) {
        event.preventDefault();

        this.slider.previous();
        this.nextRemoveTransform();
      }

      onNextButtonClick(event) {
        event.preventDefault();

        this.slider.next();
        this.prevRemoveTransform();
      }

      prevRemoveTransform() {
        this.prevButton.style.transform = null;
        this.prevButton.querySelector('[data-fill]').style.transform = null;
      }

      nextRemoveTransform() {
        this.nextButton.style.transform = null;
        this.nextButton.querySelector('[data-fill]').style.transform = null;
      }

      unload() {
        // todo
      }
    }

    return Carousel;
  })();

  // Delay JavaScript until user interaction
  theme.initWhenVisible = (function() {
    class ScriptLoader {
      constructor(callback, delay = 5000) {
        this.callback = callback;
        this.triggered = false;
        this.timeoutId = null;
        
        this.interactionEvents = ['click', 'mousemove', 'keydown', 'touchstart', 'touchmove', 'wheel'];
        this.handleInteraction = this.handleInteraction.bind(this);
        
        this.interactionEvents.forEach(eventType => {
          window.addEventListener(eventType, this.handleInteraction, { passive: true });
        });
        
        this.timeoutId = setTimeout(() => {
          if (!this.triggered) {
            this.handleInteraction({ type: 'timeout' });
          }
        }, delay);
      }

      handleInteraction(event) {
        if (this.triggered) return;
    
        this.triggered = true;
        this.callback(event);
        this.cleanup();
      }

      cleanup() {
        this.interactionEvents.forEach(eventType => {
          window.removeEventListener(eventType, this.handleInteraction, { passive: true });
        });
        
        clearTimeout(this.timeoutId);
        this.timeoutId = null;
        this.callback = null;
      }
    }

    return ScriptLoader;
  })();

  // Improve initial load time by skipping the rendering of offscreen content
  new theme.initWhenVisible(() => {
    document.body.removeAttribute('data-page-rendering');
  });

  // Touch devices skip rendering sections 3+ until the first interaction (above).
  // While the page is still at the very top, render the sections that start at
  // least one full viewport below the fold during idle time after load, one per
  // idle period, so the first tap/scroll doesn't have to lay them all out at once.
  // Nothing visible changes: content below the viewport grows only downwards,
  // and a scrolled page (back/forward restoration, #anchor) is left exactly as today.
  // Only where real idle time can be detected (requestIdleCallback, so not
  // Safari) and without a deadline, so a step never lands while the visitor is
  // interacting; elsewhere the sections render at the first interaction as before.
  if (!Shopify.designMode && theme.config.isTouch && window.requestIdleCallback) {
    window.addEventListener('load', () => {
      // Not on a back/forward load: the browser restores its scroll position
      // there (js-variables.liquid only starts reloads at the top), even while
      // it is still at 0.
      if (performance.getEntriesByType?.('navigation')[0]?.type === 'back_forward') return;

      const schedule = (task) => window.requestIdleCallback(task);
      const sections = Array.from(document.querySelectorAll('#MainContent .shopify-section+.shopify-section~.shopify-section'));
      const renderNext = () => {
        if (!document.body.hasAttribute('data-page-rendering')) return;
        if (window.scrollY !== 0) return;
        while (sections.length) {
          const section = sections.shift();
          if (section.getBoundingClientRect().top < window.innerHeight * 2) continue; // near or on screen: untouched
          section.setAttribute('data-offscreen-rendered', '');
          schedule(renderNext);
          return;
        }
      };
      schedule(renderNext);
    }, { once: true });
  }

  // Improve iOS Safari resizing
  theme.windowWidth = window.innerWidth;
  window.addEventListener('resize', () => {
    const { innerWidth } = window;
    if (innerWidth !== theme.windowWidth) {
      theme.windowWidth = innerWidth;
      document.dispatchEvent(new CustomEvent('window:resize'));
    }
  });

  theme.DOMready(theme.headerGroup.init);
  theme.DOMready(theme.utils.setScrollbarWidth);
  theme.DOMready(theme.utils.externalLinksNewTab);
  document.addEventListener('window:resize', theme.utils.throttle(theme.utils.setScrollbarWidth));

  /*============================================================================
    Things that don't require DOM to be ready
  ==============================================================================*/

  theme.config.hasSessionStorage = theme.isStorageSupported('session');
  theme.config.hasLocalStorage = theme.isStorageSupported('local');

  // Trigger events when going between breakpoints
  const mql = window.matchMedia(theme.config.mediaQuerySmall);
  theme.config.mqlSmall = mql.matches;
  mql.onchange = (mql) => {
    if (mql.matches) {
      theme.config.mqlSmall = true;
      document.dispatchEvent(new CustomEvent('matchSmall'));
    }
    else {
      theme.config.mqlSmall = false;
      document.dispatchEvent(new CustomEvent('unmatchSmall'));
    }
  }

  // Page has loaded and theme assets are ready
  window.addEventListener('DOMContentLoaded', () => {
    document.body.classList.add('loaded');
    document.dispatchEvent(new CustomEvent('page:loaded'));
  });

  class HomePressFeedback {
    constructor() {
      this.selector = '.template-index .apple-home-banner .banner__box > .button';
      this.activeElement = null;
      this.states = new WeakMap();
      this.pendingNavigation = new WeakSet();

      document.addEventListener('pointerdown', this.onPointerDown.bind(this));
      document.addEventListener('pointerup', this.onPointerUp.bind(this));
      document.addEventListener('pointercancel', this.onPointerUp.bind(this));
      window.addEventListener('blur', this.onWindowBlur.bind(this));
      document.addEventListener('click', this.onClick.bind(this));
    }

    findElement(target) {
      return target?.closest?.(this.selector) || null;
    }

    getAnimationElement(element) {
      return element.closest('.recently-viewed .product-card') || element;
    }

    getAnimationStyles(element) {
      const styles = window.getComputedStyle(element);

      if (element.matches('.recently-viewed .product-card')) {
        return {
          restingStyle: {
            scale: !styles.scale || styles.scale === 'none' ? '1' : styles.scale,
            translate: !styles.translate || styles.translate === 'none' ? '0 0' : styles.translate
          },
          pressedStyle: { scale: '0.985', translate: '0 -1px' }
        };
      }

      return {
        restingStyle: { transform: styles.transform },
        pressedStyle: {
          transform: element.matches('.apple-home-banner .banner__box > .button')
            ? 'translateY(0) scale(0.985)'
            : 'translateY(-1px) scale(0.99)'
        }
      };
    }

    onPointerDown(event) {
      if (theme.config.motionReduced || event.button !== 0 || event.isPrimary === false) return;
      // Guard against a tap on a bento/banner/recently-viewed card
      // triggering this press feedback (and onClick's manual navigation
      // below) while a drawer or modal - search, cart, the mobile menu -
      // is open above it. ModalElement adds/removes has-modal-open(ing)
      // on the body for exactly this "something is covering the page"
      // window, so it's a reliable, already-existing signal to key off.
      if (document.body.classList.contains('has-modal-open') || document.body.classList.contains('has-modal-opening')) return;

      const interactiveElement = this.findElement(event.target);
      if (!interactiveElement) return;

      const element = this.getAnimationElement(interactiveElement);

      if (this.activeElement && this.activeElement !== element) this.release(this.activeElement);

      const previous = this.states.get(element);
      previous?.press?.cancel();
      previous?.release?.cancel();

      const { restingStyle, pressedStyle } = this.getAnimationStyles(element);
      const press = element.animate(
        [restingStyle, pressedStyle],
        { duration: 90, easing: 'cubic-bezier(0.32, 0.72, 0, 1)', fill: 'forwards' }
      );
      press.finished.catch(() => {});

      this.states.set(element, { restingStyle, pressedStyle, press, release: null });
      this.activeElement = element;
    }

    onPointerUp() {
      if (this.activeElement) this.release(this.activeElement);
    }

    onWindowBlur() {
      if (this.activeElement) this.release(this.activeElement);
    }

    release(element) {
      const state = this.states.get(element);
      this.activeElement = null;
      if (!state) return null;

      state.press?.cancel();
      state.release?.cancel();

      const release = element.animate(
        [state.pressedStyle, state.restingStyle],
        { duration: 180, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' }
      );
      release.finished.catch(() => {});
      release.finished.then(() => release.cancel(), () => {});
      state.release = release;
      this.states.set(element, state);
      return release;
    }

    canDelayNavigation(event, element) {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
      if (element.target && element.target !== '_self') return false;
      if (element.hasAttribute('download') || element.getAttribute('aria-disabled') === 'true') return false;

      const url = new URL(element.href, window.location.href);
      return url.origin === window.location.origin;
    }

    onClick(event) {
      if (document.body.classList.contains('has-modal-open') || document.body.classList.contains('has-modal-opening')) return;

      const interactiveElement = this.findElement(event.target);
      if (!interactiveElement || theme.config.motionReduced || !this.canDelayNavigation(event, interactiveElement)) return;

      const element = this.getAnimationElement(interactiveElement);
      if (this.pendingNavigation.has(interactiveElement)) {
        event.preventDefault();
        return;
      }

      let animation = this.states.get(element)?.release;
      if (!animation && event.detail === 0) {
        const { restingStyle, pressedStyle } = this.getAnimationStyles(element);
        animation = element.animate(
          [
            restingStyle,
            { ...pressedStyle, offset: 0.4 },
            restingStyle
          ],
          { duration: 240, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' }
        );
      }
      if (!animation) return;

      event.preventDefault();
      this.pendingNavigation.add(interactiveElement);
      const destination = interactiveElement.href;
      animation.finished
        .catch(() => {})
        .then(() => window.location.assign(destination));
    }
  }

  // The only element this acts on (.template-index .apple-home-banner .banner__box > .button)
  // exists on the index template alone (body class from layout/theme.liquid).
  if (document.body.classList.contains('template-index')) new HomePressFeedback();
})();

// Prevent vertical scroll while using flickity sliders
(function () {
  // The guard can only cancel touches that start inside a .flickity-slider, and
  // those exist only once Flickity has created an instance. Until then its
  // non-passive <body> touchmove just made every touch scroll wait for the main
  // thread, so install it at the later of the first interaction (as before) and
  // the first Flickity instance (every instance runs the Flickity.create hooks).
  // It stays on <body>: in Chromium, element-level non-passive touch listeners
  // under the header's fixed full-viewport overlay only get non-cancelable events.
  let interacted = false;
  let hasSlider = !(window.Flickity && Flickity.create);
  let installed = false;

  const install = () => {
    if (installed || !interacted || !hasSlider) return;
    installed = true;

    var e = !1;
    var t;

    document.body.addEventListener('touchstart', function (i) {
      if (!i.target.closest('.flickity-slider')) {
        return e = !1;
        void 0;
      }
      e = !0;
      t = {
        x: i.touches[0].pageX,
        y: i.touches[0].pageY
      }
    }, theme.supportsPassive ? { passive: true } : false);

    document.body.addEventListener('touchmove', function (i) {
      if (e && i.cancelable) {
        var n = {
          x: i.touches[0].pageX - t.x,
          y: i.touches[0].pageY - t.y
        };
        Math.abs(n.x) > Flickity.defaults.dragThreshold && i.preventDefault()
      }
    }, theme.supportsPassive ? { passive: false } : false);
  };

  new theme.initWhenVisible(() => {
    interacted = true;
    install();
  });

  if (!hasSlider) {
    Flickity.create.themeScrollGuard = function () {
      hasSlider = true;
      install();
    };
  }
})();

class LoadingBar extends HTMLElement {
  constructor() {
    super();

    document.addEventListener('page:loaded', () => {
      // Lift the cover quickly on an ease-out so the page reads as ready
      // almost at once; the old 1s default 'ease' kept it veiled - and hid
      // most of the page's own entrance animations - for a full second.
      Motion.animate(this, { opacity: 0 }, { duration: 0.5, easing: [0.22, 1, 0.36, 1] }).finished.then(() => {
        this.hidden = true;
      });
    });
  }
}
customElements.define('loading-bar', LoadingBar);

class MouseCursor extends HTMLElement {
  constructor() {
    super();

    if (theme.config.isTouch) return;

    this.config = {
      posX: 0,
      posY: 0,
    };

    document.addEventListener('mousemove', (event) => {
      this.config.posX += (event.clientX - this.config.posX) / 4;
      this.config.posY += (event.clientY - this.config.posY) / 4;

      this.style.setProperty('--x', `${this.config.posX}px`);
      this.style.setProperty('--y', `${this.config.posY}px`);
    });
  }
}
customElements.define('mouse-cursor', MouseCursor);

class CustomHeader extends HTMLElement {
  constructor() {
    super();

    this.initHandler = this.init.bind(this);
    this.resizeHandler = this.setHeight.bind(this);
  }

  get allowTransparent() {
    if (document.querySelector('.shopify-section:first-child [allow-transparent-header]')) {
      return true;
    }

    return false;
  }

  get headerSection() {
    return document.querySelector('.header-section');
  }

  connectedCallback() {
    this.init();
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(this.resizeHandler);
      this.resizeObserver.observe(this, { box: 'border-box' });
    }

    if (Shopify.designMode) {
      this.designModeSection = this.closest('.shopify-section');
      this.designModeSection.addEventListener('shopify:section:load', this.initHandler);
      this.designModeSection.addEventListener('shopify:section:unload', this.initHandler);
      this.designModeSection.addEventListener('shopify:section:reorder', this.initHandler);
    }
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;

    if (this.designModeSection) {
      this.designModeSection.removeEventListener('shopify:section:load', this.initHandler);
      this.designModeSection.removeEventListener('shopify:section:unload', this.initHandler);
      this.designModeSection.removeEventListener('shopify:section:reorder', this.initHandler);
      this.designModeSection = null;
    }
  }

  init() {
    this.setHeight();
    // theme.css: the transparent look before this ran (js-variables.liquid)
    document.documentElement.classList.toggle('transparent-header-first', this.allowTransparent);

    if (this.allowTransparent) {
      this.headerSection.classList.add('header-transparent');
      this.headerSection.classList.add('no-animate');

      setTimeout(() => {
        this.headerSection.classList.remove('no-animate');
      }, 500);
    }
    else {
      this.headerSection.classList.remove('header-transparent');
    }
  }

  setHeight() {
    requestAnimationFrame(() => {
      document.documentElement.style.setProperty('--header-height', this.getBoundingClientRect().height.toFixed(1) + 'px');

      if (this.classList.contains('header--center') && document.documentElement.style.getPropertyValue('--header-nav-height').length === 0) {
        document.documentElement.style.setProperty('--header-nav-height', Math.round(document.getElementById('MenuToggle')?.clientHeight) + 'px');
      }
    });
  }
}
customElements.define('custom-header', CustomHeader, { extends: 'header' });

class StickyHeader extends CustomHeader {
  constructor() {
    super();
    this.isScrolled = false; // Track scroll state to prevent class toggling
    this.buffer = 10; // Pixel buffer to create smooth transition zone
    this.scrollHandler = theme.utils.throttle(this.onScrollHandler.bind(this));
    this.transitionEndHandler = (event) => {
      if (event.propertyName === 'padding-block-end' || event.propertyName === 'padding-bottom') {
        this.updateStickyHeight();
      }
    };
  }

  get isAlwaysSticky() {
    return this.getAttribute('data-sticky-type') === 'always';
  }

  connectedCallback() {
    super.connectedCallback();

    // Initialize scroll tracking values
    this.currentScrollTop = 0;
    this.firstScrollTop = window.scrollY;
    this.initialHeaderHeight = this.getBoundingClientRect().height; // Cache initial height
    this.headerBounds = this.headerSection.getBoundingClientRect();

    this.connectedHeaderSection = this.headerSection;
    this.connectedHeaderSection.addEventListener('transitionend', this.transitionEndHandler);

    this.beforeInit();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('scroll', this.scrollHandler, false);
    this.connectedHeaderSection?.removeEventListener('transitionend', this.transitionEndHandler);
    this.connectedHeaderSection = null;
  }

  beforeInit() {
    this.headerSection.classList.add('header-sticky');
    this.headerSection.setAttribute('data-sticky-type', this.getAttribute('data-sticky-type'));

    if (this.isAlwaysSticky) {
      this.headerSection.classList.add('header-sticky');
    }

    window.addEventListener('scroll', this.scrollHandler, false);
  }

  setHeight() {
    super.setHeight();
    this.updateStickyHeight();
  }

  updateStickyHeight() {
    if (this.isScrolled) {
      document.documentElement.style.setProperty('--sticky-header-height', this.getBoundingClientRect().height.toFixed(1) + 'px');
    }
  }

  onScrollHandler() {
    const scrollTop = window.scrollY;

    // Calculate scroll thresholds based on initial header height (prevents changing thresholds)
    const scrollThreshold = this.headerBounds.top + this.firstScrollTop + this.initialHeaderHeight;
    const scrollThresholdNav = this.headerBounds.top + this.firstScrollTop + this.initialHeaderHeight * 2;

    // Hysteresis: Different thresholds for scrolling down vs up prevents rapid toggling
    const scrollDownThreshold = scrollThreshold + this.buffer;
    const scrollUpThreshold = scrollThreshold - this.buffer;

    // Toggle 'header-scrolled' class with buffer zone
    if (!this.isScrolled && scrollTop > scrollDownThreshold) {
      // Scrolling down past threshold
      this.isScrolled = true;
      this.headerSection.classList.add('header-scrolled');
      this.updateStickyHeight();
      document.dispatchEvent(new CustomEvent('header:scrolled', { bubbles: true, detail: { scrolled: true } }));
    }
    else if (this.isScrolled && scrollTop < scrollUpThreshold) {
      // Scrolling up past threshold
      this.isScrolled = false;
      this.headerSection.classList.remove('header-scrolled');
      this.headerSection.classList.remove('header-nav-scrolled');
      document.dispatchEvent(new CustomEvent('header:scrolled', { bubbles: true, detail: { scrolled: false } }));
    }

    // Add 'header-nav-scrolled' when scrolled past 2x header height
    if (this.isScrolled && scrollTop > scrollThresholdNav) {
      this.headerSection.classList.add('header-nav-scrolled');
      
      // Close any open dropdown menus
      this.headerSection.querySelectorAll('details').forEach((details) => {
        if (details.hasAttribute('open')) {
          details.open = false;
        }
      });
    }

    // Hide header when scrolling down, show when scrolling up - but only
    // once the scroll has travelled a few px in the new direction, so
    // trackpad inertia wobble or an iOS rubber-band bounce can't flicker
    // the header in and out. Travel is measured from where the direction
    // last changed, so even a slow scroll still toggles it.
    const scrollDelta = scrollTop - this.currentScrollTop;
    if (scrollDelta !== 0 && Math.sign(scrollDelta) !== this.scrollDirection) {
      this.scrollDirection = Math.sign(scrollDelta);
      this.scrollDirectionStart = this.currentScrollTop;
    }

    if (scrollTop > (this.headerBounds.bottom + this.firstScrollTop + 100)) {
      if (this.scrollDirection && Math.abs(scrollTop - this.scrollDirectionStart) >= 8) {
        // Scrolling down - hide header; scrolling up - show header
        this.headerSection.classList.toggle('header-hidden', this.scrollDirection > 0);
      }
    }
    else {
      // Near top of page - always show header
      this.headerSection.classList.remove('header-hidden');
    }

    // Update current scroll position for next comparison
    this.currentScrollTop = scrollTop;
  }
}
customElements.define('sticky-header', StickyHeader, { extends: 'header' });

class RevealLink extends HTMLAnchorElement {
  constructor() {
    super();

    this.revealButton = new theme.RevealButton(this);
    this.revealButton.load();
  }
}
customElements.define('reveal-link', RevealLink, { extends: 'a' });

class HoverLink extends HTMLAnchorElement {
  constructor() {
    super();

    this.hoverButton = new theme.HoverButton(this);
    this.hoverButton.load();
  }
}
customElements.define('hover-link', HoverLink, { extends: 'a' });

class MagnetLink extends HoverLink {
  constructor() {
    super();

    this.magnetButton = new theme.MagnetButton(this);
    this.magnetButton.load();
  }
}
customElements.define('magnet-link', MagnetLink, { extends: 'a' });

class HoverButton extends HTMLButtonElement {
  constructor() {
    super();
    
    this.hoverButton = new theme.HoverButton(this);
    this.hoverButton.load();

    this.busyForm = this.type === 'submit' ? this.form : null;
    this.formSubmitListener = () => this.setAttribute('aria-busy', 'true');
    this.pageShowListener = () => this.removeAttribute('aria-busy');
    this.addBusyListeners();

    Motion.inView(this, this.init.bind(this));
  }

  connectedCallback() {
    this.addBusyListeners();
  }

  disconnectedCallback() {
    this.busyForm?.removeEventListener('submit', this.formSubmitListener);
    window.removeEventListener('pageshow', this.pageShowListener);
  }

  addBusyListeners() {
    this.busyForm?.addEventListener('submit', this.formSubmitListener);
    window.addEventListener('pageshow', this.pageShowListener);
  }

  static get observedAttributes() {
    return ['aria-busy'];
  }

  get contentElement() {
    return this._contentElement = this._contentElement || this.querySelector('.btn-text');
  }

  // Apple-style: the label always stays visible and in place - no dot
  // takeover - and a small trailing spinner only appears once a request has
  // actually been running long enough to be noticeable (see QUIET_LOADER_DELAY).
  get quietLoaderElement() {
    return this._quietLoaderElement = this._quietLoaderElement || document.createRange().createContextualFragment(`
      <span class="btn-loader btn-loader--quiet" aria-hidden="true"></span>
    `).firstElementChild;
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }

  init() {
    this.append(this.quietLoaderElement);
  }

  async attributeChangedCallback(name, oldValue, newValue) {
    theme.utils.handleQuietBusyChange(this, newValue === 'true');
  }
}
customElements.define('hover-button', HoverButton, { extends: 'button' });

class MagnetButton extends HoverButton {
  constructor() {
    super();

    this.magnetButton = new theme.MagnetButton(this);
    this.magnetButton.load();
  }
}
customElements.define('magnet-button', MagnetButton, { extends: 'button' });

class HoverElement extends HTMLElement {
  constructor() {
    super();

    this.hoverButton = new theme.HoverButton(this);
    this.hoverButton.load();
  }
}
customElements.define('hover-element', HoverElement);

class MagnetElement extends HoverElement {
  constructor() {
    super();

    this.magnetButton = new theme.MagnetButton(this);
    this.magnetButton.load();
  }
}
customElements.define('magnet-element', MagnetElement);

// Keeps the page still behind an open overlay (drawers, the review form)
// without hiding the scrollbar. Hiding it (overflow: hidden) changed the
// window's width, so the layout shifted as overlays opened and closed, and
// with the gutter reserved (scrollbar-gutter: stable) it left a white bar
// where the scrollbar had been. Instead, scroll input that would move the
// page is blocked: wheel, touch and keyboard scrolling still work inside
// the overlay's own scroll areas, and anything else that moves the page
// (dragging the scrollbar, find in page) is put back. Owners are counted,
// so overlapping overlays keep the page locked until the last one closes;
// callers unlock as soon as their overlay starts closing.
theme.scrollLock = (() => {
  const owners = new Set();
  const scrollKeys = new Set(['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ', 'Spacebar']);
  let savedX = 0;
  let savedY = 0;
  let touchX = 0;
  let touchY = 0;

  // The nearest scroll area between the target and the page that can still
  // move in this direction; the page itself never counts.
  const scrollerFor = (target, dx, dy) => {
    for (let element = target instanceof Element ? target : target?.parentElement; element && element !== document.body && element !== document.documentElement; element = element.parentElement) {
      const style = getComputedStyle(element);
      if (Math.abs(dy) >= Math.abs(dx)) {
        if (!/(auto|scroll|overlay)/.test(style.overflowY) || element.scrollHeight <= element.clientHeight + 1) continue;
        if (dy < 0 ? element.scrollTop > 0 : element.scrollTop + element.clientHeight < element.scrollHeight - 1) return element;
      } else {
        if (!/(auto|scroll|overlay)/.test(style.overflowX) || element.scrollWidth <= element.clientWidth + 1) continue;
        const left = Math.abs(element.scrollLeft);
        if (dx < 0 ? left > 0 : left + element.clientWidth < element.scrollWidth - 1) return element;
      }
    }
    return null;
  };

  const onWheel = (event) => {
    if (event.ctrlKey) return; // pinch zoom
    if (!scrollerFor(event.target, event.deltaX, event.deltaY) && event.cancelable) event.preventDefault();
  };

  const onTouchStart = (event) => {
    if (event.touches.length !== 1) return;
    touchX = event.touches[0].clientX;
    touchY = event.touches[0].clientY;
  };

  const onTouchMove = (event) => {
    if (event.touches.length !== 1) return;
    const dx = touchX - event.touches[0].clientX;
    const dy = touchY - event.touches[0].clientY;
    if (!scrollerFor(event.target, dx, dy) && event.cancelable) event.preventDefault();
  };

  const onKeyDown = (event) => {
    if (!scrollKeys.has(event.key) || event.defaultPrevented) return;
    const target = event.target;
    if (target instanceof Element && target.closest('input, textarea, select, [contenteditable]:not([contenteditable="false"]), [role="slider"], [role="listbox"], [role="radiogroup"]')) return;
    const up = ['ArrowUp', 'PageUp', 'Home'].includes(event.key) || (event.shiftKey && (event.key === ' ' || event.key === 'Spacebar'));
    if (!scrollerFor(target, 0, up ? -1 : 1)) event.preventDefault();
  };

  const onScroll = () => {
    if (window.scrollX !== savedX || window.scrollY !== savedY) window.scrollTo(savedX, savedY);
  };

  const listen = (method) => {
    const options = { passive: false, capture: true };
    window[method]('wheel', onWheel, options);
    window[method]('touchstart', onTouchStart, { passive: true, capture: true });
    window[method]('touchmove', onTouchMove, options);
    window[method]('keydown', onKeyDown, options);
    window[method]('scroll', onScroll, { passive: true });
  };

  return {
    lock(owner) {
      if (owners.has(owner)) return;
      owners.add(owner);
      if (owners.size > 1) return;
      savedX = window.scrollX;
      savedY = window.scrollY;
      document.documentElement.classList.add('scroll-locked');
      listen('addEventListener');
    },
    unlock(owner) {
      if (!owners.delete(owner) || owners.size > 0) return;
      document.documentElement.classList.remove('scroll-locked');
      listen('removeEventListener');
    },
    get locked() {
      return owners.size > 0;
    }
  };
})();

// Resolves the first time the visitor makes a genuine downward scroll
// gesture (touch drag, wheel, or keyboard paging) - as opposed to a scroll
// caused by page load/layout settling, browser scroll restoration, or an
// address-bar resize, none of which dispatch these raw input events. Used
// by AnimateElement.waitForGate() on mobile to let a locked section release
// the instant the visitor shows scroll intent, without relying on
// IntersectionObserver visibility (which can already be true on load for a
// section near the bottom of a short mobile viewport).
theme.watchForUserScroll = function () {
  if (theme._userScrollPromise) return theme._userScrollPromise;
  theme._userScrollPromise = new Promise((resolve) => {
    let startY = null;
    const cleanup = () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('wheel', onWheel);
      document.removeEventListener('keydown', onKeyDown);
    };
    const markScrolled = () => {
      cleanup();
      resolve();
    };
    const onTouchStart = (event) => {
      startY = event.touches[0] ? event.touches[0].clientY : null;
    };
    const onTouchMove = (event) => {
      if (startY === null || !event.touches[0]) return;
      // Finger moving up the screen scrolls the page DOWN.
      if (startY - event.touches[0].clientY > 5) markScrolled();
    };
    const onWheel = (event) => {
      if (event.deltaY > 0) markScrolled();
    };
    const onKeyDown = (event) => {
      if (['ArrowDown', 'PageDown', ' ', 'End'].includes(event.key)) markScrolled();
    };
    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove', onTouchMove, { passive: true });
    document.addEventListener('wheel', onWheel, { passive: true });
    document.addEventListener('keydown', onKeyDown);
  });
  return theme._userScrollPromise;
};
// Started eagerly (not lazily on first waitForGate() call) - a below-the-
// fold section's Motion.inView, and so its waitForGate(), only fires once
// the visitor has already scrolled it into view, which would be too late
// to start listening for the very gesture that did the scrolling.
theme.watchForUserScroll();

class AnimateElement extends HTMLElement {
  constructor() {
    super();

    this.loadPromise = new Promise((resolve) => {
      this.resolveLoad = resolve;
    });
  }

  connectedCallback() {
    if (theme.config.motionReduced) {
      this.resolveLoad();
      return;
    }

    this.animation = new theme.Animation(this);
    this.animation.beforeLoad();

    Motion.inView(this, async () => {
      this.dataset.revealStarted = 'true';
      if (!this.immediate && this.media) await theme.utils.imageLoaded(this.media);
      setTimeout(this.resolveLoad, 500);
      await this.waitForGate();
      // Marks when the VISUAL animation actually begins (after any
      // data-wait-for chain above has resolved) - distinct from
      // revealStarted, which only means this element entered the
      // viewport and is about to start processing (waiting and/or
      // animating). Other components computing "how far into this
      // element's own animation are we" (e.g. MotionList's early-visual
      // reveal trigger) need THIS timestamp, not viewport-entry time.
      this.dataset.animateStartedAt = performance.now().toFixed(1);
      this.dispatchEvent(new CustomEvent('theme:reveal-animating', { bubbles: true }));
      await this.animation.load();
      this.parentElement.classList.add('animated');
      // Real completion signal (unlike loadPromise above, which resolves
      // on a flat 500ms timer regardless of this element's actual
      // duration) - lets other components, like MotionList's product
      // grid reveal, wait for this exact element to finish instead of
      // guessing a fixed delay.
      this.dataset.revealComplete = 'true';
      this.dispatchEvent(new CustomEvent('theme:reveal-complete', { bubbles: true }));
    });
  }

  // Opt-in via data-wait-for (a selector for another animate-element) - lets
  // this element hold off starting until that one has actually finished
  // (theme:reveal-complete), instead of both firing independently the
  // moment each scrolls into view. No-op, zero behavior change, when the
  // attribute is absent - every other animate-element sitewide is unaffected.
  // Falls back to a flat timeout (data-wait-for-timeout, default 2500ms) if
  // the target is missing or never completes.
  waitForGate() {
    const selector = this.getAttribute('data-wait-for');
    if (!selector) return Promise.resolve();
    const gate = document.querySelector(selector);
    if (!gate || gate.dataset.revealComplete) return Promise.resolve();
    // If the gate target never even got a chance to start (its own
    // Motion.inView never fired) and has already scrolled above the
    // viewport, the visitor jumped straight past it - don't hold this
    // section hostage waiting on something that's never going to run.
    // But if it DID start, it's genuinely mid-flight (or about to finish)
    // even though normal continued scrolling has since carried it above
    // the fold too - still wait for its real completion in that case,
    // that's the ordering this gate exists to guarantee.
    if (!gate.dataset.revealStarted && gate.getBoundingClientRect().bottom < 0) return Promise.resolve();

    // This section can already be sitting in the viewport on load (a short
    // viewport, or a wide one where the hero doesn't fill the screen), so
    // viewport visibility can't be what decides whether it's allowed to
    // animate - it would fire at the same time as the gate. Instead, lock
    // it until whichever of these happens first: the visitor makes an
    // actual scroll gesture, the gate is visually done (its own known
    // delay + 35% of its duration, timed from its real animateStartedAt -
    // not a guess), or it genuinely completes. Plus a generous safety-net
    // fallback purely for the case none of those ever fire (e.g. a JS
    // error elsewhere on the page).
    return new Promise((resolve) => {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        resolve();
      };
      gate.addEventListener('theme:reveal-complete', finish, { once: true });
      theme.watchForUserScroll().then(finish);
      const GATE_DURATIONS_MS = { 'fade-in': 1500, 'fade-up': 1500, 'fade-up-fast': 900, 'fade-up-large': 1000, 'zoom-out': 1300 };
      const gateDelayMs = gate.hasAttribute('data-animate-delay') ? parseInt(gate.getAttribute('data-animate-delay')) : 0;
      const gateDurationMs = GATE_DURATIONS_MS[gate.getAttribute('data-animate')] ?? 1500;
      const earlyTriggerOffset = gateDelayMs + gateDurationMs * 0.35;
      const scheduleEarlyTrigger = () => {
        const elapsedSinceGateStarted = performance.now() - parseFloat(gate.dataset.animateStartedAt);
        setTimeout(finish, Math.max(0, earlyTriggerOffset - elapsedSinceGateStarted));
      };
      if (gate.dataset.animateStartedAt) {
        scheduleEarlyTrigger();
      } else {
        gate.addEventListener('theme:reveal-animating', scheduleEarlyTrigger, { once: true });
      }
      const fallback = this.hasAttribute('data-wait-for-timeout') ? parseInt(this.getAttribute('data-wait-for-timeout')) : 2500;
      setTimeout(finish, fallback + 1500);
    });
  }

  reset() {
    this.animation?.reset();
  }

  refresh() {
    this.animation?.reload();
  }
}
customElements.define('animate-element', AnimateElement);

class AnimatePicture extends HTMLPictureElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (theme.config.motionReduced) return;

    this.animation = new theme.Animation(this);
    this.animation.beforeLoad();

    Motion.inView(this, async () => {
      if (!this.immediate && this.media) await theme.utils.imageLoaded(this.media);
      await this.animation.load();
    });
  }
}
customElements.define('animate-picture', AnimatePicture, { extends: 'picture' });

class AnnouncementBar extends HTMLElement {
  constructor() {
    super();
    
    this.selectedIndex = this.selectedIndex;

    if (!theme.config.isTouch || Shopify.designMode) {
      Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' });
    }
    else {
      new theme.initWhenVisible(this.init.bind(this));
    }
  }

  static get observedAttributes() {
    return ['selected-index'];
  }

  get selectedIndex() {
    return parseInt(this.getAttribute('selected-index')) || 0;
  }

  set selectedIndex(index) {
    this.setAttribute('selected-index', Math.min(Math.max(index, 0), this.items.length - 1).toString());
  }

  get items() {
    return this._items = this._items || Array.from(this.children);
  }

  get autoplay() {
    return this.hasAttribute('autoplay');
  }

  get speed() {
    return this.hasAttribute('autoplay') ? parseInt(this.getAttribute('autoplay-speed')) * 1000 : 5000;
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;

    if (this.items.length > 1) {
      this.slider = new Flickity(this, {
        accessibility: false,
        fade: true,
        pageDots: false,
        prevNextButtons: false,
        wrapAround: true,
        rightToLeft: theme.config.rtl,
        autoPlay: this.autoplay ? this.speed : false,
        on: {
          ready: function() {
            setTimeout(() => this.element.setAttribute('loaded', ''));
          }
        }
      });
  
      this.slider.on('change', this.onChange.bind(this));
      this.addEventListener('slider:previous', () => this.slider.previous());
      this.addEventListener('slider:next', () => this.slider.next());
      this.addEventListener('slider:play', () => this.slider.playPlayer());
      this.addEventListener('slider:pause', () => this.slider.pausePlayer());
      
      if (Shopify.designMode) {
        this.addEventListener('shopify:block:select', (event) => this.slider.select(this.items.indexOf(event.target)));
      }
    }
  }

  disconnectedCallback() {
    if (this.slider) this.slider.destroy();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'selected-index' && oldValue !== null && oldValue !== newValue) {
      const focusableEvents = 'button, [href]';
      
      const fromElement = this.items[parseInt(oldValue)];
      const toElement = this.items[parseInt(newValue)];
      
      fromElement.querySelectorAll(focusableEvents).forEach((el) => {
        el.setAttribute('tabindex', '-1');
      });
      toElement.querySelectorAll(focusableEvents).forEach((el) => {
        el.removeAttribute('tabindex');
      });
    }
  }

  onChange() {
    this.selectedIndex = this.slider.selectedIndex;
    this.dispatchEvent(new CustomEvent('slider:change', { bubbles: true, detail: { currentPage: this.slider.selectedIndex } }));
  }
}
customElements.define('announcement-bar', AnnouncementBar);

class AccordionsDetails extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('toggle', this.onToggle);
  }

  get items() {
    return this._items = this._items || Array.from(this.querySelectorAll('details[is="accordion-details"]'));
  }

  onToggle(event) {
    const { current: target, open } = event.detail;
    const closing = this.items.filter((item) => item !== target).map((item) => item.close());

    if (open) {
      let headerHeight = 0;
      if (!theme.config.mqlSmall && document.querySelector('header.header')) {
        headerHeight = Math.round(document.querySelector('header.header').clientHeight);
      }
      // Measured once any sibling above has finished collapsing (and never
      // sooner than the 250ms it always waited): the collapse now runs
      // longer than that, and measuring mid-collapse left the opened row
      // tucked under the header once the layout settled.
      Promise.all([...closing, new Promise((resolve) => setTimeout(resolve, 250))]).then(() => {
        if (!target.open) return;
        window.scrollTo({
          top: target.getBoundingClientRect().top + window.scrollY - headerHeight,
          behavior: theme.config.motionReduced ? 'auto' : 'smooth'
        });
      });
    }
  }
}
customElements.define('accordions-details', AccordionsDetails);

class AccordionDetails extends HTMLDetailsElement {
  constructor() {
    super();

    this._open = this.hasAttribute('open');
    this.summaryElement = this.querySelector('summary');
    this.contentElement = this.querySelector('summary + *');
    this.setAttribute('aria-expanded', this._open ? 'true' : 'false');

    this.summaryElement.addEventListener('click', this.onSummaryClick.bind(this));

    if (Shopify.designMode) {
      this.addEventListener('shopify:block:select', () => {
        if (this.designModeActive) this.open = true;
      });
      this.addEventListener('shopify:block:deselect', () => {
        if (this.designModeActive) this.open = false;
      });
    }
  }

  get designModeActive() {
    return true;
  }

  get controlledElement() {
    return this.closest('accordions-details');
  }

  static get observedAttributes() {
    return ['open'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'open') {
      this.setAttribute('aria-expanded', newValue === '' ? 'true' : 'false');
    }
  }

  get open() {
    return this._open;
  }

  set open(value) {
    if (value !== this._open) {
      this._open = value;

      if (this.isConnected) {
        this.transition(value);
      }
      else {
        value ? this.setAttribute('open', '') : this.removeAttribute('open');
      }
    }

    this.setAttribute('aria-expanded', value ? 'true' : 'false');
    this.dispatchEventHandler();
  }

  onSummaryClick(event) {
    event.preventDefault();
    this.open = !this.open;
  }

  // Called by accordions-details on every sibling when one opens. Already
  // closed (or already closing) items are left alone rather than restarting
  // a collapse, and aria-expanded flips at once so the sibling's icon turns
  // back together with its collapse instead of after it.
  close() {
    if (!this._open) return;
    this._open = false;
    this.setAttribute('aria-expanded', 'false');
    return this.transition(false);
  }

  // Opens like the product FAQ (sections/ultimate-pack-faq.liquid): the
  // height eases out while the content fades in with a slight downward
  // settle, starting just after the panel begins to open. Closes on an even
  // ease-in-out - the opening curve front-loads the motion, which makes a
  // collapse snap shut - with the content fading out quickly ahead of it.
  // A click mid-animation carries on from the current height and fade
  // instead of jumping, and the superseded run skips its clean-up: that
  // used to strip [open] from a panel that had just been re-opened (a quick
  // close-then-open), leaving it shut while it read as open.
  async transition(value) {
    const token = this.transitionToken = (this.transitionToken || 0) + 1;

    if (theme.config.motionReduced) {
      value ? this.setAttribute('open', '') : this.removeAttribute('open');
      return;
    }

    this.style.overflow = 'hidden';

    if (value) {
      const midway = this.hasAttribute('open');
      const startHeight = midway ? this.clientHeight : this.summaryElement.clientHeight;
      const fromOpacity = midway ? parseFloat(window.getComputedStyle(this.contentElement).opacity) || 0 : 0;
      this.setAttribute('open', '');

      await Motion.timeline([
        [this, { height: [`${startHeight}px`, `${this.scrollHeight}px`] }, { duration: 0.46, easing: [0.22, 1, 0.36, 1] }],
        [this.contentElement, { opacity: [fromOpacity, 1], transform: [`translateY(${-6 * (1 - fromOpacity)}px)`, 'translateY(0)'] }, { duration: 0.4, at: fromOpacity > 0 ? 0 : 0.06, easing: [0.22, 1, 0.36, 1] }]
      ]).finished;
    }
    else {
      await Motion.timeline([
        [this.contentElement, { opacity: 0, transform: 'translateY(-4px)' }, { duration: 0.24, easing: [0.25, 0.1, 0.25, 1] }],
        [this, { height: [`${this.clientHeight}px`, `${this.summaryElement.clientHeight}px`] }, { duration: 0.42, at: '<', easing: [0.45, 0, 0.25, 1] }]
      ]).finished;

      if (token !== this.transitionToken) return;
      this.removeAttribute('open');
    }

    if (token !== this.transitionToken) return;
    this.style.height = 'auto';
    this.style.overflow = 'visible';
  }

  dispatchEventHandler() {
    (this.controlledElement ?? this).dispatchEvent(new CustomEvent('toggle', { bubbles: true, detail: { current: this, open: this.open } }));
  }
}
customElements.define('accordion-details', AccordionDetails, { extends: 'details' });

class FooterDetails extends AccordionDetails {
  constructor() {
    super();

    this.loadListener = this.load.bind(this);
    this.load();
  }

  connectedCallback() {
    document.addEventListener('matchSmall', this.loadListener);
    document.addEventListener('unmatchSmall', this.loadListener);
  }

  disconnectedCallback() {
    document.removeEventListener('matchSmall', this.loadListener);
    document.removeEventListener('unmatchSmall', this.loadListener);
  }

  get designModeActive() {
    return theme.config.mqlSmall;
  }

  get openDefault() {
    return this.hasAttribute('data-opened');
  }

  load() {
    if (!theme.config.mqlSmall || this.openDefault) {
      this.open = true;
    }
    else {
      if (this.open && !this.openDefault) {
        this.open = false;
      }
    }
  }
}
customElements.define('footer-details', FooterDetails, { extends: 'details' });

class GestureElement extends HTMLElement {
  constructor() {
    super();
    this.config = {
      thresholdY: Math.max(25, Math.floor(0.15 * window.innerHeight)),
      velocityThreshold: 10,
      disregardVelocityThresholdY: Math.floor(0.5 * this.clientHeight),
      pressThreshold: 8,
      diagonalSwipes: false,
      diagonalLimit: Math.tan(((45 * 1.5) / 180) * Math.PI),
      longPressTime: 500,
    };

    this.handlers = {
      panstart: [],
      panmove: [],
      panend: [],
      swipeup: [],
      swipedown: [],
      longpress: [],
    };

    Motion.inView(this, this.init.bind(this));
  }

  init() {
    this.addEventListener('touchstart', this.onTouchStart.bind(this), theme.supportsPassive ? { passive: true } : false);
    this.addEventListener('touchmove', this.onTouchMove.bind(this), theme.supportsPassive ? { passive: true } : false);
    this.addEventListener('touchend', this.onTouchEnd.bind(this), theme.supportsPassive ? { passive: true } : false);
    this.addEventListener('touchcancel', this.onTouchCancel.bind(this), theme.supportsPassive ? { passive: true } : false);
  }

  on(type, fn) {
    if (this.handlers[type]) {
      this.handlers[type].push(fn);
      return {
        type,
        fn,
        cancel: () => this.off(type, fn),
      };
    }
  }

  off(type, fn) {
    if (this.handlers[type]) {
      const idx = this.handlers[type].indexOf(fn);
      if (idx !== -1) {
        this.handlers[type].splice(idx, 1);
      }
    }
  }

  fire(type, event) {
    for (let i = 0; i < this.handlers[type].length; i++) {
      this.handlers[type][i](event);
    }
  }

  onTouchStart(event) {
    this.thresholdY = this.config.thresholdY;
    this.disregardVelocityThresholdY = this.config.disregardVelocityThresholdY;
    this.touchStartY = event.changedTouches[0].screenY;
    this.touchMoveY = null;
    this.touchEndY = null;
    this.swipingDirection = null;

    // Long press.
    this.longPressTimer = setTimeout(() => this.fire('longpress', event), this.config.longPressTime);
    this.fire('panstart', event);
  }

  onTouchMove(event) {
    const touchMoveY = event.changedTouches[0].screenY - (this.touchStartY ?? 0);
    this.velocityY = touchMoveY - (this.touchMoveY ?? 0);
    this.touchMoveY = touchMoveY;
    const absTouchMoveY = Math.abs(this.touchMoveY);
    this.swipingVertical = absTouchMoveY > this.thresholdY;
    this.swipingDirection = this.swipingVertical ? 'vertical' : 'pre-vertical';

    if (absTouchMoveY > this.config.pressThreshold) {
      clearTimeout(this.longPressTimer ?? undefined);
    }
    this.fire('panmove', event);
  }

  onTouchCancel(event) {
    clearTimeout(this.longPressTimer ?? undefined);
    this.touchMoveY = 0;
    this.velocityY = 0;
    this.swipingDirection = null;
    this.fire('panend', event);
  }

  onTouchEnd(event) {
    this.touchEndY = event.changedTouches[0].screenY;
    this.fire('panend', event);
    clearTimeout(this.longPressTimer ?? undefined);

    const y = this.touchEndY - (this.touchStartY ?? 0);
    const absY = Math.abs(y);

    if (absY > this.thresholdY) {
      this.swipedVertical = this.config.diagonalSwipes ? y <= this.config.diagonalLimit : absY > this.thresholdY;
      if (this.swipedVertical) {
        if (y < 0) {
          // Upward swipe.
          if ((this.velocityY ?? 0) < -this.config.velocityThreshold || y < -this.disregardVelocityThresholdY) {
            this.fire('swipeup', event);
          }
        } else {
          // Downward swipe.
          if ((this.velocityY ?? 0) > this.config.velocityThreshold || y > this.disregardVelocityThresholdY) {
            this.fire('swipedown', event);
          }
        }
      }
    }
  }
}
customElements.define('gesture-element', GestureElement);

class OverlayElement extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('mousemove', this.onMouseMove);
    this.addEventListener('mouseleave', this.onMouseLeave);
    this.addEventListener('mousedown', this.onMouseDown);
    this.addEventListener('mouseup', this.onMouseUp);
  }

  get cursor() {
    return document.querySelector('mouse-cursor');
  }

  onMouseMove() {
    this.cursor.classList.add('active');
    this.cursor.setAttribute('aria-hidden', 'false');
  }
  onMouseLeave() {
    this.cursor.classList.remove('active');
    this.cursor.setAttribute('aria-hidden', 'true');
  }
  onMouseDown() {
    this.cursor.classList.add('pressed');
  }
  onMouseUp() {
    this.cursor.classList.remove('pressed');
  }
}
customElements.define('overlay-element', OverlayElement);

const lockLayerCount = new WeakMap();
class ModalElement extends HTMLElement {
  constructor() {
    super();

    this.events = {
      afterHide: 'modal:afterHide',
      afterShow: 'modal:afterShow',
      closeAll: 'modal:closeAll'
    };

    this.classes = {
      open: 'has-modal-open',
      opening: 'has-modal-opening'
    };
  }

  static get observedAttributes() {
    return ['id', 'open'];
  }

  get shouldLock() {
    return false;
  }

  get shouldAppendToBody() {
    return false;
  }

  get shouldCloseAll() {
    return false;
  }

  get open() {
    return this.hasAttribute('open');
  }

  get controls() {
    return Array.from(document.querySelectorAll(`[aria-controls="${this.id}"]`));
  }

  get overlay() {
    return this._overlay = this._overlay || this.querySelector('.fixed-modal');
  }

  get gesture() {
    return this._gesture = this._gesture || this.querySelector('gesture-element');
  }

  get designMode() {
    return this.hasAttribute('shopify-design-mode');
  }

  get focusElement() {
    return this.querySelector('gesture-element') || this.querySelector('button');
  }

  connectedCallback() {
    this.abortController = new AbortController();
    
    this.controls.forEach((button) => button.addEventListener('click', this.onButtonClick.bind(this), { signal: this.abortController.signal }));
    document.addEventListener('keyup', (event) => event.code === 'Escape' && this.hide(), { signal: this.abortController.signal });
    document.addEventListener(this.events.closeAll, () => this.hide(), { signal: this.abortController.signal });

    if (this.gesture) {
      this.gestureConfig = {
        animationFrame: null,
        moveY: 0,
        maxGestureDistance: 0,
        endPoint: 0,
        layerHeight: null
      };

      this.gestureWrap = this.gesture.parentElement;

      // show()/hide() move drawers to <body> and back, re-running connectedCallback;
      // GestureElement.on() does not dedupe, so bind the pan handlers only once.
      if (!this.gestureHandlersBound) {
        this.gestureHandlersBound = true;
        setTimeout(() => {
          this.gesture.on('panstart', this.onPanStart.bind(this));
          this.gesture.on('panmove', this.onPanMove.bind(this));
          this.gesture.on('panend', this.onPanEnd.bind(this));
        }, 75);
      }
    }

    if (Shopify.designMode && this.designMode) {
      const section = this.closest('.shopify-section');
      section.addEventListener('shopify:section:select', (event) => this.show(null, !event.detail.load), { signal: this.abortController.signal });
      section.addEventListener('shopify:section:deselect', this.hide.bind(this), { signal: this.abortController.signal });
    }
  }

  disconnectedCallback() {
    this.abortController?.abort();
    // (Appending to body on open disconnects and reconnects the element:
    // only a real removal releases the lock.)
    if (!this.isConnected) queueMicrotask(() => { if (!this.isConnected) theme.scrollLock.unlock(this); });
    
    if (Shopify.designMode) {
      document.body.classList.remove(this.classes.open);
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    switch (name) {
      case 'open':
        this.controls.forEach((button) => button.setAttribute('aria-expanded', newValue === null ? 'false' : 'true'));

        if (oldValue === null && (newValue === "" || newValue === 'immediate')) {
          if (this.shouldLock) theme.scrollLock.lock(this);
          this.hidden = false;
          this.removeAttribute('inert');

          this.originalParentBeforeAppend = null;
          if (this.shouldAppendToBody && this.parentElement !== document.body) {
            this.originalParentBeforeAppend = this.parentElement;
            document.body.append(this);
          }
          const showTransitionPromise = this.showTransition(newValue !== 'immediate') || Promise.resolve();
          showTransitionPromise.then(() => {
            this.afterShow();
            this.dispatchEvent(new CustomEvent(this.events.afterShow, { bubbles: true }));
          });
        }
        else if (oldValue !== null && newValue === null) {
          // The page scrolls again as soon as the close starts.
          theme.scrollLock.unlock(this);
          this.setAttribute('inert', '');

          const hideTransitionPromise = this.hideTransition() || Promise.resolve();
          hideTransitionPromise.then(() => {
            this.afterHide();
            
            if (!this.hasAttribute('inert')) return;

            if (this.parentElement === document.body && this.originalParentBeforeAppend) {
              this.originalParentBeforeAppend.appendChild(this);
              this.originalParentBeforeAppend = null;
            }
            this.dispatchEvent(new CustomEvent(this.events.afterHide, { bubbles: true }));

            this.hidden = true;
          });
        }
        this.dispatchEvent(new CustomEvent('toggle', { bubbles: true }));

        break;
    }
  }

  onButtonClick(event) {
    event.preventDefault();

    if (this.open) {
      // Only relevant when the backdrop itself (not an explicit close
      // button, or reopening the drawer) is what was tapped.
      if (event.currentTarget === this.overlay) this.guardAgainstGhostClick();
      this.hide();
    }
    else {
      this.show(event.currentTarget);
    }
  }

  // On some mobile browsers, a single tap/hover-close on the dimmed
  // backdrop can both dismiss the drawer AND separately activate
  // whatever is now exposed underneath: the browser's touch-to-click
  // compatibility event for that same physical gesture is dispatched
  // and re-hit-tested after this element has already gone non-
  // interactive, landing on the newly revealed content instead of
  // being swallowed. Called both from onButtonClick above (tapping the
  // backdrop directly) and from any drawer's own hover-to-close script
  // (hovering/touching the backdrop closes it without going through
  // onButtonClick at all), since either path can trigger the same
  // browser behavior. Eats any click that lands elsewhere within the
  // next moment, in the capture phase, so it never reaches its target -
  // short enough that a genuine, separate follow-up tap from the
  // visitor is unaffected.
  guardAgainstGhostClick() {
    const guardUntil = (typeof performance !== 'undefined' ? performance.now() : Date.now()) + 350;
    const swallowGhostClick = (nextEvent) => {
      if ((typeof performance !== 'undefined' ? performance.now() : Date.now()) - guardUntil > 0) return;
      nextEvent.preventDefault();
      nextEvent.stopPropagation();
    };
    document.addEventListener('click', swallowGhostClick, { capture: true, once: true });
    setTimeout(() => document.removeEventListener('click', swallowGhostClick, { capture: true }), 350);
  }

  hide() {
    if (!this.open) return;

    this.beforeHide();
    this.resetGesture();
    this.removeAttribute('open');

    return theme.utils.waitForEvent(this, this.events.afterHide);
  }
  show(activeElement = null, animate = true) {
    if (this.open) return;

    if (this.shouldCloseAll) {
      document.dispatchEvent(new CustomEvent(this.events.closeAll), { bubbles: true });
    }

    this.beforeShow();
    this.activeElement = activeElement;
    this.setAttribute('open', animate ? '' : 'immediate');

    if (this.shouldLock) {
      document.body.classList.add(this.classes.opening);
    }

    return theme.utils.waitForEvent(this, this.events.afterShow);
  }

  beforeHide() { }
  beforeShow() { }

  afterHide() {
    setTimeout(() => {
      theme.a11y.removeTrapFocus(this.activeElement);
      if (this.shouldLock) {
        lockLayerCount.set(ModalElement, lockLayerCount.get(ModalElement) - 1);

        document.body.classList.toggle(this.classes.open, lockLayerCount.get(ModalElement) > 0);
      }
    });
  }
  afterShow() {
    theme.a11y.trapFocus(this, this.focusElement);
    if (this.shouldLock) {
      lockLayerCount.set(ModalElement, lockLayerCount.get(ModalElement) + 1);

      document.body.classList.remove(this.classes.opening);
      document.body.classList.add(this.classes.open);
    }
  }

  showTransition() {
    // Two frames instead of a fixed 75ms timer: the first paints the
    // un-hidden, not-yet-[active] state as the transitions' starting
    // point, the second flips [active]. Same guarantee, with ~45-65ms
    // less dead time between the tap and the panel starting to move.
    requestAnimationFrame(() => requestAnimationFrame(() => {
      this.setAttribute('active', '');
    }));
    return new Promise((resolve) => {
      const computedStyle = window.getComputedStyle(this.overlay);
      const hasTransition = computedStyle.transitionProperty !== 'none' && parseFloat(computedStyle.transitionDuration) > 0;

      if (!hasTransition) {
        resolve();
        return;
      }  
                          
      // transitioncancel too: a bottom-sheet drag that starts while the
      // backdrop is still fading in sets its transition to none, which
      // cancels the fade, so transitionend may never fire.
      const done = () => {
        this.overlay.removeEventListener('transitionend', done);
        this.overlay.removeEventListener('transitioncancel', done);
        resolve();
      };
      this.overlay.addEventListener('transitionend', done);
      this.overlay.addEventListener('transitioncancel', done);
    });
  }
  hideTransition() {
    this.removeAttribute('active');
    return new Promise((resolve) => {
      this.overlay.addEventListener('transitionend', resolve, { once: true });
    });
  }

  resetGesture() {
    if (this.gesture) {
      this.gestureWrap.style.transform = '';
      this.gestureWrap.style.transition = '';
      this.overlay.style.opacity = '';
      this.overlay.style.transition = '';
    }
  }

  onPanStart() {
    this.removeTransition(this.gestureWrap, 'transform');

    if (this.hasAttribute('open')) {
      if (this.gestureConfig.layerHeight === null) {
        this.gestureConfig.layerHeight = this.gestureWrap.getBoundingClientRect().height;
      }
      this.gestureConfig.maxGestureDistance = this.gestureConfig.layerHeight - 50;
      this.gestureConfig.endPoint = this.gestureConfig.layerHeight * 0.3;
    }
  }

  onPanMove() {
    if (this.gestureConfig.animationFrame) return;

    this.gestureConfig.animationFrame = requestAnimationFrame(() => {
      const clamp = (a, min = 0, max = 1) => Math.min(max, Math.max(min, a));
      const invlerp = (x, y, a) => clamp((a - x) / (y - x));

      // Follow the finger 1:1 while dragging - a transition here made the
      // sheet and backdrop trail ~0.1s behind the touch. onPanEnd sets
      // the release transitions.
      this.gestureWrap.style.transition = 'none';
      this.overlay.style.transition = 'none';

      if (this.hasAttribute('open')) {
        if (this.gestureConfig.layerHeight === null) {
          this.gestureConfig.layerHeight = this.gestureWrap.getBoundingClientRect().height;
        }

        this.gestureConfig.moveY = this.gesture.touchMoveY;
        this.gestureConfig.maxGestureDistance = this.gestureConfig.layerHeight - 50;

        if (this.gestureConfig.moveY >= 0) {
          this.gestureWrap.style.transform = `translateY(${Math.min(
            this.gestureConfig.moveY,
            this.gestureConfig.maxGestureDistance
          )}px)`;

          this.overlay.style.opacity = invlerp(this.gestureConfig.layerHeight, 0, this.gestureConfig.moveY);
        }
        else {
          this.gestureWrap.style.transform = 'translateY(0)';
          this.gestureWrap.style.transition = '';
          this.overlay.style.opacity = '1';
        }
      }

      this.gestureConfig.animationFrame = null;
    });
  }

  onPanEnd() {
    this.gestureConfig.animationFrame === null || cancelAnimationFrame(this.gestureConfig.animationFrame);
    this.gestureConfig.animationFrame = null;
    this.gestureWrap.style.transition = 'transform .1s linear';

    if (this.gestureConfig.layerHeight === null) {
      this.gestureConfig.layerHeight = this.gestureWrap.getBoundingClientRect().height;
    }

    this.gestureConfig.endPoint = this.gestureConfig.layerHeight * 0.3;

    if (this.hasAttribute('open')) {
      this.gestureConfig.moveY = this.gesture.touchMoveY;

      if (this.gestureConfig.moveY < this.gestureConfig.endPoint) {
        // Released short of the dismiss point: settle back like a sheet
        // (moving on the first frame, soft landing) instead of a 0.1s
        // linear snap, and let the backdrop return to full dim with it -
        // it was otherwise left at the lighter opacity the drag gave it
        // until the drawer next closed.
        this.gestureWrap.style.transition = 'transform .32s cubic-bezier(.32, .72, 0, 1)';
        this.gestureWrap.style.transform = 'translateY(0)';
        this.overlay.style.transition = 'opacity .32s cubic-bezier(.32, .72, 0, 1)';
        this.overlay.style.opacity = '';
      }
      else {
        this.gestureWrap.style.transform = `translateY(${this.gestureConfig.layerHeight}px)`;

        this.gestureWrap.addEventListener(
          this.whichTransitionEvent(),
          () => {
            this.hide();
          },
          { once: true }
        );
      }
    }
  }

  removeTransition(node, transition) {
    const match = node.style.transition.match(new RegExp('(?:^|,)\\s*' + transition + '(?:$|\\s|,)[^,]*', 'i'));
    if (match) {
      const transitionArray = node.style.transition.split('');
      transitionArray.splice(match.index, match[0].length);
      node.style.transition = transitionArray.join('');
    }
  }

  whichTransitionEvent() {
    let t;
    const el = document.createElement('fakeelement');
    const transitions = {
      WebkitTransition: 'webkitTransitionEnd',
      MozTransition: 'transitionend',
      MSTransition: 'msTransitionEnd',
      OTransition: 'oTransitionEnd',
      transition: 'transitionEnd',
    };

    for (t in transitions) {
      if (el.style[t] !== undefined) {
        return transitions[t];
      }
    }
  }
}
customElements.define('modal-element', ModalElement);
lockLayerCount.set(ModalElement, 0);

class DrawerElement extends ModalElement {
  constructor() {
    super();

    // Merged onto ModalElement's own this.events (not replacing it) so
    // closeAll ('modal:closeAll') survives - overwriting it silently
    // broke shouldCloseAll for every DrawerElement subclass, since both
    // the dispatch in show() and the listener in connectedCallback read
    // this.events.closeAll, which was left undefined.
    Object.assign(this.events, {
      afterHide: 'drawer:afterHide',
      afterShow: 'drawer:afterShow'
    });
  }

  get shouldLock() {
    return true;
  }

  get shouldAppendToBody() {
    return true;
  }
}
customElements.define('drawer-element', DrawerElement);

class MenuDrawer extends DrawerElement {
  constructor() {
    super();
  }

  get menuItems() {
    return this._menuItems = this._menuItems || this.querySelectorAll('.drawer__menu:not(.active)>li');
  }

  beforeShow() {
    super.beforeShow();
    // Start the submenus' Section Rendering request with the open instead of when the
    // slide-in first brings each api-button into view. A response that is already cached
    // is left to the existing inView path, so its parse is not added to this tap.
    this.querySelectorAll('api-button').forEach((button) => {
      if (button.initialized || typeof button.init !== 'function') return;
      if (cachedSectionsRenderingAPI.get(`${theme.routes.root_url}?section_id=${button.sectionId}`)) return;
      button.init();
    });
    if (theme.config.motionReduced) return;

    // Scheduled for the next frame - after show() has un-hidden the drawer
    // and moved it to <body>, but before that frame paints - so the rows
    // already sit at their hidden first keyframe (Motion uses fill: both)
    // when the drawer first appears. The old 300ms timer let the menu
    // show and then blink out on phones, where the drawer content fades
    // in with no delay. A short start offset lets the panel lead; the
    // per-row step is capped so long menus don't drag.
    requestAnimationFrame(() => {
      Motion.animate(this.menuItems, { transform: ['translateX(-16px)', 'translateX(0)'], opacity: [0, 1] }, { duration: 0.5, easing: [0.22, 1, 0.36, 1], delay: (i) => (window.matchMedia('(max-width: 639px)').matches ? 0.1 : 0.4) + Math.min(i, 8) * 0.045 }).finished.then(() => {
        this.menuItems.forEach((item) => item.removeAttribute('style'));
      });
    });
  }

  beforeHide() {
    super.beforeHide();

    setTimeout(() => {
      this.querySelectorAll('details[is=menu-details]').forEach((subMenu) => {
        subMenu.onCloseButtonClick();
      });
    }, 300);
  }
}
customElements.define('menu-drawer', MenuDrawer);

class ShareDrawer extends DrawerElement {
  constructor() {
    super();
  }

  get menuItems() {
    return this._menuItems = this._menuItems || this.querySelectorAll('.share-buttons>li');
  }

  get urlToShare() {
    const urlInput = this.querySelector('input[type=hidden]');
    return urlInput ? urlInput.value : document.location.href;
  }

  connectedCallback() {
    if (navigator.share) {
      this.controls.forEach((button) => button.addEventListener('click', this.shareTo.bind(this)));
    }
    else {
      super.connectedCallback();
    }
  }

  async shareTo(event) {
    event.preventDefault();
    try {
      await navigator.share({ url: this.urlToShare, title: document.title });
    }
    catch(error) {
      if (error.name !== 'AbortError') console.error(error);
    }
  }

  beforeShow() {
    super.beforeShow();
    if (theme.config.motionReduced) return;

    // Same timing as MenuDrawer.beforeShow: queued for the next frame so
    // the buttons start hidden instead of showing and blinking out at
    // 300ms on phones; a 16px rise instead of 2.5rem; capped stagger.
    requestAnimationFrame(() => {
      Motion.animate(this.menuItems, { transform: ['translateY(16px)', 'translateY(0)'], opacity: [0, 1] }, { duration: 0.5, easing: [0.22, 1, 0.36, 1], delay: (i) => 0.1 + Math.min(i, 8) * 0.045 }).finished.then(() => {
        this.menuItems.forEach((item) => item.removeAttribute('style'));
      });
    });
  }
}
customElements.define('share-drawer', ShareDrawer);

class BackInStockDrawer extends DrawerElement {
  constructor() {
    super();

    if (!theme.config.isTouch || Shopify.designMode) {
      this.init();
    }
    else {
      new theme.initWhenVisible(this.init.bind(this));
    }
  }

  get submited() {
    return this.querySelector('.alert') !== null;
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;

    // Open modal if errors or success message exist
    if (this.submited) {
      setTimeout(() => {
        this.show();
      }, 1000);
    }
  }
}
customElements.define('back-in-stock-drawer', BackInStockDrawer);

class MenuDetails extends HTMLDetailsElement {
  constructor() {
    super();

    this.summary.addEventListener('click', this.onSummaryClick.bind(this));
    this.closeButton.addEventListener('click', this.onCloseButtonClick.bind(this));
  }

  get parent() {
    return this.closest('[data-parent]');
  }

  get summary() {
    return this.querySelector('summary');
  }

  get closeButton() {
    return this.querySelector('[data-close]');
  }

  get focusElement() {
    return this.summary.nextElementSibling.querySelector('button');
  }

  onSummaryClick(event) {
    event.preventDefault();
    this.setAttribute('open', '');
    this.activeElement = event.currentTarget;

    // Two frames instead of a 100ms timer: [open] only has to render the
    // submenu at its off-screen start position once for the slide to have
    // a starting point. Focus still moves ~200ms after the tap, once the
    // slide has mostly landed (focusing earlier could scroll the
    // overflow-hidden nav to reveal a half-arrived button).
    requestAnimationFrame(() => requestAnimationFrame(() => {
      this.parent.classList.add('active');
      this.classList.add('active');
      this.summary.setAttribute('aria-expanded', true);
      this.summary.setAttribute('tabindex', '-1');

      setTimeout(() => {
        theme.a11y.trapFocus(this, this.focusElement);
      }, 170);
    }));
  }

  onCloseButtonClick() {
    this.parent.classList.remove('active');
    this.classList.remove('active');
    this.summary.setAttribute('aria-expanded', false);
    this.summary.removeAttribute('tabindex');

    this.closeAnimation();
  }

  closeAnimation() {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        requestAnimationFrame(handleAnimation);
      }
      else {
        this.removeAttribute('open');
        theme.a11y.removeTrapFocus(this.activeElement);
      }
    }

    requestAnimationFrame(handleAnimation);
  }
}
customElements.define('menu-details', MenuDetails, { extends: 'details' });

class QuantityLabel extends HTMLLabelElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.querySelector('.quantity__rules-cart').append(this.animationElement);
  }

  static get observedAttributes() {
    return ['aria-busy'];
  }

  get contentElement() {
    return this._contentElement = this._contentElement || this.querySelector('.quantity-cart');
  }

  get animationElement() {
    return this._animationElement = this._animationElement || document.createRange().createContextualFragment(`
      <span class="btn-loader btn-loader--quiet" aria-hidden="true"></span>
    `).firstElementChild;
  }

  async attributeChangedCallback(name, oldValue, newValue) {
    theme.utils.handleQuietBusyChange(this, newValue === 'true');
  }
}
customElements.define('quantity-label', QuantityLabel, { extends: 'label' });

class QuantityInput extends HTMLElement {
  quantityUpdateUnsubscriber = undefined;
  quantityBoundriesUnsubscriber = undefined;
  quantityRulesUnsubscriber = undefined;
  
  constructor() {
    super();
  }

  get sectionId() {
    return this.getAttribute('data-section-id');
  }

  get productId() {
    return this.getAttribute('data-product-id');
  }

  get input() {
    return this.querySelector('input');
  }

  get value() {
    return this.input.value;
  }

  connectedCallback() {
    this.abortController = new AbortController();

    this.buttons = Array.from(this.querySelectorAll('button'));
    this.changeEvent = new Event('change', { bubbles: true });
    const listenerOptions = { signal: this.abortController.signal };
    this.input.addEventListener('change', this.onInputChange.bind(this), listenerOptions);
    this.input.addEventListener('focus', () => setTimeout(() => this.input?.select()), listenerOptions);

    this.buttons.forEach((button) => button.addEventListener('click', this.onButtonClick.bind(this), listenerOptions));

    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
    this.quantityBoundriesUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.quantityBoundries, this.setQuantityBoundries.bind(this));
    this.quantityRulesUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.quantityRules, this.updateQuantityRules.bind(this));
  }

  disconnectedCallback() {
    this.abortController.abort();

    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
    if (this.quantityBoundriesUnsubscriber) {
      this.quantityBoundriesUnsubscriber();
    }
    if (this.quantityRulesUnsubscriber) {
      this.quantityRulesUnsubscriber();
    }
  }

  onButtonClick(event) {
    event.preventDefault();
    if (this.input.disabled || this.input.readOnly || this.closest('cart-items')?.hasAttribute('aria-busy')) return;
    const previousValue = this.input.value;

    if (event.currentTarget.name === 'plus') {
      if (parseInt(this.input.getAttribute('data-min')) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.getAttribute('data-min');
      }
      else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (this.input.getAttribute('data-min') === previousValue && event.currentTarget.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
  }

  onInputChange() {
    if (this.input.value === '') {
      this.input.value = parseInt(this.input.min);
    }
    this.validateQtyRules();
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector('button[name="minus"]');
      if (buttonMinus) buttonMinus.toggleAttribute('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const buttonPlus = this.querySelector('button[name="plus"]');
      if (buttonPlus) buttonPlus.toggleAttribute('disabled', parseInt(value) >= parseInt(this.input.max));
    }
  }

  updateQuantityRules({ data: { sectionId, productId, parsedHTML } }) {
    if (sectionId !== this.sectionId || productId !== this.productId) return;

    const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
    const quantityFormUpdated = parsedHTML.getElementById(`QuantityForm-${sectionId}-${this.productId}`);
    const quantityForm = this.closest(`#QuantityForm-${sectionId}-${this.productId}`);
    for (let selector of selectors) {
      const current = quantityForm.querySelector(selector);
      const updated = quantityFormUpdated.querySelector(selector);
      if (!current || !updated) continue;

      if (selector === '.quantity__input') {
        const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
        for (let attribute of attributes) {
          const valueUpdated = updated.getAttribute(attribute);
          if (valueUpdated !== null) {
            current.setAttribute(attribute, valueUpdated);
          }
          else {
            current.removeAttribute(attribute);
          }
        }
      }
      else {
        current.innerHTML = updated.innerHTML;
      }
    }
  }

  setQuantityBoundries({ data: { sectionId, productId } }) {
    if (sectionId !== this.sectionId || productId !== this.productId) return;
    
    const data = {
      cartQuantity: this.input.hasAttribute('data-cart-quantity') ? parseInt(this.input.getAttribute('data-cart-quantity')) : 0,
      min: this.input.hasAttribute('data-min') ? parseInt(this.input.getAttribute('data-min')) : 1,
      max: this.input.hasAttribute('data-max') ? parseInt(this.input.getAttribute('data-max')) : null,
      step: this.input.hasAttribute('step') ? parseInt(this.input.getAttribute('step')) : 1
    };

    let min = data.min;
    const max = data.max === null ? data.max : data.max - data.cartQuantity;
    if (max !== null) min = Math.min(min, max);
    if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

    this.input.min = min;

    if (max) {
      this.input.max = max;
    }
    else {
      this.input.removeAttribute('max');
    }
    this.input.value = min;

    theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.quantityUpdate, undefined);
  }

  reset() {
    this.input.value = this.input.defaultValue;
  }
}
customElements.define('quantity-input', QuantityInput);

class CartCount extends HTMLElement {
  constructor() {
    super();
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  get itemCount() {
    return parseInt(this.innerText);
  }

  onCartUpdate(event) {
    if (event.cart.errors) return;

    this.textContent = event.cart.item_count;
    this.hidden = Number(event.cart.item_count) === 0;
  }
}
customElements.define('cart-count', CartCount);

class RecentlyViewed extends HTMLElement {
  constructor() {
    super();

    if (!theme.config.hasLocalStorage) {
      this.hidden = true;
      return;
    }

    // About a screen and a half ahead (was 600px): a phone scrolling at a
    // normal flick outran the request and reached an empty section.
    Motion.inView(this, this.init.bind(this), { margin: '1400px 0px 1400px 0px' });
  }

  init() {
    const query = this.getQueryString();
    // Nothing to show (no history, or only the product being viewed): the
    // request would run with an empty q, which always renders an empty
    // <recently-viewed> and ends in the else-branch below. Take that branch
    // without the round trip, once DOMContentLoaded has fired, so listeners
    // attached by later deferred scripts (CartDrawer's 'is-empty', cart.js)
    // are in place, as they were after the fetch. body gets .loaded then (top
    // of this file); readyState is already 'interactive' while they still run.
    if (!query) {
      const finish = () => {
        this.closest('.recently-section')?.remove();
        this.dispatchEvent(new CustomEvent('is-empty'));
      };
      if (document.body.classList.contains('loaded')) finish();
      else document.addEventListener('DOMContentLoaded', finish, { once: true });
      return;
    }

    fetch(this.getAttribute('data-url') + query)
      .then(response => response.text())
      .then(responseText => {
        const sectionInnerHTML = new DOMParser()
          .parseFromString(responseText, 'text/html')
          .querySelector('.shopify-section');

        const recommendations = sectionInnerHTML.querySelector('recently-viewed');
        if (recommendations && recommendations.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
          this.dispatchEvent(new CustomEvent('recentlyViewed:loaded'));
        }
        else {
          this.closest('.recently-section')?.remove();
          this.dispatchEvent(new CustomEvent('is-empty'));
        }
      })
      .catch(e => {
        console.error(e);
      });
  }

  getQueryString() {
    let recentlyViewed = '[]';
    if (theme.config.hasLocalStorage) {
      recentlyViewed = window.localStorage.getItem(`${theme.settings.themeName}:recently-viewed`) || '[]';
    }

    const items = new Set(JSON.parse(recentlyViewed));
    if (this.hasAttribute('data-product-id')) {
      items.delete(parseInt(this.getAttribute('data-product-id')));
    }
    return Array.from(items.values(), (item) => `id:${item}`).slice(0, this.hasAttribute('data-limit') ? parseInt(this.getAttribute('data-limit')) : 4).join(' OR ');
  }
}
customElements.define('recently-viewed', RecentlyViewed);

class ProductRecommendations extends HTMLElement {
  constructor() {
    super();

    // Same lead as RecentlyViewed (was 600px).
    Motion.inView(this, this.init.bind(this), { margin: '1400px 0px 1400px 0px' });
  }

  init() {
    // The header (title + prev/next buttons) is always server-rendered,
    // so innerHTML is never actually empty - check the products
    // container itself for real content (carried over across a cart
    // re-render by cart.js) and skip the redundant fetch when it's
    // already there, instead of wiping it out and reloading from
    // scratch every time the cart updates.
    if (this.querySelector('.horizontal-products')?.children.length) return;

    this.setAttribute('aria-busy', 'true');
    const loadingWait = setTimeout(() => this.setAttribute('data-text-loading', ''), 2000);
    fetch(this.getAttribute('data-url'))
      .then(response => response.text())
      .then(responseText => {
        const sectionInnerHTML = new DOMParser()
          .parseFromString(responseText, 'text/html')
          .querySelector('.shopify-section');

        if (sectionInnerHTML === null) return;

        const recommendations = sectionInnerHTML.querySelector('product-recommendations');
        if (recommendations && recommendations.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
          this.dispatchEvent(new CustomEvent('recommendations:loaded'));
        }
        else {
          this.setAttribute('hidden', '');
          this.closest('.recommendations-section')?.remove();
          this.dispatchEvent(new CustomEvent('is-empty'));
        }
      })
      .catch(e => {
        console.error(e);
      })
      .finally(() => {
        clearTimeout(loadingWait);
        this.removeAttribute('aria-busy');
        this.removeAttribute('data-text-loading');
      });
  }
}
customElements.define('product-recommendations', ProductRecommendations);

class ProductComplementary extends HTMLElement {
  constructor() {
    super();

    this.selectedIndex = this.selectedIndex;
  }

  static get observedAttributes() {
    return ['selected-index'];
  }

  get selectedIndex() {
    return parseInt(this.getAttribute('selected-index')) || 0;
  }

  set selectedIndex(index) {
    this.setAttribute('selected-index', Math.min(Math.max(index, 0), this.items.length - 1).toString());
  }

  get items() {
    return this._items = this._items || Array.from(this.children);
  }

  get container() {
    return this.closest('product-recommendations');
  }

  get prevButton() {
    return this.container.querySelector('[data-prev-button]');
  }

  get nextButton() {
    return this.container.querySelector('[data-next-button]');
  }

  connectedCallback() {
    if (this.innerHTML.trim().length) {
      if (this.classList.contains('flickity')) {
        this.carousel = new theme.Carousel(this, {
          accessibility: false,
          prevNextButtons: false,
          adaptiveHeight: true,
          pageDots: false,
          contain: true,
          cellAlign: 'center',
          rightToLeft: theme.config.rtl,
        }, {
          previous: this.prevButton,
          next: this.nextButton
        });

        this.carousel.load();
        this.carousel.slider.on('change', this.onChange.bind(this));
      }
    }
    else {
      this.container.hidden = true;
    }
  }

  disconnectedCallback() {
    if (this.carousel) {
      this.carousel.unload();
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'selected-index' && oldValue !== null && oldValue !== newValue) {
      const focusableEvents = 'button, [href]';
      
      const fromElement = this.items[parseInt(oldValue)];
      const toElement = this.items[parseInt(newValue)];
      
      fromElement.querySelectorAll(focusableEvents).forEach((el) => {
        el.setAttribute('tabindex', '-1');
      });
      toElement.querySelectorAll(focusableEvents).forEach((el) => {
        el.removeAttribute('tabindex');
      });
    }
  }

  onChange() {
    this.selectedIndex = this.carousel.slider.selectedIndex;
  }
}
customElements.define('product-complementary', ProductComplementary);

class MediaElement extends HTMLElement {
  constructor() {
    super();
  }

  get parallax() {
    return this.hasAttribute('data-parallax') ? parseFloat(this.getAttribute('data-parallax')) : false;
  }

  get direction() {
    return this.hasAttribute('data-parallax-dir') ? this.getAttribute('data-parallax-dir') : 'vertical';
  }

  get media() {
    return Array.from(this.querySelectorAll('picture>img, video, iframe, svg, video-media, g-map'));
  }

  connectedCallback() {
    if (theme.config.motionReduced || !this.parallax) return;
    this.setupParallax();
  }

  setupParallax() {
    const [scale, translate] = [1 + this.parallax, this.parallax * 100 / (1 + this.parallax)];

    if (this.direction === 'vertical') {
      Motion.scroll(
        Motion.animate(this.media, { transform: [`scale(${scale}) translateY(0)`, `scale(${scale}) translateY(${translate}%)`], transformOrigin: ['bottom', 'bottom'] }, { easing: 'linear' }),
        { target: this, offset: ['start end', 'end start'] }
      );
    }
    else if (this.direction === 'horizontal') {
      Motion.scroll(
        Motion.animate(this.media, { transform: [`scale(${scale}) translateX(0)`, `scale(${scale}) translateX(${translate}%)`], transformOrigin: ['right', 'right'] }, { easing: 'linear' }),
        { target: this, offset: ['start end', 'end start'] }
      );
    }
    else {
      Motion.scroll(
        Motion.animate(this.media, { transform: [`scale(1)`, `scale(${scale})`], transformOrigin: ['center', 'center'] }, { easing: 'linear' }),
        { target: this, offset: ['start end', 'end start'] }
      );
    }
  }
}
customElements.define('media-element', MediaElement);

class SplitWords extends HTMLElement {
  constructor() {
    super();

    this.loadPromise = new Promise((resolve) => {
      this.resolveLoad = resolve;
    });
  }

  connectedCallback() {
    if (theme.config.motionReduced) {
      this.resolveLoad();
      return;
    }
    if (!document.body.hasAttribute('data-title-animation')) return;
    
    this.init();
  }

  async init() {
    const splitting = Splitting({ target: this, by: 'words' });

    splitting[0].words.forEach((item, index) => {
      const wrapper = document.createElement('animate-element');
      wrapper.className = 'block';
      wrapper.setAttribute('data-animate', this.getAttribute('data-animate'));
      wrapper.setAttribute('data-animate-delay', (this.hasAttribute('data-animate-delay') ? parseInt(this.getAttribute('data-animate-delay')) : 0) + (index * 30));

      for (const itemContent of item.childNodes) {
        wrapper.appendChild(itemContent);
      }

      item.appendChild(wrapper);
    });
    this.classList.remove('js-invisible');

    const animateElements = this.querySelectorAll('animate-element');
    await Promise.all([...animateElements].map(el => el.loadPromise));
    this.resolveLoad();
  }
}
customElements.define('split-words', SplitWords);

class HighlightedText extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, this.init.bind(this));
  }

  get splitWords() {
    return this.closest('split-words');
  }

  async init() {
    if (this.splitWords) {
      await this.splitWords.loadPromise;
    }
    this.classList.add('animated');
  }
}
customElements.define('highlighted-text', HighlightedText, { extends: 'em' });

class MarqueeElement extends HTMLElement {
  constructor() {
    super();

    if (theme.config.motionReduced) return;

    if (!theme.config.isTouch || Shopify.designMode) {
      Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' });
    }
    else {
      new theme.initWhenVisible(this.init.bind(this));
    }
  }

  get childElement() {
    return this.firstElementChild;
  }

  get speed() {
    return this.hasAttribute('data-speed') ? parseInt(this.getAttribute('data-speed')) : 16;
  }

  get maximum() {
    return this.hasAttribute('data-maximum') ? parseInt(this.getAttribute('data-maximum')) : Math.ceil(this.parentWidth / this.childElementWidth) + 2;
  }

  get direction() {
    return this.hasAttribute('data-direction') ? this.getAttribute('data-direction') : 'left';
  }

  get parallax() {
    return !theme.config.isTouch && this.hasAttribute('data-parallax') ? parseFloat(this.getAttribute('data-parallax')) : false;
  }

  get parentWidth() {
    return this.getWidth(this);
  }

  get childElementWidth() {
    return this.getWidth(this.childElement);
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;

    if (this.childElementCount === 1) {
      this.childElement.classList.add('animated');

      const maximum = this.maximum;
      for (let index = 0; index < maximum; index++) {
        this.clone = this.childElement.cloneNode(true);
        this.clone.setAttribute('aria-hidden', 'true');
        this.clone.querySelectorAll('button, [href]').forEach((el) => {
          el.setAttribute('tabindex', '-1');
        });
        this.appendChild(this.clone);
        this.clone.querySelectorAll('.media').forEach((media) => media.classList.remove('loading'));
      }

      this.style.setProperty('--duration', `${(33 - this.speed) * Math.min(2.5, Math.ceil(this.childElementWidth / this.parentWidth))}s`);

      // Hovering a scrolling-text marquee eases it to a stop, and leaving
      // eases it back up to speed, rather than freezing it mid-frame and
      // jolting it back into motion. Slowing eases out (it answers at
      // once); picking up eases in and out. Rows inside a .scrolling-wrapper
      // listen on the wrapper so they all ease together, as the CSS :hover
      // pause they replace did. That CSS pause stays as the fallback
      // wherever this can't run (see .marquee--eased).
      if (this.matches('.scrolling-text') && typeof Element.prototype.getAnimations === 'function') {
        const children = Array.from(this.children);
        children.forEach((element) => element.classList.add('marquee--eased'));
        const hoverTarget = this.closest('.scrolling-wrapper') || this;
        let frame;
        const easeTo = (rate, duration) => {
          cancelAnimationFrame(frame);
          const animations = children.flatMap((element) => element.getAnimations().filter((animation) => animation.animationName?.startsWith('scrolling-')));
          if (!animations.length) return;
          const from = animations[0].playbackRate;
          let start;
          const step = (now) => {
            start ??= now;
            const progress = Math.min(1, (now - start) / duration);
            const eased = rate < from ? 1 - Math.pow(1 - progress, 3) : progress * progress * (3 - 2 * progress);
            animations.forEach((animation) => { animation.playbackRate = from + (rate - from) * eased; });
            if (progress < 1) frame = requestAnimationFrame(step);
          };
          frame = requestAnimationFrame(step);
        };
        hoverTarget.addEventListener('pointerenter', (event) => { if (event.pointerType === 'mouse') easeTo(0, 500); });
        hoverTarget.addEventListener('pointerleave', (event) => { if (event.pointerType === 'mouse') easeTo(1, 700); });
      }
    }

    if (this.parallax) {
      let translate = this.parallax * 100 / (1 + this.parallax);
      if (this.direction === 'right') {
        translate = translate * -1;
      }
      if (theme.config.rtl) {
        translate = translate * -1;
      }

      Motion.scroll(
        Motion.animate(this, { transform: [`translateX(${translate}%)`, `translateX(0)`] }, { easing: 'linear' }),
        { target: this, offset: ['start end', 'end start'] }
      );
    }
    else {
      // pause when out of view
      const observer = new IntersectionObserver((entries, _observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.classList.remove('paused');
          }
          else {
            this.classList.add('paused');
          }
        });
      }, { rootMargin: '0px 0px 50px 0px' });
      observer.observe(this);
    }
  }

  getWidth(element) {
    const rect = element.getBoundingClientRect();
    return rect.right - rect.left;
  }
}
customElements.define('marquee-element', MarqueeElement);

class ScrolledImages extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' });
  }

  get parallax() {
    return this.hasAttribute('data-parallax') ? parseFloat(this.getAttribute('data-parallax')) : false;
  }

  get template() {
    return this.querySelector('template');
  }

  get images() {
    return Array.from(this.template.content.querySelectorAll('.scrolled-images__item'));
  }

  init() {
    this.beforeInit();

    if (theme.config.motionReduced || !this.parallax) return;
    this.setupParallax();
  }

  beforeInit() {
    const main = this.querySelector('.scrolled-images__main');

    for (let i = 0; i < 3; i++) {
      let images = this.shuffle(this.images);

      if (images.length < 8) {
        let start = 0;
        while (images.length < 8) {
          images.push(images[start].cloneNode(true));
          start++;
        }
      }

      const row = document.createElement('div');
      row.classList = 'scrolled-images__row';
      images.forEach((item) => row.appendChild(item.cloneNode(true)));

      main.appendChild(row);
    }

    // Fade the tile field in instead of letting it pop in fully formed the
    // moment the rows are built (theme.js is deferred, so on the homepage
    // hero this lands just after first paint).
    if (!theme.config.motionReduced) {
      Motion.animate(main, { opacity: [0, 1] }, { duration: 0.8, easing: [0.22, 1, 0.36, 1] });
    }
  }

  setupParallax() {
    Array.from(this.querySelectorAll('.scrolled-images__row')).forEach((element, index) => {
      let translate = -1 * this.parallax * 100 / (1 + this.parallax);
      if (theme.config.rtl) {
        translate = translate * -1;
      }

      if (index % 2 === 0) {
        Motion.scroll(
          Motion.animate(element, { transform: [`translateX(${translate}%)`, 'translateX(0)'] }, { easing: 'linear' }),
          { target: this, offset: Motion.ScrollOffset.Any }
        );
      }
      else {
        Motion.scroll(
          Motion.animate(element, { transform: ['translateX(0)', `translateX(${translate}%)`] }, { easing: 'linear' }),
          { target: this, offset: Motion.ScrollOffset.Any }
        );
      }
    });
  }

  shuffle(arr) {
    return arr.sort(() => Math.random() - 0.5);
  }
}
customElements.define('scrolled-images', ScrolledImages);

class DropdownElement extends HTMLElement {
  constructor() {
    super();

    this.classes = {
      bodyClass: 'has-dropdown'
    };

    this.events = {
      afterHide: 'dropdown:afterHide',
      afterShow: 'dropdown:afterShow'
    };

    this.detectHoverListener = this.detectHover.bind(this);
    this.controls.forEach((button) => {
      button.addEventListener('click', this.onToggleClicked.bind(this));
      button.addEventListener('mouseenter', this.detectHoverListener.bind(this));
      button.addEventListener('mouseleave', this.detectHoverListener.bind(this));
    });

    this.detectClickOutsideListener = this.detectClickOutside.bind(this);
    this.detectEscKeyboardListener = this.detectEscKeyboard.bind(this);
    this.detectFocusOutListener = this.detectFocusOut.bind(this);
  }

  static get observedAttributes() {
    return ["id", 'open'];
  }

  get open() {
    return this.hasAttribute('open');
  }

  get controls() {
    return Array.from(document.querySelectorAll(`[aria-controls="${this.id}"]`));
  }

  get container() {
    return this.querySelector('*:first-child');
  }

  attributeChangedCallback(name, oldValue, newValue) {
    switch (name) {
      case 'open':
        this.controls.forEach((button) => button.setAttribute('aria-expanded', newValue === null ? 'false' : 'true'));
        break;
    }
  }

  show() {
    document.body.classList.add(this.classes.bodyClass);
    this.setAttribute('open', '');
    
    document.addEventListener('click', this.detectClickOutsideListener);
    document.addEventListener('keyup', this.detectEscKeyboardListener);
    document.addEventListener('focusout', this.detectFocusOutListener);

    this.afterShow();
  }

  hide() {
    document.body.classList.remove(this.classes.bodyClass);
    this.removeAttribute('open');

    document.removeEventListener('click', this.detectClickOutsideListener);
    document.removeEventListener('keyup', this.detectEscKeyboardListener);
    document.removeEventListener('focusout', this.detectFocusOutListener);

    this.afterHide();
  }

  onToggleClicked(event) {
    event?.preventDefault();
    this.open ? this.hide() : this.show();
  }

  afterShow() {
    Motion.animate(this, { opacity: [0, 1], visibility: 'visible' }, { duration: theme.config.motionReduced ? 0 : 0.6, easing: [.7, 0, .2, 1] });
    Motion.animate(this.container, { transform: ['translateY(-105%)', 'translateY(0)'] }, { duration: theme.config.motionReduced ? 0 : 0.6, easing: [.7, 0, .2, 1] });
  }

  afterHide() {
    Motion.animate(this, { opacity: 0, visibility: 'hidden' }, { duration: theme.config.motionReduced ? 0 : 0.3, easing: [.7, 0, .2, 1] });
    Motion.animate(this.container, { transform: 'translateY(-105%)' }, { duration: theme.config.motionReduced ? 0 : 0.6, easing: [.7, 0, .2, 1] });
  }

  detectClickOutside(event) {
    if (!this.parentElement.contains(event.target)) {
      this.hide();
    }
  }

  detectEscKeyboard(event) {
    if (event.code === 'Escape') {
      this.hide();
    }
  }

  detectFocusOut(event) {
    if (event.relatedTarget && !this.contains(event.relatedTarget)) {
      this.hide();
    }
  }

  detectHover(event) {
    if (theme.config.isTouch) return;

    if (event.type === 'mouseenter') {
      this.show();
    }
    else {
      this.hide();
    }
  }
}
customElements.define('dropdown-element', DropdownElement);

class DropdownLocalization extends DropdownElement {
  constructor() {
    super();
  }

  afterShow() {
    Motion.animate(this, { opacity: [0, 1], visibility: 'visible' }, { duration: theme.config.motionReduced ? 0 : 0.24, easing: [0.25, 0.1, 0.25, 1] });
  }

  afterHide() {
    Motion.animate(this, { opacity: 0, visibility: 'hidden' }, { duration: theme.config.motionReduced ? 0 : 0.18, easing: [0.25, 0.1, 0.25, 1] });
  }
}
customElements.define('dropdown-localization', DropdownLocalization);

const lockDropdownCount = new WeakMap();
class DetailsDropdown extends HTMLDetailsElement {
  constructor() {
    super();

    this.classes = {
      bodyClass: 'has-dropdown-menu'
    };

    this.events = {
      afterHide: 'menu:afterHide',
      afterShow: 'menu:afterShow'
    };

    this._open = this.hasAttribute('open');
    this.summaryElement.addEventListener('click', this.onSummaryClicked.bind(this));

    this.detectClickOutsideListener = this.detectClickOutside.bind(this);
    this.detectEscKeyboardListener = this.detectEscKeyboard.bind(this);
    this.detectFocusOutListener = this.detectFocusOut.bind(this);

    this.hoverTimer = null;
    this.detectHoverListener = this.detectHover.bind(this);
    this.addEventListener('mouseenter', this.detectHoverListener.bind(this));
    this.addEventListener('mouseleave', this.detectHoverListener.bind(this));
  }

  set open(value) {
    if (value !== this._open) {
      this._open = value;

      if (this.isConnected) {
        this.transition(value);
      }
      else {
        value ? this.setAttribute('open', '') : this.removeAttribute('open');
        this.updateAriaExpanded(value);
      }
    }
  }

  get summaryElement() {
    return this.firstElementChild;
  }

  get contentElement() {
    return this.lastElementChild;
  }

  get open() {
    return this._open;
  }

  get trigger() {
    return this.hasAttribute('trigger') ? this.getAttribute('trigger') : 'click';
  }

  get level() {
    return this.hasAttribute('level') ? this.getAttribute('level') : 'top';
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }

  onSummaryClicked(event) {
    event.preventDefault();

    if (!theme.config.isTouch && this.trigger === 'hover' && this.summaryElement.hasAttribute('data-link') && this.summaryElement.getAttribute('data-link').length > 0) {
      window.location.href = this.summaryElement.getAttribute('data-link');
    }
    else {
      this.open = !this.open;
    }
  }

  async transition(value) {
    this.updateAriaExpanded(value);

    if (value) {
      lockDropdownCount.set(DetailsDropdown, lockDropdownCount.get(DetailsDropdown) + 1);
      document.body.classList.add(this.classes.bodyClass);
      
      this.setAttribute('open', '');
      this.summaryElement.setAttribute('open', '');
      if (theme.config.motionReduced) {
        this.contentElement.setAttribute('open', '');
      } else {
        // Two frames (not a fixed 100ms) is all the freshly rendered
        // <details> content needs to get a start state for the [open]
        // item-stagger transitions, so the links begin easing in almost
        // immediately. The _open guard stops a quick hover-through (menu
        // already closed again before the frames ran) from leaving [open]
        // stuck on the hidden panel, which made the next open skip its
        // stagger.
        requestAnimationFrame(() => requestAnimationFrame(() => {
          if (this._open) this.contentElement.setAttribute('open', '');
        }));
      }
      document.addEventListener('click', this.detectClickOutsideListener);
      document.addEventListener('keyup', this.detectEscKeyboardListener);
      document.addEventListener('focusout', this.detectFocusOutListener);
      await this.transitionIn();
      this.shouldReverse();
    }
    else {
      lockDropdownCount.set(DetailsDropdown, lockDropdownCount.get(DetailsDropdown) - 1);
      document.body.classList.toggle(this.classes.bodyClass, lockDropdownCount.get(DetailsDropdown) > 0);
      
      this.summaryElement.removeAttribute('open');
      this.contentElement.removeAttribute('open');
      document.removeEventListener('click', this.detectClickOutsideListener);
      document.removeEventListener('keyup', this.detectEscKeyboardListener);
      document.removeEventListener('focusout', this.detectFocusOutListener);
      await this.transitionOut();
      if (!this.open) this.removeAttribute('open');
    }
  }

  async transitionIn() {
    // Apple-style flyout: a fade plus a short drop, both easing out from
    // the first frame. The old -105% slide had to be masked by a 0.2s
    // opacity delay (.dropdown has no overflow clip, so the panel would
    // otherwise show sliding over the header/announcement bar), which left
    // ~0.4s of nothing after hover. A 10px offset needs no masking.
    const reduced = theme.config.motionReduced;
    Motion.animate(this.contentElement, { opacity: [0, 1], visibility: 'visible' }, { duration: reduced ? 0 : 0.24, easing: [0.25, 0.1, 0.25, 1] });
    const translateY = this.level === 'top' ? '-10px' : '8px';
    return Motion.animate(this.contentElement.firstElementChild, { transform: [`translateY(${translateY})`, 'translateY(0)'] }, { duration: reduced ? 0 : 0.36, easing: [0.22, 1, 0.36, 1] }).finished;
  }

  async transitionOut() {
    // Same short path back, a little quicker than the entrance.
    const reduced = theme.config.motionReduced;
    Motion.animate(this.contentElement, { opacity: 0, visibility: 'hidden' }, { duration: reduced ? 0 : 0.18, easing: [0.25, 0.1, 0.25, 1] });
    const translateY = this.level === 'top' ? '-10px' : '8px';
    return Motion.animate(this.contentElement.firstElementChild, { transform: `translateY(${translateY})` }, { duration: reduced ? 0 : 0.22, easing: [0.25, 0.1, 0.25, 1] }).finished;
  }

  detectClickOutside(event) {
    if (!this.contains(event.target) && !(event.target.closest('details') instanceof DetailsDropdown)) {
      this.open = false;
    }
  }

  detectEscKeyboard(event) {
    if (event.code === 'Escape') {
      const targetMenu = event.target.closest('details[open]');
      if (targetMenu) {
        targetMenu.open = false;
      }
    }
  }

  detectFocusOut(event) {
    if (event.relatedTarget && !this.contains(event.relatedTarget)) {
      this.open = false;
    }
  }

  detectHover(event) {
    if (this.trigger !== 'hover' || theme.config.isTouch) return;

    if (event.type === 'mouseenter') {
      this.open = true;
    }
    else {
      this.open = false;
    }
  }

  shouldReverse() {
    const maxWidth = this.contentElement.offsetLeft + this.contentElement.clientWidth * 2;
    if (maxWidth > window.innerWidth) {
      this.contentElement.classList.add('should-reverse');
    }
  }

  updateAriaExpanded(isOpen) {
    this.summaryElement.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
  }
}
customElements.define('details-dropdown', DetailsDropdown, { extends: 'details' });
lockDropdownCount.set(DetailsDropdown, 0);

class DetailsMega extends DetailsDropdown {
  constructor() {
    super();

    if (Shopify.designMode) {
      this.addEventListener('shopify:block:select', () => this.open = true);
      this.addEventListener('shopify:block:deselect', () => this.open = false);
    }
  }

  async transitionIn() {
    this.contentElement.querySelector('tabs-element')?.unload();
    setTimeout(() => {
      this.contentElement.querySelector('tabs-element')?.load();
    }, theme.config.motionReduced ? 0 : 200);

    document.body.classList.add('with-mega');
    // Still extends down from under the header (clipped by .mega-menu's
    // overflow), but on an iOS-sheet ease-out so it starts moving on the
    // first frame; the old (.7,0,.2,1) sat nearly still for ~0.2s.
    return Motion.animate(this.contentElement.firstElementChild, { visibility: 'visible', transform: ['translateY(-105%)', 'translateY(0)'] }, { duration: theme.config.motionReduced ? 0 : 0.48, easing: [0.32, 0.72, 0, 1] }).finished;
  }

  async transitionOut() {
    document.body.classList.remove('with-mega');
    // Exit a little quicker than the entrance, starting to retract at once.
    return Motion.animate(this.contentElement.firstElementChild, { visibility: 'hidden', transform: 'translateY(-105%)' }, { duration: theme.config.motionReduced ? 0 : 0.34, easing: [0.25, 0.1, 0.25, 1] }).finished;
  }
}
customElements.define('details-mega', DetailsMega, { extends: 'details' });

class LocalizationListbox extends HTMLFormElement {
  constructor() {
    super();

    this.items.forEach((item) => item.addEventListener('click', this.onItemClick.bind(this)));
  }

  get items() {
    return this._items = this._items || Array.from(this.querySelectorAll('a'));
  }

  get input() {
    return this.querySelector('input[name="locale_code"], input[name="country_code"]');
  }

  get returnTo() {
    return this.querySelector('input[name="return_to"]');
  }

  onItemClick(event) {
    event.preventDefault();

    this.input.value = event.currentTarget.getAttribute('data-value');
    this.returnTo.value = window.location.pathname;
    this.submit();
  }
}
customElements.define('localization-listbox', LocalizationListbox, { extends: 'form' });

class LocalizationForm extends HTMLFormElement {
  constructor() {
    super();

    if (!theme.config.isTouch || Shopify.designMode) {
      Motion.inView(this, this.init.bind(this));
    }
    else {
      new theme.initWhenVisible(this.init.bind(this));
    }
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;

    this.addEventListener('change', this.submit);
  }
}
customElements.define('localization-form', LocalizationForm, { extends: 'form' });

const cachedSectionsRenderingAPI = new Map();
class APIButton extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, this.init.bind(this));
  }

  get sectionId() {
    return this.getAttribute('data-section-id');
  }

  get sectionRenderingId() {
    return this.getAttribute('data-id');
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;

    if (document.getElementById(this.sectionRenderingId) === null || document.getElementById(this.sectionRenderingId).hasAttribute('loaded')) return;

    const url = `${theme.routes.root_url}?section_id=${this.sectionId}`;
    cachedSectionsRenderingAPI.has(url)
      ? this.renderSectionFromCache(url)
      : this.renderSectionFromFetch(url);

    this.sectionsRenderingAPIListener = this.afterSectionsRenderingAPI.bind(this);
    document.addEventListener('sectionsRenderingAPI:cached', this.sectionsRenderingAPIListener);
  }

  renderSectionFromCache(url, sharedParse) {
    const responseText = cachedSectionsRenderingAPI.get(url);
    if (responseText.length) {
      this.updateSectionRendering(responseText, sharedParse);
    }
  }

  renderSectionFromFetch(url) {
    cachedSectionsRenderingAPI.set(url, '');
    fetch(url)
      .then(response => response.text())
      .then(responseText => {
        // Parsed once here and handed to the api-buttons waiting for this response,
        // instead of each of them parsing the whole section again in this same task.
        const sharedParse = { document: new DOMParser().parseFromString(responseText, 'text/html'), taken: [] };
        this.updateSectionRendering(responseText, sharedParse);
        cachedSectionsRenderingAPI.set(url, responseText);

        document.dispatchEvent(new CustomEvent('sectionsRenderingAPI:cached', {
          detail: {
            url,
            sectionId: this.sectionId,
            sharedParse
          }
        }));
      })
      .catch(e => {
        console.error(e);
      });
  }

  updateSectionRendering(responseText, sharedParse) {
    // A node is only taken from the shared parse while it is exactly what a fresh parse
    // would return (no node with its id taken before, nothing moved out of it); otherwise
    // the response is parsed again as before.
    let source = sharedParse?.document.getElementById(this.sectionRenderingId);
    if (source && sharedParse.taken.some((node) => node.id === this.sectionRenderingId || source.contains(node.parent))) source = null;
    if (source) sharedParse.taken.push({ id: this.sectionRenderingId, parent: source.parentNode });
    else source = new DOMParser().parseFromString(responseText, 'text/html').getElementById(this.sectionRenderingId);
    document.getElementById(this.sectionRenderingId).replaceWith(source);

    // remove listener
    document.removeEventListener('sectionsRenderingAPI:cached', this.sectionsRenderingAPIListener);
  }

  afterSectionsRenderingAPI(event) {
    if (event.detail.sectionId === this.sectionId) {
      this.renderSectionFromCache(event.detail.url, event.detail.sharedParse);
    }
  }
}
customElements.define('api-button', APIButton);

class StickyElement extends HTMLElement {
  constructor() {
    super();

    this.endScroll = window.innerHeight - this.offsetHeight - 500;
    this.currPos = window.scrollY;
    this.screenHeight = window.innerHeight;
    this.stickyElementHeight = this.offsetHeight;
    this.bottomGap = this.offsetHeight < window.innerHeight ? this.topGap : 0;
    this.ticking = false;
    this.scrollHandler = this.onScrollHandler.bind(this);
  }

  connectedCallback() {
    window.addEventListener('scroll', this.scrollHandler, {
      capture: true,
      passive: true,
    });
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.scrollHandler, true);
  }

  get headerHeight() {
    let header = document.querySelector('.header-section header[is="sticky-header"]');
    if (this.hasAttribute('aria-controls')) {
      header = document.getElementById(this.getAttribute('aria-controls'));
    }
    return header ? (header.getBoundingClientRect().height + this.topGap) : (this.offsetHeight < window.innerHeight ? this.topGap : 0);
  }

  get topGap() {
    return this.hasAttribute('data-gap') ? parseInt(this.getAttribute('data-gap')) : 20;
  }

  get inset() {
    return parseFloat(this.style.getPropertyValue('--inset')) || 0;
  }

  onScrollHandler() {
    if (!this.ticking) {
      requestAnimationFrame(() => {
        const style = window.getComputedStyle(this);
        if (style.position == 'sticky') {
          this.screenHeight = window.innerHeight;
          this.stickyElementHeight = this.offsetHeight;

          this.positionStickySidebar();
        }
        this.ticking = false;
      });
      this.ticking = true;
    }
  }

  positionStickySidebar() {
    this.endScroll = window.innerHeight - this.offsetHeight - this.bottomGap;
    const stickyElementTop = this.inset;
    const scrollDelta = this.currPos - window.scrollY;

    if (this.stickyElementHeight + this.headerHeight + this.bottomGap > this.screenHeight) {
      let newInset;
      
      if (window.scrollY < this.currPos) {
        // Scroll up
        if (stickyElementTop < this.headerHeight) {
          newInset = Math.min(stickyElementTop + scrollDelta, this.headerHeight);
        } else {
          newInset = this.headerHeight;
        }
      } else {
        // Scroll down
        if (stickyElementTop > this.endScroll) {
          newInset = Math.max(stickyElementTop + scrollDelta, this.endScroll);
        } else {
          newInset = this.endScroll;
        }
      }

      if (Math.abs(newInset - stickyElementTop) > 0.5) {
        this.style.setProperty('--inset', `${newInset.toFixed(1)}px`);
      }
    } else {
      this.style.setProperty('--inset', `${this.headerHeight.toFixed(1)}px`);
    }

    this.currPos = window.scrollY;
  }
}
customElements.define('sticky-element', StickyElement);

class ParallaxElement extends HTMLDivElement {
  constructor() {
    super();

    this.load();

    this.matchSmallListener = this.unload.bind(this);
    this.unmatchSmallListener = this.load.bind(this);
  }

  connectedCallback() {
    document.addEventListener('matchSmall', this.matchSmallListener);
    document.addEventListener('unmatchSmall', this.unmatchSmallListener);
  }

  disconnectedCallback() {
    document.removeEventListener('matchSmall', this.matchSmallListener);
    document.removeEventListener('unmatchSmall', this.unmatchSmallListener);
  }

  get mobileDisabled() {
    return false;
  }

  load() {
    if (theme.config.motionReduced) return;
    if (this.mobileDisabled && (theme.config.isTouch || theme.config.mqlSmall)) return;

    this.motion();
  }

  motion() {
    // Linear, like MediaElement and ScrolledImages: a scroll-linked layer
    // should move at one constant rate against the scroll. Motion's default
    // 'ease' made it hang near the viewport edges, rush through the middle,
    // and put it ~80% of the way through its travel at viewport center
    // instead of at its designed midpoint (collage columns and the
    // images-with-text overlap sat visibly off their layout when centered).
    this.animation = Motion.scroll(
      Motion.animate(this, { transform: [`translateY(${this.getAttribute('data-start')})`, `translateY(${this.getAttribute('data-stop')})`] }, { easing: 'linear' }),
      { target: this, offset: Motion.ScrollOffset.Any }
    );
  }

  unload() {
    if (this.animation) {
      this.animation();
      this.style.transform = null;
    }
  }
}
customElements.define('parallax-element', ParallaxElement, { extends: 'div' });

class ParallaxOverlay extends HTMLElement {
  constructor() {
    super();

    this.load();

    this.refreshListener = this.refresh.bind(this);
  }

  connectedCallback() {
    if (Shopify.designMode) document.addEventListener('shopify:section:unload', this.refreshListener);
  }

  disconnectedCallback() {
    if (Shopify.designMode) document.removeEventListener('shopify:section:unload', this.refreshListener);
  }

  refresh() {
    let options = {};
    options[this.getAttribute('data-target')] = this.getAttribute('data-stop');

    Motion.animate(this, options, { duration: 0 });
  }

  load() {
    let options = {};
    options[this.getAttribute('data-target')] = [this.getAttribute('data-start'), this.getAttribute('data-stop')];

    this.animation = Motion.scroll(
      Motion.animate(this, options),
      { target: this.parentElement, offset: Motion.ScrollOffset.Enter }
    );
  }
}
customElements.define('parallax-overlay', ParallaxOverlay);

class FooterParallax extends ParallaxElement {
  constructor() {
    super();

    this.sectionUnloadListener = this.refresh.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();
    if (Shopify.designMode) document.addEventListener('shopify:section:unload', this.sectionUnloadListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (Shopify.designMode) document.removeEventListener('shopify:section:unload', this.sectionUnloadListener);
  }

  get mobileDisabled() {
    return true;
  }

  motion() {
    this.animation = Motion.scroll(
      Motion.animate(this, { transform: ['translateY(-50%)', 'translateY(0)'] }),
      { target: this, offset: Motion.ScrollOffset.Enter }
    );
  }

  refresh() {
    if (theme.config.motionReduced) return;
    if (this.mobileDisabled && (theme.config.isTouch || theme.config.mqlSmall)) return;
    
    setTimeout(() => {
      Motion.animate(this, { transform: 'translateY(0)' }, { duration: 0 });
    });
  }
}
customElements.define('footer-parallax', FooterParallax, { extends: 'div' });

class FooterGroup extends HTMLElement {
  constructor() {
    super();

    this.initHandler = this.init.bind(this);
    this.init();

    if (Shopify.designMode) {
      document.addEventListener('shopify:section:load', this.initHandler);
    }
  }

  connectedCallback() {
    if (Shopify.designMode) document.addEventListener('shopify:section:load', this.initHandler);
  }

  disconnectedCallback() {
    if (Shopify.designMode) document.removeEventListener('shopify:section:load', this.initHandler);
  }

  get rounded() {
    return Array.from(this.querySelectorAll('.section--rounded'));
  }

  get sections() {
    return Array.from(this.querySelectorAll('.shopify-section'));
  }

  init() {
    this.sections.forEach((shopifySection, index) => {
      const section = shopifySection.querySelector('.section');
      if (section) {
        section.classList.remove('section--next-rounded');
        section.style.zIndex = this.sections.length - index;
      }
    });

    this.rounded.forEach((section) => {
      const shopifySection = section.closest('.shopify-section');
      let previousShopifySection = shopifySection.previousElementSibling;

      if (previousShopifySection === null) {
        previousShopifySection = document.querySelector('.main-content .shopify-section:last-child');
      }

      if (previousShopifySection) {
        const previousSection = previousShopifySection.querySelector('.section');
        if (previousSection) {
          previousSection.classList.add('section--next-rounded');
        }
      }
    });
  }
}
customElements.define('footer-group', FooterGroup, { extends: 'footer' });

class CarouselElement extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' });
  }

  get items() {
    return this._items = this._items || Array.from(this.children);
  }

  get watchCSS() {
    return this.hasAttribute('watch-css');
  }

  get initialIndex() {
    return parseInt(this.getAttribute('initial-index') || 0);
  }

  init() {
    if (this.items.length > 1) {
      this.carousel = new Flickity(this, {
        watchCSS: this.watchCSS,
        prevNextButtons: false,
        adaptiveHeight: true,
        wrapAround: true,
        rightToLeft: theme.config.rtl,
        initialIndex: this.initialIndex,
      });

      this.addEventListener('control:select', (event) => this.select(event.detail.index));
      this.carousel.on('change', this.onChange.bind(this));

      if (Shopify.designMode) {
        this.addEventListener('shopify:block:select', (event) => this.carousel.select(this.items.indexOf(event.target)));
      }
    }
  }

  disconnectedCallback() {
    if (this.carousel) this.carousel.destroy();
  }

  select(index = 0, immediate = false) {
    if (!immediate) {
      const { selectedIndex, slides } = this.carousel;

      immediate = Math.abs(index - selectedIndex) > 3;
      
      if (index === 0 && selectedIndex === slides.length - 1) {
        immediate = false;
      }

      if (index === slides.length - 1 && selectedIndex === 0) {
        immediate = false;
      }
    }
    this.carousel.select(index, false, immediate);
  }

  onChange(index) {
    this.dispatchEvent(new CustomEvent('carousel:change', { bubbles: true, detail: { index } }));
  }
}
customElements.define('carousel-element', CarouselElement);

class TestimonialsElement extends CarouselElement {
  constructor() {
    super();

    Motion.inView(this, () => {
      setTimeout(() => this.update(), 600);
    });
  }

  update() {
    if (this.carousel) this.carousel.select(0);
  }
}
customElements.define('testimonials-element', TestimonialsElement);

class SecondaryMedia extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' });
  }

  get template() {
    return this.previousElementSibling;
  }

  static get observedAttributes() {
    return ['selected-index'];
  }

  get selectedIndex() {
    return parseInt(this.getAttribute('selected-index')) || 0;
  }

  set selectedIndex(index) {
    this.setAttribute('selected-index', Math.min(Math.max(index, 0), this.items.length - 1).toString());
  }

  disconnectedCallback() {
    if (this.carousel) this.carousel.destroy();
  }

  init() {
    this.appendChild(this.template.content.cloneNode(true));
    this.mediaCount = this.querySelectorAll('.media').length;
    this.items = Array.from(this.children);

    if (this.mediaCount > 1) {
      this.style.visibility = 'hidden';

      this.carousel = new Flickity(this, {
        accessibility: false,
        draggable: false,
        pageDots: true,
        prevNextButtons: false,
        wrapAround: true,
        rightToLeft: theme.config.rtl,
        on: {
          ready: () => {
            this.style.visibility = 'visible';
          }
        }
      });

      // page dots only when there is more than one picture to hover
      // through: a two-picture card (most motion graphics templates) shows
      // its second picture on hover, and a lone dot says nothing
      if (this.mediaCount === 2 || this.carousel.cells.length < 3) {
        this.classList.add('without-dots');
      }

      this.addEventListener('mousemove', this.onMoveHandler);
      this.addEventListener('mouseleave', this.onLeaveHandler);
      this.carousel.on('change', this.onChange.bind(this));
    }
  }

  onMoveHandler(event) {
    const index = this.getHoverIndex(event);
    // Flickity's select() re-runs unselect/select on the current slide
    // (class + aria-hidden writes), restarts its settled rAF loop and
    // re-dispatches 'select' even when the index is unchanged; skip it.
    if (index === undefined || index === this.carousel.selectedIndex) return;
    this.carousel.select(index);
  }

  getHoverIndex(event) {
    if (this.mediaCount === 2) {
      return 1;
    }

    const { width } = this.carousel.size;
    const mouseX = event.clientX - this.getBoundingClientRect().left;

    if (this.mediaCount === 3) {
      if (mouseX < width / 2) {
        return 1;
      }
      return 2;
    }

    if (this.mediaCount === 4) {
      if (mouseX < width / 3) {
        return 1;
      }
      if (mouseX < (2 * width) / 3) {
        return 2;
      }
      return 3;
    }
  }

  onLeaveHandler() {
    this.carousel.select(0);
  }

  onChange() {
    this.selectedIndex = this.carousel.selectedIndex;
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'selected-index' && oldValue !== null && oldValue !== newValue) {
      const fromElement = this.items[parseInt(oldValue)];
      const toElement = this.items[parseInt(newValue)];

      if (fromElement.tagName === 'VIDEO-MEDIA') {
        fromElement?.pause();
      }

      if (toElement.tagName === 'VIDEO-MEDIA') {
        toElement?.play();
      }
    }
  }
}
customElements.define('secondary-media', SecondaryMedia);

class MotionList extends HTMLElement {
  constructor() {
    super();

    this.isMobile = theme.config.mqlSmall || theme.config.isTouch;
    this.motionReduced = theme.config.motionReduced || this.hasAttribute('motion-reduced');

    if (this.hasAttribute('data-product-reveal')) return;

    if (this.motionReduced || this.initialized) return;
    
    this.unload();
    Motion.inView(this, this.load.bind(this));
  }

  connectedCallback() {
    if (!this.hasAttribute('data-product-reveal')) return;
    queueMicrotask(() => {
      if (this.isConnected) this.setupProductReveal();
    });
  }

  disconnectedCallback() {
    if (!this.hasAttribute('data-product-reveal')) return;
    this.productObserver?.disconnect();
    this.productListeners?.abort();
    this.finishProductReveals();
    this.productListeners = null;
  }

  setupProductReveal() {
    if (this.productListeners) return this.observeProductCards();
    this.productListeners = new AbortController();
    this.productSeen = this.productSeen || new WeakSet();
    this.productAnimations = new Map();
    this.productMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const options = { signal: this.productListeners.signal };
    this.productMotionQuery.addEventListener('change', () => {
      if (this.productMotionQuery.matches) this.finishProductReveals();
    }, options);
    // click (not pointerdown) - a touch that becomes a scroll/swipe never
    // fires click, only a genuine tap does, so a downward scroll that
    // happens to start with a finger over the grid no longer gets
    // misread as "the visitor wants this card now" and skips the reveal
    // animation entirely.
    const revealOnInteraction = () => this.finishProductReveals();
    this.addEventListener('focusin', revealOnInteraction, options);
    this.addEventListener('click', revealOnInteraction, options);

    const slider = this.closest('slider-element');
    if (slider && theme.config.mqlSmall && this.hasAttribute('data-reveal-next-mobile')) {
      const revealFollowingCard = (currentCard) => {
        const cards = this.items;
        const currentIndex = cards.indexOf(currentCard);
        const nextCard = currentIndex >= 0 ? cards[currentIndex + 1] : null;
        if (!nextCard || this.productSeen.has(nextCard)) return;
        this.revealProductCard(nextCard, 0);
      };

      slider.addEventListener('slider:change', (event) => {
        revealFollowingCard(event.detail?.currentElement);
      }, options);

      let swipeFrame = null;
      slider.addEventListener('scroll', () => {
        if (swipeFrame !== null) return;
        swipeFrame = requestAnimationFrame(() => {
          swipeFrame = null;
          if (!this.isConnected) return;

          const cards = this.items;
          const offset = Math.abs(slider.itemOffset);
          if (!offset) return;

          const progress = Math.abs(slider.scrollLeft) / offset;
          const activeIndex = Math.min(cards.length - 1, Math.max(0, Math.ceil(progress - 0.9)));
          revealFollowingCard(cards[activeIndex]);
        });
      }, { ...options, passive: true });

      this.productListeners.signal.addEventListener('abort', () => {
        if (swipeFrame !== null) cancelAnimationFrame(swipeFrame);
      }, { once: true });
    }

    this.setAttribute('initialized', '');
    if (!this.motionReduced && !this.productMotionQuery.matches && 'IntersectionObserver' in window && Element.prototype.animate) {
      // Default 0.06 fires the instant a sliver of a card touches the
      // viewport edge - fine for most grids, but on a tall stacked mobile
      // grid that means a card low on the page can start (and often
      // finish) its reveal before the visitor has scrolled far enough to
      // actually see it happening. Opt-in via data-reveal-threshold-mobile
      // (0-1, how much of the card must be visible) to require it to be
      // meaningfully in view first - mobile only, and a no-op (same 0.06
      // default) everywhere it's not set or on desktop.
      let threshold = (theme.config.mqlSmall && this.hasAttribute('data-reveal-threshold-mobile'))
        ? parseFloat(this.getAttribute('data-reveal-threshold-mobile'))
        : 0.06;
      // The configured ratio is tuned against a normal phone viewport. A
      // card's height is a fixed CSS value, so on a taller viewport
      // (Pro Max, etc.) that same ratio needs the card scrolled further up
      // - even though it's already sitting comfortably on screen with a
      // lot of extra room below it, which reads as "why hasn't this
      // animated yet". Scale the ratio down for taller viewports so the
      // same amount of visible card content (not the same fraction of it)
      // triggers the reveal, floored so it never gets unreasonably eager.
      if (theme.config.mqlSmall && this.hasAttribute('data-reveal-threshold-mobile')) {
        const REFERENCE_VIEWPORT_HEIGHT = 700;
        threshold = Math.max(0.4, threshold * Math.min(1, REFERENCE_VIEWPORT_HEIGHT / window.innerHeight));
      }
      this.productObserver = new IntersectionObserver(entries => {
        let visible = entries.filter(entry => entry.isIntersecting && !this.productSeen.has(entry.target));
        if (visible.length && theme.config.mqlSmall && this.hasAttribute('data-reveal-next-mobile')) {
          const cards = this.items;
          const firstVisible = visible
            .map(entry => entry.target)
            .sort((a, b) => cards.indexOf(a) - cards.indexOf(b))[0];
          const nextCard = cards[cards.indexOf(firstVisible) + 1];
          if (nextCard && !this.productSeen.has(nextCard) && !visible.some(entry => entry.target === nextCard)) {
            visible.push({ target: nextCard });
          }
        }
        // IntersectionObserver fires an initial callback the instant
        // .observe() is called, even for targets that are still off-screen
        // (isIntersecting: false). Without this guard, that phantom
        // "nothing to reveal" batch was the one that ran first and flipped
        // hasRevealedProducts, so by the time the real post-scroll batch
        // arrived it skipped waitForRevealGate entirely and revealed
        // immediately instead of waiting on the heading/description.
        if (!visible.length) return;

        // While actively (fast) scrolling, cards can cross the visibility
        // threshold in several separate IntersectionObserver callbacks
        // instead of one combined batch. Only the FIRST such callback used
        // to go through waitForRevealGate - hasRevealedProducts flipped
        // true immediately, so every later callback that arrived before
        // the gate had even resolved took the "already revealed" branch
        // and revealed its cards instantly, skipping the wait. Instead,
        // accumulate every card that shows up before the gate resolves
        // into one pending batch, revealed together once it does.
        if (this.hasRevealedProducts) {
          if (this.gateResolved) {
            visible.forEach((entry, index) => this.revealProductCard(entry.target, index));
          } else {
            visible.forEach((entry) => {
              if (!this.pendingFirstBatch.includes(entry.target)) this.pendingFirstBatch.push(entry.target);
            });
          }
          return;
        }

        this.hasRevealedProducts = true;
        this.gateResolved = false;
        this.pendingFirstBatch = visible.map((entry) => entry.target);
        this.waitForRevealGate(() => {
          this.gateResolved = true;
          const cards = this.items;
          const toReveal = this.pendingFirstBatch.sort((a, b) => cards.indexOf(a) - cards.indexOf(b));
          toReveal.forEach((card, index) => this.revealProductCard(card, index));
        });
      }, { threshold });
    }
    this.observeProductCards();
  }

  observeProductCards() {
    if (!this.productObserver || this.motionReduced || this.productMotionQuery.matches) {
      this.finishProductReveals();
      return;
    }
    this.items.forEach(card => {
      if (this.productSeen.has(card) || card.hasAttribute('data-product-pending')) return;
      card.setAttribute('data-product-pending', '');
      this.productObserver.observe(card);
    });
  }

  // Waits for the section's own heading/description to actually finish
  // animating (theme:reveal-complete, dispatched by AnimateElement) before
  // running `callback`, instead of guessing a fixed delay that's wrong
  // whenever this grid's own scroll-trigger timing doesn't happen to line
  // up with the text's. Opt-in via data-reveal-after (a selector for the
  // gating element, e.g. this section's .description) - falls back to a
  // flat data-reveal-delay (or 1850ms) when no selector is set, nothing
  // matches it, or it takes too long to fire.
  waitForRevealGate(callback) {
    const fallback = this.hasAttribute('data-reveal-delay') ? parseInt(this.getAttribute('data-reveal-delay')) : 1850;
    const mobileSelector = theme.config.mqlSmall ? this.getAttribute('data-reveal-after-mobile') : null;
    const selector = mobileSelector || this.getAttribute('data-reveal-after');
    const gate = selector ? document.querySelector(selector) : null;
    if (!gate) {
      setTimeout(callback, fallback);
      return;
    }
    if (gate.dataset.revealComplete) {
      callback();
      return;
    }
    // Same as AnimateElement.waitForGate(): only skip waiting when the gate
    // never even started (its own Motion.inView never fired) and has
    // already scrolled above the viewport - a visitor who jumped straight
    // past it. If it DID start, it's genuinely mid-flight even though
    // normal continued scrolling carried it above the fold too - still
    // wait for its real completion, that's the ordering this gate exists
    // to guarantee.
    if (!gate.dataset.revealStarted && gate.getBoundingClientRect().bottom < 0) {
      callback();
      return;
    }
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      callback();
    };
    gate.addEventListener('theme:reveal-complete', finish, { once: true });
    // Waiting for the gate's animation to fully finish (e.g. ~1.85s for the
    // default description fade-up: 350ms delay + 1.5s duration) reads as a
    // long dead pause. Its cubic-bezier(0.16,1,0.3,1) ease-out is a bezier
    // curve, so the fraction of visual progress at a given fraction of
    // duration is the same regardless of the element's actual duration -
    // sampled it directly: ~75% opacity/well underway by 20% of duration,
    // ~97% settled by 50%. Products only need to start after the gate is
    // clearly forming, not fully finished (the heading, which finishes
    // faster, is by then long since visually settled too under the same
    // curve). The real completion event and outer fallback below stay as
    // safety nets in case timing or settings differ.
    const GATE_DURATIONS_MS = { 'fade-in': 1500, 'fade-up': 1500, 'fade-up-fast': 900, 'fade-up-large': 1000, 'zoom-out': 1300 };
    const gateDelayMs = gate.hasAttribute('data-animate-delay') ? parseInt(gate.getAttribute('data-animate-delay')) : 0;
    const gateDurationMs = GATE_DURATIONS_MS[gate.getAttribute('data-animate')] ?? 1500;
    const earlyTriggerOffset = gateDelayMs + gateDurationMs * 0.2;
    // Must be timed from when the gate's VISUAL animation actually begins
    // (animateStartedAt/theme:reveal-animating), not from whenever this
    // function happens to run - if the gate itself has its own
    // data-wait-for chain (e.g. a heading waiting on the hero), its real
    // animation can start well after this call, and firing the early
    // trigger on the old, naive "now" basis revealed products before the
    // heading had even started.
    const scheduleEarlyTrigger = () => {
      const elapsedSinceGateStarted = performance.now() - parseFloat(gate.dataset.animateStartedAt);
      setTimeout(finish, Math.max(0, earlyTriggerOffset - elapsedSinceGateStarted));
    };
    if (gate.dataset.animateStartedAt) {
      scheduleEarlyTrigger();
    } else {
      gate.addEventListener('theme:reveal-animating', scheduleEarlyTrigger, { once: true });
    }
    setTimeout(finish, fallback + 1500);
  }

  revealProductCard(card, index) {
    if (!this.isConnected || this.productSeen.has(card)) return;
    this.productObserver?.unobserve(card);
    this.productSeen.add(card);
    card.removeAttribute('data-product-pending');
    card.setAttribute('data-product-revealing', '');
    const compact = window.matchMedia('(max-width: 767px), (pointer: coarse)').matches;
    // A grid can opt into a slower/larger/more-staggered reveal via
    // data-reveal-duration (ms), data-reveal-distance (px) and
    // data-reveal-stagger (ms, per card) on the motion-list itself.
    // Mobile uses a longer duration and wider stagger so stacked cards
    // enter at a calm, readable pace instead of snapping into place.
    const mobileDuration = compact && this.hasAttribute('data-reveal-duration-mobile') ? parseInt(this.getAttribute('data-reveal-duration-mobile')) : null;
    const mobileDistance = compact && this.hasAttribute('data-reveal-distance-mobile') ? parseInt(this.getAttribute('data-reveal-distance-mobile')) : null;
    const mobileStagger = compact && this.hasAttribute('data-reveal-stagger-mobile') ? parseInt(this.getAttribute('data-reveal-stagger-mobile')) : null;
    const duration = mobileDuration ?? (this.hasAttribute('data-reveal-duration') ? parseInt(this.getAttribute('data-reveal-duration')) : (compact ? 760 : 720));
    const distance = mobileDistance ?? (this.hasAttribute('data-reveal-distance') ? parseInt(this.getAttribute('data-reveal-distance')) : (compact ? 26 : 24));
    const stagger = mobileStagger ?? (this.hasAttribute('data-reveal-stagger') ? parseInt(this.getAttribute('data-reveal-stagger')) : (compact ? 85 : 90));
    // 360 (4 items at the 90ms default) capped out right at a typical grid's
    // own row width, so any batch bigger than about one row - collection
    // pages default to 16 products per page, all revealed in a single batch
    // when they're already in the initial viewport - had every card past
    // the 4th bunch up at the same capped delay instead of continuing to
    // stagger into the rows below. Raised so the default 16-per-page batch
    // (15 * 90ms = 1350ms for its last card) stays fully staggered; only
    // genuinely oversized batches (products_per_page can go up to 50) still
    // get capped, so the wait for the very last card stays reasonable.
    const staggerCap = this.hasAttribute('data-reveal-stagger-cap') ? parseInt(this.getAttribute('data-reveal-stagger-cap')) : 1500;
    const easing = compact && this.hasAttribute('data-reveal-easing-mobile') ? this.getAttribute('data-reveal-easing-mobile') : (this.getAttribute('data-reveal-easing') || 'cubic-bezier(0.16, 1, 0.3, 1)');
    const animation = card.animate(
      [{ opacity: 0, translate: `0 ${distance}px` }, { opacity: 1, translate: '0 0' }],
      { duration: duration, delay: Math.min(index * stagger, staggerCap), easing: easing, fill: 'both' }
    );
    this.productAnimations.set(card, animation);
    const finish = () => {
      if (this.productAnimations.get(card) !== animation) return;
      this.productAnimations.delete(card);
      card.removeAttribute('data-product-revealing');
      animation.cancel();
    };
    animation.finished.then(() => {
      if (this.productAnimations.get(card) !== animation) return;
      animation.cancel();
      if (this.hasAttribute('data-reveal-children')) {
        const entrances = card.getAnimations({ subtree: true }).filter(child =>
          'animationName' in child && !child.effect.pseudoElement && Number.isFinite(child.effect.getComputedTiming().endTime) &&
          child.effect.getKeyframes().some(frame => 'transform' in frame || 'opacity' in frame)
        );
        if (entrances.length) {
          Promise.allSettled(entrances.map(child => child.finished)).then(finish);
          return;
        }
      }
      finish();
    }, finish);
  }

  finishProductReveals() {
    this.productObserver?.disconnect();
    this.productAnimations?.forEach((animation, card) => {
      card.removeAttribute('data-product-revealing');
      animation.cancel();
    });
    this.productAnimations?.clear();
    this.items.forEach(card => {
      card.removeAttribute('data-product-pending');
      this.productSeen?.add(card);
    });
  }

  get items() {
    return Array.from(this.querySelectorAll('.card'));
  }

  get itemsToShow() {
    return Array.from(this.querySelectorAll('.card:not([style])'));
  }

  get initialized() {
    return this.hasAttribute('initialized');
  }

  // Legacy (non data-product-reveal) grids - blog, search, collection
  // lists, portfolio, bundles. Matched to the product-card reveal above:
  // a short rise on the same expo-out curve, a tighter per-item stagger,
  // and a cap so a long batch doesn't keep the last cards waiting. (Was
  // 50px on Motion's default 'ease' with an uncapped 100ms stagger.)
  getAnimationParams() {
    return {
      distance: this.isMobile ? 20 : 24,
      duration: this.motionReduced ? 0.001 : 0.7,
      staggerDelay: this.motionReduced ? 0 : (this.isMobile ? 0.06 : 0.07),
      staggerCap: this.motionReduced ? 0 : 1,
      easing: [0.16, 1, 0.3, 1]
    };
  }

  getVisibleItems(items) {
    const viewportHeight = window.innerHeight + window.scrollY;
    const visible = [];
    const invisible = [];
    
    items.forEach(item => {
      const rect = item.getBoundingClientRect();
      const isVisible = rect.top < viewportHeight && rect.bottom > 0;
      
      if (isVisible) {
        visible.push(item);
      } else {
        invisible.push(item);
      }
    });
    
    return { visible, invisible };
  }

  unload() {
    if (this.hasAttribute('data-product-reveal')) return this.finishProductReveals();
    const { distance } = this.getAnimationParams();
    const visibleItems = this.items;

    // Same pending/revealing attributes as the data-product-reveal path: the
    // card hover rules (apple-product-card.css, apple-collection.css) wait for
    // them, so a hover can't start its scale/transform transition on a card
    // this list is still sliding in (search results, bundles, blog grids...),
    // which read as choppy motion until the reveal ended.
    visibleItems.forEach((item) => item.setAttribute('data-product-pending', ''));
    Motion.animate(visibleItems, { y: distance, opacity: 0, visibility: 'hidden' }, { duration: 0 });
  }

  startReveal(items) {
    items.forEach((item) => {
      item.removeAttribute('data-product-pending');
      item.setAttribute('data-product-revealing', '');
    });
  }

  async load() {
    if (this.hasAttribute('data-product-reveal')) return this.setupProductReveal();
    const { distance, duration, staggerDelay, staggerCap, easing } = this.getAnimationParams();
    const { visible: visibleItems, invisible: inVisibleItems } = this.getVisibleItems(this.items);

    this.startReveal(this.items);
    try {
      await Motion.animate(visibleItems, { y: [distance, 0], opacity: [0, 1], visibility: ['hidden', 'visible'] }, { duration: duration, easing: easing, delay: (i) => Math.min(i * staggerDelay, staggerCap) }).finished;
      // Awaited (wasn't before): the "initialized" attribute below gates a
      // CSS transition on .product-card that intentionally stays off until
      // this whole reveal is done, so it can't fight the WAAPI animation
      // over the same transform - but firing this without waiting for it
      // meant initialized got set (and the transform cleared) while these
      // items, usually just the last row, were still mid-animation. The
      // CSS transition would then kick in on top of the still-running JS
      // one, reading as the last product "double" sliding in.
      await Motion.animate(inVisibleItems, { y: [distance, 0], opacity: [0, 1], visibility: ['hidden', 'visible'] }, { duration: duration, easing: easing }).finished;
    } finally {
      this.items.forEach((item) => item.removeAttribute('data-product-revealing'));
    }

    this.items.forEach(item => item.style.removeProperty('transform'));
    this.setAttribute('initialized', '');
  }

  async reload() {
    if (this.hasAttribute('data-product-reveal')) return this.setupProductReveal();
    const { distance, duration, staggerDelay, staggerCap, easing } = this.getAnimationParams();
    const { visible: visibleItems, invisible: inVisibleItems } = this.getVisibleItems(this.itemsToShow);

    this.startReveal(this.itemsToShow);
    try {
      await Motion.animate(visibleItems, { y: [distance, 0], opacity: [0, 1], visibility: ['hidden', 'visible'] }, { duration: duration, easing: easing, delay: (i) => Math.min(i * staggerDelay, staggerCap) }).finished;
      // Same fix as load() above - await this one too so it can't still be
      // running when the transform cleanup / CSS transition gate below fires.
      await Motion.animate(inVisibleItems, { y: [distance, 0], opacity: [0, 1], visibility: ['hidden', 'visible'] }, { duration: duration, easing: easing }).finished;
    } finally {
      this.items.forEach((item) => item.removeAttribute('data-product-revealing'));
    }

    this.items.forEach(item => item.style.removeProperty('transform'));
  }
}
customElements.define('motion-list', MotionList);

class LazyBackground extends HTMLElement {
  constructor() {
    super();

    this.init();
  }

  get image() {
    const style = window.getComputedStyle(this);
    return style.backgroundImage ? style.backgroundImage.slice(5, -2).replace('width=1', 'width=720') : false;
  }

  init() {
    // The hero tiles (images-with-text-overlay.liquid) carry an inline width=1
    // placeholder that legacyInit() swaps for width=720. imageLoaded() resolves
    // at once for its detached <img>, so that swap has always landed before the
    // first paint. For an absolute or /-relative inline URL (resolving it only
    // prepends the origin), make the same swap here from the inline value
    // instead of the computed one: the placeholder is then never style-resolved
    // (no forced style recalc per tile, no width=1 download). The <img> still
    // starts the 720 download at the same moment as before.
    const inline = this.style.backgroundImage.match(/^url\("((?:https?:)?\/[^"'()\\\s]*)"\)$/);
    const image = inline && inline[1].replace('width=1', 'width=720');
    if (!image || image === inline[1]) return this.legacyInit();

    const img = document.createElement('img');
    img.src = image;
    this.style.backgroundImage = `url(${image})`;
  }

  async legacyInit() {
    if (!this.image) return;

    const img = document.createElement('img');
    img.src = this.image;
    img.style.visibility = 'hidden';
    img.style.position = 'absolute';

    await theme.utils.imageLoaded(img);
    this.style.backgroundImage = `url(${this.image})`;
    img.remove();
  }
}
customElements.define('lazy-background', LazyBackground);

class MenuToggle extends MagnetButton {
  constructor() {
    super();

    this.addEventListener('click', this.onClick);
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }

  get expanded() {
    return this.getAttribute('aria-expanded') === 'true';
  }

  onClick() {
    this.setAttribute('aria-expanded', this.expanded ? 'false' : 'true');
    if (this.controlledElement) this.controlledElement.classList.toggle('active');
  }
}
customElements.define('menu-toggle', MenuToggle, { extends: 'button' });

class ScrollShadow extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, this.init.bind(this));
  }

  get template() {
    return this.querySelector('template');
  }

  init() {
    this.attachShadow({ mode: 'open' });
    this.shadowRoot.appendChild(this.template.content.cloneNode(true));
    this.shadowRoot.addEventListener('slotchange', () => this.load());
    this.updater = new theme.scrollShadow.Updater(this.shadowRoot.children[1]);

    this.load();
  }

  load() {
    this.updater.on(this.children[0]);
  }

  disconnectedCallback() {
    this.updater?.on();
  }
}
customElements.define('scroll-shadow', ScrollShadow);

class CustomSelect extends HTMLSelectElement {
  constructor() {
    super();

    this.onChange();
    this.addEventListener('change', this.onChange);
  }

  onChange() {
    this.value !== '' || this.selectedIndex === -1 ? this.setAttribute('selected', '') : this.removeAttribute('selected');
  }
}
customElements.define('custom-select', CustomSelect, { extends: 'select' });

class GMap extends HTMLElement {
  constructor() {
    super();

    if (!this.hasAttribute('data-api-key') || !this.hasAttribute('data-map-address')) {
      return;
    }

    Motion.inView(this, this.prepMapApi.bind(this), { margin: '600px 0px 600px 0px' });

    // Global function called by Google on auth errors.
    // Show an auto error message on all map instances.
    window.gm_authFailure = () => {

      // Show errors only to merchant in the editor.
      if (Shopify.designMode) {
        window.mapError(theme.strings.authError);
      }
    };

    window.mapError = (error, element) => {
      const container = element ? element.closest('.shopify-section') : document;
      container.querySelectorAll('.gmap--error').forEach((error) => {
        error.remove();
      });

      container.querySelectorAll('g-map').forEach((map) => {
        const message = document.createElement('div');
        message.classList.add('rte', 'alert', 'alert--error', 'gmap--error');
        message.innerHTML = error;
        map.closest('.with-map').prepend(message);
      });
    };

    window.gmNoop = () => { };
  }

  prepMapApi() {
    this.loadScript()
      .then(this.initMap.bind(this))
      .then(() => {
        setTimeout(() => {
          const container = this.closest('.banner__map');
          if (container && container.previousElementSibling) {
            container.previousElementSibling.classList.remove('opacity-0');
          }
        }, 1e3);
      });
  }

  loadScript() {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      document.body.appendChild(script);
      script.onload = resolve;
      script.onerror = reject;
      script.async = true;
      script.src = `https://maps.googleapis.com/maps/api/js?key=${this.getAttribute('data-api-key')}&callback=gmNoop`;
    });
  }

  initMap() {
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ address: this.getAttribute('data-map-address') }, (results, status) => {
      if (status !== google.maps.GeocoderStatus.OK) {

        // Show errors only to merchant in the editor.
        if (Shopify.designMode) {
          let errorMessage;

          switch (status) {
            case 'ZERO_RESULTS':
              errorMessage = theme.strings.addressNoResults;
              break;
            case 'OVER_QUERY_LIMIT':
              errorMessage = theme.strings.addressQueryLimit;
              break;
            case 'REQUEST_DENIED':
              errorMessage = theme.strings.authError;
              break;
            default:
              errorMessage = theme.strings.addressError;
              break;
          }
          window.mapError(errorMessage, this);
        }
      }
      else {
        const mapOptions = {
          zoom: parseInt(this.getAttribute('data-zoom')),
          center: results[0].geometry.location,
          draggable: true,
          clickableIcons: false,
          scrollwheel: false,
          disableDoubleClickZoom: true,
          disableDefaultUI: true
        };

        const map = new google.maps.Map(this, mapOptions), center = map.getCenter();

        map.setCenter(center);

        const icon = {
          path: "M22.6746 0C10.2174 0 0 8.79169 0 21.5118C0 31.2116 4.33864 38.333 9.26606 42.998C11.7232 45.3243 14.3387 47.0534 16.6674 48.2077C18.9384 49.3333 21.1148 50 22.6746 50C24.2345 50 26.4108 49.3333 28.6818 48.2077C31.0105 47.0534 33.626 45.3243 36.0832 42.998C41.0106 38.333 45.3492 31.2116 45.3492 21.5118C45.3492 8.79169 35.1318 0 22.6746 0ZM29.6514 22.6746C29.6514 26.5278 26.5278 29.6514 22.6746 29.6514C18.8214 29.6514 15.6978 26.5278 15.6978 22.6746C15.6978 18.8214 18.8214 15.6978 22.6746 15.6978C26.5278 15.6978 29.6514 18.8214 29.6514 22.6746Z",
          fillColor: this.getAttribute('data-marker-color'),
          fillOpacity: 1,
          anchor: new google.maps.Point(15, 55),
          strokeWeight: 0,
          scale: 0.7
        };

        new google.maps.Marker({
          map: map,
          position: map.getCenter(),
          icon: icon
        });

        const styledMapType = new google.maps.StyledMapType(JSON.parse(this.parentNode.querySelector('[data-gmap-style]').innerHTML));

        map.mapTypes.set('styled_map', styledMapType);
        map.setMapTypeId('styled_map');
      }
    });
  }
}
customElements.define('g-map', GMap);

class GMapLocations extends HTMLUListElement {
  constructor() {
    super();

    this.selectedIndex = this.selectedIndex;
    this.buttons.forEach((button, index) => button.addEventListener('click', () => this.selectedIndex = index));

    if (Shopify.designMode) {
      this.addEventListener('shopify:block:select', (event) => this.selectedIndex = this.buttons.indexOf(event.target));
    }
  }

  static get observedAttributes() {
    return ['selected-index'];
  }

  get selectedIndex() {
    return parseInt(this.getAttribute('selected-index')) || 0;
  }

  set selectedIndex(index) {
    this.setAttribute('selected-index', Math.min(Math.max(index, 0), this.buttons.length - 1).toString());
  }

  get buttons() {
    return this._buttons = this._buttons || Array.from(this.hasAttribute('selector') ? this.querySelectorAll(this.getAttribute('selector')) : this.children);
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }

  attributeChangedCallback(name, oldValue, newValue) {
    this.buttons.forEach((button, index) => {
      button.classList.toggle('active', index === parseInt(newValue));
      button.querySelector('[data-map-button]')?.classList.toggle('button--secondary', index !== parseInt(newValue));
    });

    if (name === 'selected-index' && oldValue !== null && oldValue !== newValue) {
      if (this.controlledElement) {
        const button = this.buttons[parseInt(newValue)];
        this.controlledElement.setAttribute('data-map-address', button.getAttribute('data-map-address'));
        this.controlledElement.initMap();
      }
    }
  }
}
customElements.define('g-map-locations', GMapLocations, { extends: 'ul' });

class PreviousButton extends HoverButton {
  constructor() {
    super();

    this.load();
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }

  load() {
    this.onClickListener = this.onClick.bind(this);
    this.addEventListener('click', this.onClickListener);

    if (this.controlledElement) {
      this.updateStatusListener = this.updateStatus.bind(this);
      this.controlledElement.addEventListener('slider:previousStatus', this.updateStatusListener);
    }
  }

  unload() {
    this.removeEventListener('click', this.onClickListener);
    if (this.controlledElement) {
      this.controlledElement.removeEventListener('slider:previousStatus', this.updateStatusListener);
    }
  }

  onClick() {
    (this.controlledElement ?? this).dispatchEvent(new CustomEvent('slider:previous', { bubbles: true, cancelable: true }));
  }

  updateStatus(event) {
    switch (event.detail.status) {
      case 'hidden':
          this.hidden = true;
        break;

      case 'disabled':
        this.disabled = true;
        break;

      default:
        this.hidden = false;
        this.disabled = false;
    }
  }
}
customElements.define('previous-button', PreviousButton, { extends: 'button' });

class NextButton extends HoverButton {
  constructor() {
    super();

    this.load();
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }

  load() {
    this.onClickListener = this.onClick.bind(this);
    this.addEventListener('click', this.onClickListener);

    if (this.controlledElement) {
      this.updateStatusListener = this.updateStatus.bind(this);
      this.controlledElement.addEventListener('slider:nextStatus', this.updateStatusListener);
    }
  }

  unload() {
    this.removeEventListener('click', this.onClickListener);
    if (this.controlledElement) {
      this.controlledElement.removeEventListener('slider:nextStatus', this.updateStatusListener);
    }
  }

  onClick() {
    (this.controlledElement ?? this).dispatchEvent(new CustomEvent('slider:next', { bubbles: true, cancelable: true }));
  }

  updateStatus(event) {
    switch (event.detail.status) {
      case 'hidden':
          this.hidden = true;
        break;

      case 'disabled':
        this.disabled = true;
        break;

      default:
        this.hidden = false;
        this.disabled = false;
    }
  }
}
customElements.define('next-button', NextButton, { extends: 'button' });

class SliderElement extends HTMLElement {
  constructor() {
    super();

    this.updateTabindexListener = this.updateTabindex.bind(this);
    Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' });
  }

  connectedCallback() {
    if (!this.initialized) return;
    this.addBreakpointListeners();
  }

  disconnectedCallback() {
    this.cancelGalleryScroll();
    this.stopWatchingInterruptedGlide();
    document.removeEventListener('matchSmall', this.updateTabindexListener);
    document.removeEventListener('unmatchSmall', this.updateTabindexListener);
  }

  addBreakpointListeners() {
    document.addEventListener('matchSmall', this.updateTabindexListener);
    document.addEventListener('unmatchSmall', this.updateTabindexListener);
  }

  get looping() {
    return false;
  }

  get items() {
    return this._items = this._items || Array.from(this.hasAttribute('selector') ? this.querySelectorAll(this.getAttribute('selector')) : this.children);
  }

  get itemsToShow() {
    return Array.from(this.items).filter(element => element.clientWidth > 0);
  }

  get itemOffset() {
    if (theme.config.rtl) {
      return this.itemsToShow.length > 1 ? this.itemsToShow[0].offsetLeft - this.itemsToShow[1].offsetLeft : 0;
    }

    return this.itemsToShow.length > 1 ? this.itemsToShow[1].offsetLeft - this.itemsToShow[0].offsetLeft : 0;
  }

  get perPage() {
    let elementWidth = this.clientWidth;
    const mql = window.matchMedia('screen and (min-width: 1280px)');
    if (mql.matches) {
      const styles = window.getComputedStyle(this);
      elementWidth = this.clientWidth - parseFloat(styles.paddingLeft) - parseFloat(styles.paddingRight);
    }

    return Math.floor(elementWidth / this.itemOffset);
  }

  get isScrollable() {
    const style = window.getComputedStyle(this);
    const hasScrollableOverflow = style.overflow === 'scroll' || style.overflow === 'auto' || style.overflowY === 'scroll' || style.overflowY === 'auto' || style.overflowX === 'scroll' || style.overflowX === 'auto';
    const hasScrollableContent = this.scrollHeight > this.clientHeight || this.scrollWidth > this.clientWidth;
    
    return hasScrollableOverflow && hasScrollableContent;
  }

  reset() {
    this._items = Array.from(this.hasAttribute('selector') ? this.querySelectorAll(this.getAttribute('selector')) : this.children);
  }
  
  init() {
    this.hasPendingOnScroll = false;
    this.currentPage = 1;
    this.initialized = true;
    this.updateButtons();
    this.updateTabindex();

    this.addEventListener('scroll', () => {
      clearTimeout(this.scrollendTimeout);
      clearTimeout(this.scrollUpdateTimeout);
      this.scrollUpdateTimeout = setTimeout(() => this.update(), 50);
    });
    this.addEventListener('scrollend', this.scrollend);
    this.addEventListener('slider:previous', this.previous);
    this.addEventListener('slider:next', this.next);
    this.addBreakpointListeners();
    if (this.closest('media-gallery')) this.addSwipePaging();

    if (Shopify.designMode) {
      this.addEventListener('shopify:block:select', (event) => event.target.scrollIntoView({behavior: 'smooth'}));
    }
  }

  // Touch swipes on the product gallery's images and videos page exactly
  // one slide. Left to the browser, a light swipe on this proximity-snapped
  // slider either sprang back to the same image or came to rest partway
  // between two, and a hard one could fling past several. Image and video
  // slides are touch-action: pan-y (theme.css), so the browser still
  // scrolls the page for a mostly-vertical drag (and pinch-zooms), but
  // leaves horizontal drags to this: the slide follows the finger (at most
  // one slide either way), and on release glides on - carrying the finger's
  // speed - to the next or previous slide in the swipe's direction, however
  // short it was. Mouse, YouTube/Vimeo and 3D model slides keep their own
  // behaviour. (A video here has no controls to drag: a tap pauses or plays
  // it - VideoMedia.)
  //
  // Not on iPhone and iPad (every browser there runs on Safari's engine):
  // Safari has no direction lock for pan-y, so a sideways swipe that strayed
  // at all from level was claimed for page scrolling, cancelling this swipe
  // and springing the image back. There the browser pages the gallery
  // itself, one slide per swipe (theme.css).
  addSwipePaging() {
    if (window.CSS?.supports?.('-webkit-touch-callout', 'none')) return;
    let swipe = null;
    // Released (lifted), or cancelled - the browser took the gesture over,
    // or a second finger began a pinch - which returns to the start slide.
    const finish = (event, released) => {
      const { base, pitch, engaged, samples } = swipe;
      swipe = null;
      this.gallerySwipe = false;
      if (!engaged) return;
      this.lastArrowNavigation = performance.now();
      let step = 0;
      let speed = 0;
      if (released) {
        // Direction (physical scroll) of the whole swipe, and the finger's
        // speed over its last ~100 ms: a quick flick back toward the start
        // slide cancels the swipe instead of paging past it.
        const moved = -Math.sign(event.clientX - samples[0].x);
        const first = samples.find((sample) => event.timeStamp - sample.time < 100) ?? samples.at(-1);
        if (event.timeStamp > first.time) speed = -(event.clientX - first.x) / (event.timeStamp - first.time);
        step = moved;
        if (Math.abs(speed) > 0.3) step = moved === 0 || Math.sign(speed) === moved ? Math.sign(speed) : 0;
      }
      const target = this.gallerySnapPosition(base + step * pitch);
      if (theme.config.motionReduced) {
        this.scrollToPosition(target);
        return;
      }
      this.hasPendingOnScroll = true;
      this.animateGalleryScroll(target, Math.sign(speed) === Math.sign(target - this.scrollLeft) ? speed : 0);
    };

    this.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse') return;
      if (!event.isPrimary) {
        if (swipe) finish(event, false);
        return;
      }
      const type = event.target.closest?.('[data-media-type]')?.getAttribute('data-media-type');
      if (type !== 'image' && type !== 'video') return;
      const pitch = Math.abs(this.itemOffset);
      if (!(pitch > 0) || this.scrollWidth - this.clientWidth < 1) return;
      swipe = { id: event.pointerId, x: event.clientX, y: event.clientY, pitch, engaged: false, samples: [{ x: event.clientX, time: event.timeStamp }] };
      // While a finger is down on a slide, its own touchmoves no longer
      // stop a running glide (see animateGalleryScroll): a tap leaves it be,
      // and a swipe takes it over from here.
      this.gallerySwipe = true;
    }, { passive: true });

    this.addEventListener('pointermove', (event) => {
      if (!swipe || event.pointerId !== swipe.id) return;
      const dx = event.clientX - swipe.x;
      if (!swipe.engaged) {
        if (Math.abs(dx) < 8 || Math.abs(dx) <= Math.abs(event.clientY - swipe.y)) return;
        // Paging counts from the slide the gallery is on - or, mid-glide,
        // the one it's headed to, so quick swipes in a row each move on one.
        swipe.base = this.galleryScroll?.target ?? this.gallerySnapPosition(this.scrollLeft);
        this.stopWatchingInterruptedGlide();
        this.cancelGalleryScroll(true);
        cancelAnimationFrame(this.galleryRelease);
        this.galleryRelease = null;
        this.style.scrollSnapType = 'none';
        this.style.scrollBehavior = 'auto';
        swipe.engaged = true;
        swipe.start = this.scrollLeft + dx;
      }
      swipe.samples.push({ x: event.clientX, time: event.timeStamp });
      if (swipe.samples.length > 8) swipe.samples.splice(1, 1);
      const left = swipe.start - dx;
      this.scrollLeft = Math.max(swipe.base - swipe.pitch, Math.min(swipe.base + swipe.pitch, left));
    }, { passive: true });

    ['pointerup', 'pointercancel'].forEach((type) => this.addEventListener(type, (event) => {
      if (swipe && event.pointerId === swipe.id) finish(event, type === 'pointerup');
    }, { passive: true }));
  }

  // Mid-glide (see animateGalleryScroll), step from where the glide is
  // headed rather than wherever it happens to be, so a quick second click
  // still lands exactly on a slide.
  previous() {
    this.lastArrowNavigation = performance.now();
    this.scrollPosition = (this.galleryScroll?.target ?? this.scrollLeft) - this.perPage * this.itemOffset * (theme.config.rtl ? -1 : 1);
    this.scrollToPosition(this.scrollPosition);
  }

  next() {
    this.lastArrowNavigation = performance.now();
    this.scrollPosition = (this.galleryScroll?.target ?? this.scrollLeft) + this.perPage * this.itemOffset * (theme.config.rtl ? -1 : 1);
    this.scrollToPosition(this.scrollPosition);
  }

  select(selectedIndex, immediate = false) {
    const items = this.itemsToShow;
    const selectedItem = items[selectedIndex - 1];
    if (!selectedItem) return;

    this.scrollPosition = selectedItem.offsetLeft - items[0].offsetLeft;
    this.scrollToPosition(this.scrollPosition, immediate);
  }

  // True while the gallery glides, and briefly after it lands or an arrow
  // is pressed (whichever is later). An
  // arrow that hides at the first/last image stops catching clicks at once,
  // so rapid presses on it landed on the image underneath - still the one
  // being left, mid-glide - and opened its zoom. Media buttons ignore
  // clicks while this is set.
  get navigating() {
    return Boolean(this.galleryScroll) || performance.now() - (this.lastArrowNavigation ?? -Infinity) < 350;
  }

  selected(selectedIndex) {
    return this.itemsToShow[selectedIndex];
  }

  scrollend(event) {
    if (event && event.target !== this) return;
    // Each frame of the gallery glide is its own instant scroll, so the
    // browser can report a scrollend for every one; only the glide's own
    // final call (no event) counts.
    if (event && this.galleryScroll) return;

    clearTimeout(this.scrollendTimeout);
    clearTimeout(this.scrollUpdateTimeout);
    this.updateCurrentPage();
    this.hasPendingOnScroll = false;
    this.updateButtons();
    this.dispatchEventHandler();
  }

  updateCurrentPage() {
    const items = this.itemsToShow;
    const previousPage = this.currentPage;
    const offset = Math.abs(this.itemOffset);
    this.currentPage = offset > 0
      ? Math.max(1, Math.min(items.length, Math.round(Math.abs(this.scrollLeft) / offset) + 1))
      : 1;

    items.forEach((sliderItem, index) => {
      sliderItem.classList.toggle('selected', index + 1 === this.currentPage);
    });

    return this.currentPage !== previousPage;
  }

  update() {
    clearTimeout(this.scrollendTimeout);

    const pageChanged = this.updateCurrentPage();
    if (pageChanged && !this.hasPendingOnScroll) {
      this.dispatchEventHandler();
    }

    if (!('onscrollend' in this) && (pageChanged || this.hasPendingOnScroll)) {
      this.scrollendTimeout = setTimeout(() => {
        this.dispatchEvent(new CustomEvent('scrollend', { bubbles: true, composed: true }));
      }, 75);
    }

    if (this.looping) return;

    this.updateButtons();
  }

  // Mid-glide (see animateGalleryScroll) the arrows already reflect the
  // slide it's headed to, so the one that needs to hide or appear does so
  // the moment the glide starts rather than once the image passes halfway.
  updateButtons() {
    const page = this.galleryScroll?.page ?? this.currentPage;
    let previousDisabled = page === 1;
    let nextDisabled = page === this.itemsToShow.length;

    if (this.perPage > 1) {
      previousDisabled = previousDisabled || this.itemsToShow.length > 0 && this.isVisible(this.itemsToShow[0]) && this.scrollLeft === 0;
      nextDisabled = nextDisabled || this.itemsToShow.length > 0 && this.isVisible(this.itemsToShow[this.itemsToShow.length - 1]);
    }

    this.dispatchEvent(
      new CustomEvent('slider:previousStatus', {
        bubbles: true,
        detail: {
          status: previousDisabled ? (nextDisabled ? 'hidden' : 'disabled') : 'visible'
        },
      })
    );
    this.dispatchEvent(
      new CustomEvent('slider:nextStatus', {
        bubbles: true,
        detail: {
          status: nextDisabled ? (previousDisabled ? 'hidden' : 'disabled') : 'visible'
        },
      })
    );
  }

  updateTabindex() {
    this.removeAttribute('tabindex');

    if (this.isScrollable) {
      this.setAttribute('tabindex', '0');
    }
  }

  isVisible(element, offset = 0) {
    const lastVisibleSlide = this.clientWidth + this.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.scrollLeft;

    /*
    const lastVisibleSlide = this.clientWidth + this.scrollLeft - offset;
    const offsetLeft = element.getBoundingClientRect().left;
    return offsetLeft + element.clientWidth <= lastVisibleSlide && offsetLeft >= this.scrollLeft;
    */
  }

  scrollToPosition(position, immediate = false) {
    if (!Number.isFinite(position)) return;

    if (this.closest('media-gallery')) {
      this.hasPendingOnScroll = !immediate;
      if (!immediate && !theme.config.motionReduced) {
        this.animateGalleryScroll(position);
        return;
      }
      this.announceGalleryTarget(position);
      this.cancelGalleryScroll();
      this.scrollTo({
        left: position,
        behavior: immediate ? 'instant' : 'auto'
      });
      return;
    }

    clearTimeout(this.scrollendTimeout);
    clearTimeout(this.scrollUpdateTimeout);
    const maximum = Math.max(0, this.scrollWidth - this.clientWidth);
    position = theme.config.rtl
      ? Math.max(-maximum, Math.min(0, position))
      : Math.max(0, Math.min(maximum, position));
    this.scrollPosition = position;
    const instant = immediate || theme.config.motionReduced;
    const alreadyAtPosition = Math.abs(this.scrollLeft - position) < 1;
    this.hasPendingOnScroll = !instant;

    this.scrollTo({
      left: position,
      behavior: instant ? 'instant' : 'smooth'
    });

    if (instant || alreadyAtPosition) {
      this.scrollend();
    } else {
      this.scrollendTimeout = setTimeout(() => this.scrollend(), 150);
    }
  }

  // The product gallery's glide for chevron and thumbnail-click navigation.
  // The browser's native smooth scroll uses a short, fixed curve that reads
  // as a jolt at this slide size, and with scroll-snap on it can be tugged
  // toward a snap point partway through. This follows a critically damped
  // spring instead (Apple's own motion model, no overshoot): it picks up
  // speed quickly and gently, then spends most of its time softly settling
  // rather than braking hard - an ease-in-out cubic surged through the
  // middle and read as a bounce. Snapping is paused only while it runs; it
  // always lands exactly on a
  // slide (already a snap point), so restoring snapping never moves it and
  // wheel/touch/drag snapping is unchanged. Only genuine scrolling on the
  // slider (see cancel below) hands control back to the browser.
  animateGalleryScroll(position, swipeVelocity = 0) {
    // Retargeting mid-glide keeps snapping paused throughout - restoring
    // it for even a moment would tug the image toward the nearest slide -
    // and carries the current speed into the new glide rather than
    // restarting from a standstill or lurching forward. A released touch
    // swipe (see addSwipePaging) hands over the finger's speed the same way.
    const velocity = this.galleryScroll?.velocity ?? swipeVelocity;
    this.stopWatchingInterruptedGlide();
    this.cancelGalleryScroll(true);
    cancelAnimationFrame(this.galleryRelease);
    this.galleryRelease = null;
    const target = this.gallerySnapPosition(position);
    const page = this.announceGalleryTarget(target);
    const start = this.scrollLeft;
    const distance = target - start;
    if (Math.abs(distance) < 0.5) {
      this.releaseGalleryScroll();
      this.scrollend();
      return;
    }

    // Both paused while it runs: snapping, and the gallery's CSS
    // scroll-behavior: smooth - some engines (Safari) still smooth each
    // per-frame step despite behavior: 'instant', so the image trailed the
    // glide and was still moving when snapping came back, which tugged it
    // back a little.
    this.style.scrollSnapType = 'none';
    this.style.scrollBehavior = 'auto';
    const duration = Math.min(900, 640 + Math.abs(distance) / Math.max(1, this.clientWidth) * 80);
    const stiffness = 8;
    const initial = Math.max(0, Math.min(stiffness, velocity * duration / distance));
    const curve = (progress) => 1 - (1 + (stiffness - initial) * progress) * Math.exp(-stiffness * progress);
    const settled = curve(1);
    // Clocked from the first frame (as if it began one frame earlier), so
    // a retarget never spends a frame standing still.
    let startTime;
    // A click, a tap or a pinch-zoom is not scrolling: those used to stop
    // the glide dead as well, leaving the image resting between two
    // slides. Only a mostly-horizontal wheel/trackpad scroll, a touch drag
    // or the slider's own scroll keys take over - and even then the
    // gallery still settles on a slide once they finish.
    const cancel = (event) => {
      if (event.type === 'touchmove' && this.gallerySwipe) return;
      if (event.type === 'wheel' && (event.ctrlKey || Math.abs(event.deltaX) <= Math.abs(event.deltaY))) return;
      if (event.type === 'keydown' && !['ArrowLeft', 'ArrowRight', 'PageUp', 'PageDown', 'Home', 'End'].includes(event.key)) return;
      this.cancelGalleryScroll();
      this.watchInterruptedGlide();
    };
    const step = (now) => {
      startTime ??= now - 1000 / 60;
      const elapsed = Math.max(0, now - startTime);
      const progress = Math.min(1, elapsed / duration);
      const left = start + distance * curve(progress) / settled;
      const glide = this.galleryScroll;
      if (elapsed > glide.elapsed) glide.velocity = (left - glide.left) / (elapsed - glide.elapsed);
      glide.left = left;
      glide.elapsed = elapsed;
      this.scrollLeft = left;
      if (progress < 1) {
        glide.frame = requestAnimationFrame(step);
        return;
      }
      // Landed: snapping comes back a frame later, once the final
      // position has been committed, so there is nothing left to re-snap.
      this.lastArrowNavigation = performance.now();
      this.cancelGalleryScroll(true);
      this.galleryRelease = requestAnimationFrame(() => this.releaseGalleryScroll());
      this.scrollend();
    };

    this.galleryScroll = { target, page, cancel, left: start, elapsed: 0, velocity, frame: requestAnimationFrame(step) };
    this.updateButtons();
    ['wheel', 'touchmove', 'keydown'].forEach((type) => this.addEventListener(type, cancel, { passive: true }));
  }

  // Tells the gallery (MediaGallery.showSlide) which slide a glide, jump,
  // arrow press or swipe is headed to as soon as it starts, so that slide's
  // image starts loading right away instead of when the glide happens to
  // scroll it near the view.
  announceGalleryTarget(position) {
    const items = this.itemsToShow;
    const offset = Math.abs(this.itemOffset);
    const page = offset > 0 ? Math.max(1, Math.min(items.length, Math.round(Math.abs(position) / offset) + 1)) : undefined;
    if (page) this.dispatchEvent(new CustomEvent('slider:target', { detail: { page, currentElement: items[page - 1] } }));
    return page;
  }

  // After a visitor takes over mid-glide, the gallery still ends on a
  // slide: once their own scrolling has been quiet for a moment (and no
  // finger is down), it glides to the nearest slide if it isn't on one.
  watchInterruptedGlide() {
    this.stopWatchingInterruptedGlide();
    const watch = { touching: false };
    const settle = () => {
      if (watch.touching || this.galleryScroll) return;
      this.stopWatchingInterruptedGlide();
      const position = this.gallerySnapPosition(this.scrollLeft);
      if (Math.abs(position - this.scrollLeft) < 1) return;
      this.hasPendingOnScroll = true;
      this.animateGalleryScroll(position);
    };
    watch.rearm = () => {
      clearTimeout(watch.timer);
      watch.timer = setTimeout(settle, 180);
    };
    watch.touchstart = () => { watch.touching = true; };
    watch.touchend = () => { watch.touching = false; watch.rearm(); };
    this.galleryWatch = watch;
    this.addEventListener('scroll', watch.rearm, { passive: true });
    this.addEventListener('touchstart', watch.touchstart, { passive: true });
    this.addEventListener('touchend', watch.touchend, { passive: true });
    this.addEventListener('touchcancel', watch.touchend, { passive: true });
    watch.rearm();
  }

  stopWatchingInterruptedGlide() {
    const watch = this.galleryWatch;
    if (!watch) return;
    this.galleryWatch = null;
    clearTimeout(watch.timer);
    this.removeEventListener('scroll', watch.rearm);
    this.removeEventListener('touchstart', watch.touchstart);
    this.removeEventListener('touchend', watch.touchend);
    this.removeEventListener('touchcancel', watch.touchend);
  }

  cancelGalleryScroll(keepSnapPaused = false) {
    const glide = this.galleryScroll;
    if (glide) {
      this.galleryScroll = null;
      cancelAnimationFrame(glide.frame);
      ['wheel', 'touchmove', 'keydown'].forEach((type) => this.removeEventListener(type, glide.cancel));
    }
    if (!keepSnapPaused) this.releaseGalleryScroll();
  }

  releaseGalleryScroll() {
    cancelAnimationFrame(this.galleryRelease);
    this.galleryRelease = null;
    this.style.scrollSnapType = '';
    this.style.scrollBehavior = '';
  }

  // The exact (sub-pixel) snap position of the slide nearest the requested
  // one. offsetLeft is whole pixels, but slides are usually a fractional
  // width, so landing on it left the real snap point a fraction away for
  // snapping to correct afterwards.
  gallerySnapPosition(position) {
    const maximum = Math.max(0, this.scrollWidth - this.clientWidth);
    const clamp = (value) => theme.config.rtl ? Math.max(-maximum, Math.min(0, value)) : Math.max(0, Math.min(maximum, value));
    const items = this.itemsToShow;
    const offset = Math.abs(this.itemOffset);
    // Clamped before picking the slide: a press past either end (say a
    // quick double "previous" from the second image, asking for -1) must
    // resolve to the end slide, not - via Math.abs - bounce back to the
    // image it came from.
    const clamped = clamp(position);
    if (!items.length || !(offset > 0)) return clamped;
    const item = items[Math.max(0, Math.min(items.length - 1, Math.round(Math.abs(clamped) / offset)))];
    const itemRect = item.getBoundingClientRect();
    const sliderRect = this.getBoundingClientRect();
    if (!(itemRect.width > 0)) return clamp(position);
    return clamp(this.scrollLeft + (theme.config.rtl ? itemRect.right - sliderRect.right : itemRect.left - sliderRect.left));
  }

  dispatchEventHandler() {
    this.dispatchEvent(
      new CustomEvent('slider:change', {
        detail: {
          currentPage: this.currentPage,
          currentElement: this.itemsToShow[this.currentPage - 1],
        },
      })
    );
  }
}
customElements.define('slider-element', SliderElement);

class SliderDots extends HTMLElement {
  constructor() {
    super();

    new theme.initWhenVisible(this.init.bind(this));
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }

  get items() {
    return this._items = this._items || Array.from(this.children);
  }

  get itemsToShow() {
    return Array.from(this.items).filter(element => element.clientWidth > 0);
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;

    if (this.controlledElement) {
      this.controlledElement.addEventListener('slider:change', this.onChange.bind(this));

      this.items.forEach((item) => {
        item.addEventListener('click', this.onButtonClick.bind(this));
      });
    }
  }

  reset() {
    this._items = Array.from(this.children);
  }

  onChange(event) {
    this.transitionTo(parseInt(event.detail.currentPage) - 1);

    this.itemsToShow.forEach((item) => {
      item.setAttribute('aria-current', parseInt(item.getAttribute('data-index')) === parseInt(event.detail.currentPage) ? 'true' : 'false');
    });
  }

  transitionTo(selectedIndex, immediate = false) {
    if (this.itemsToShow[selectedIndex]) {
      const scrollElement = this.hasAttribute('align-selected') ? this.closest(this.getAttribute('align-selected')) : this;
      scrollElement?.scrollTo({
        left: this.itemsToShow[selectedIndex].offsetLeft - scrollElement.clientWidth / 2 + this.itemsToShow[selectedIndex].clientWidth / 2,
        top: this.itemsToShow[selectedIndex].offsetTop - scrollElement.clientHeight / 2 - this.itemsToShow[selectedIndex].clientHeight / 2,
        behavior: immediate ? 'instant' : theme.config.motionReduced ? 'auto' : 'smooth'
      });
    }
  }

  onButtonClick(event) {
    event.preventDefault();
    const target = event.currentTarget;

    this.itemsToShow.forEach((item) => {
      item.setAttribute('aria-current', item.getAttribute('data-index') === target.getAttribute('data-index') ? 'true' : 'false');
    });

    this.controlledElement.select(parseInt(target.getAttribute('data-index')));
  }
}
customElements.define('slider-dots', SliderDots);

class ProgressBar extends HTMLElement {
  constructor() {
    super();

    if (this.hasAttribute('style')) return;

    Motion.inView(this, this.init.bind(this));
  }

  init() {
    this.style.setProperty('--progress', `${parseInt(this.getAttribute('data-value')) * 0.75 * 100 / parseInt(this.getAttribute('data-max'))}%`);
  }
}
customElements.define('progress-bar', ProgressBar);

const onYouTubePromise = new Promise((resolve) => {
  window.onYouTubeIframeAPIReady = () => resolve();
});
class DeferredMedia extends HTMLElement {
  constructor() {
    super();

    if (this.posterElement) {
      this.posterElement.addEventListener('click', this.onPosterClick.bind(this));
    }

    if (this.autoplay) {
      Motion.inView(this, this.init.bind(this));
    }
  }

  get posterElement() {
    return this.querySelector('[id^="DeferredPoster-"]');
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }

  get autoplay() {
    return this.hasAttribute('autoplay');
  }

  get playing() {
    return this.hasAttribute('playing');
  }

  get player() {
    return this.playerProxy = this.playerProxy || new Proxy(this.playerTarget(), {
      get: (target, prop) => {
        return async () => {
          target = await target;
          // A play() queued while a YouTube/Vimeo player was still loading, then a
          // pause() before it was ready (e.g. scrolled out of view), which found
          // nothing playing yet: the last request wins, so it is not started
          // off screen now. (A native video's target is ready at once.)
          if (prop === 'play' && this.wantsPlay === false) return;
          this.playerHandler(target, prop);
        };
      }
    });
  }

  static get observedAttributes() {
    return ['playing'];
  }

  onPosterClick(event) {
    event.preventDefault();
    event.stopPropagation();
    this.togglePlayback();
  }

  togglePlayback() {
    if (this.playing) {
      this.paused = true;
      this.pause();
    }
    else {
      this.paused = false;
      this.play();
    }
  }

  init() {
    this.outOfView = false;
    if (!this.paused) {
      this.play();
    }
    return () => {
      this.outOfView = true;
      this.pause();
    };
  }

  play() {
    this.wantsPlay = true;
    if (!this.playing) {
      this.player.play();
    }
  }

  pause() {
    this.wantsPlay = false;
    if (this.playing) {
      this.player.pause();
    }
  }

  // The YouTube/Vimeo embeds start by themselves once loaded (autoplay=1): if
  // the visitor scrolled away before that (init()'s cleanup paused while nothing
  // played yet), pause again instead of playing off screen. init() plays it when
  // it is back in view.
  pausedOffscreen() {
    return this.outOfView === true && this.wantsPlay === false;
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'playing') {
      if (oldValue === null && newValue === '') {
        return this.dispatchEvent(new CustomEvent('video:play', { bubbles: true }));
      }
      
      if (newValue === null) {
        return this.dispatchEvent(new CustomEvent('video:pause', { bubbles: true }));
      }
    }
  }
}

// A video file marked data-tap-toggle (the product gallery's videos, the
// product pages' films) plays without the browser's controls: a tap on it
// pauses or plays it, and its play button comes back while it is paused,
// whether the visitor paused it or the browser wouldn't start it (iPhone's
// Low Power Mode blocks autoplay) - no controls appear for that either.
class VideoMedia extends DeferredMedia {
  constructor() {
    super();

    if (this.tapToggle) {
      this.playLabel = this.posterElement?.getAttribute('aria-label');
      this.addEventListener('click', (event) => {
        if (this.posterElement?.contains(event.target)) return;
        // the end of a swipe through the gallery isn't a tap
        if (this.closest('slider-element')?.navigating) return;
        this.togglePlayback();
      });
    }
  }

  get tapToggle() {
    return this.hasAttribute('data-tap-toggle');
  }

  togglePlayback() {
    super.togglePlayback();
    if (this.tapToggle) this.toggleAttribute('data-user-paused', this.paused);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    // its play button pauses it while it plays: say so
    if (name === 'playing' && this.tapToggle && this.posterElement?.dataset.pauseLabel) {
      this.posterElement.setAttribute('aria-label', newValue === null ? this.playLabel : this.posterElement.dataset.pauseLabel);
    }
    return super.attributeChangedCallback(name, oldValue, newValue);
  }

  playerTarget() {
    if (this.hasAttribute('host')) {
      this.setAttribute('loaded', '');
      this.closest('.media')?.classList.remove('loading');

      return new Promise(async (resolve) => {
        const templateElement = this.querySelector('template');
        if (templateElement) {
          templateElement.replaceWith(templateElement.content.firstElementChild.cloneNode(true));
        }
        const muteVideo = this.hasAttribute('autoplay') || window.matchMedia('screen and (max-width: 1023px)').matches;
        const script = document.createElement('script');
        script.type = 'text/javascript';
        if (this.getAttribute('host') === 'youtube') {
          if (!window.YT || !window.YT.Player) {
            script.src = 'https://www.youtube.com/iframe_api';
            document.head.appendChild(script);
            await new Promise((resolve2) => {
              script.onload = resolve2;
            });
          }
          await onYouTubePromise;
          const player = new YT.Player(this.querySelector('iframe'), {
            events: {
              onReady: () => {
                if (muteVideo) {
                  player.mute();
                }
                resolve(player);
              },
              onStateChange: (event) => {
                if (event.data === YT.PlayerState.PLAYING) {
                  if (this.pausedOffscreen()) {
                    player.pauseVideo();
                    return;
                  }
                  this.setAttribute('playing', '');
                }
                else if (event.data === YT.PlayerState.ENDED || event.data === YT.PlayerState.PAUSED) {
                  this.removeAttribute('playing');
                }
              }
            }
          });
        }
        if (this.getAttribute('host') === 'vimeo') {
          if (!window.Vimeo || !window.Vimeo.Player) {
            script.src = 'https://player.vimeo.com/api/player.js';
            document.head.appendChild(script);
            await new Promise((resolve2) => {
              script.onload = resolve2;
            });
          }
          const player = new Vimeo.Player(this.querySelector('iframe'));
          if (muteVideo) {
            player.setMuted(true);
          }
          player.on('play', () => {
            if (this.pausedOffscreen()) {
              player.pause();
              return;
            }
            this.setAttribute('playing', '');
          });
          player.on('pause', () => this.removeAttribute('playing'));
          player.on('ended', () => this.removeAttribute('playing'));
          resolve(player);
        }
      });
    }
    else {
      let video;
      const templateElement = this.querySelector('template');

      if (templateElement) {
        video = templateElement.content.firstElementChild.cloneNode(true);
        video.addEventListener('loadedmetadata', () => this.setLoadedState(), { once: true });
        templateElement.replaceWith(video);
      } else {
        video = this.querySelector('video');
      }

      if (video && !templateElement) {
        if (video.readyState >= 1) {
          this.setLoadedState();
        } else {
          video.addEventListener('loadedmetadata', () => this.setLoadedState(), { once: true });
        }
      }

      const player = video;
      if (!player) return;
    
      player.addEventListener('play', () => {
        this.setAttribute('playing', '');
        this.removeAttribute('suspended');
        if (this.tapToggle) {
          this.paused = false;
          this.removeAttribute('data-user-paused');
        }
      });
      
      player.addEventListener('pause', () => {
        if (!player.seeking && player.paused) {
          this.removeAttribute('playing');
        }
      });

      player.addEventListener('canplay', () => {
        if (this.hasAttribute('autoplay')) {
          requestAnimationFrame(() => {
            // Scrolled out of view (and paused there by init()'s cleanup) before the first
            // data arrived: stay paused, as it would be had the data arrived first; init()
            // resumes it when it is back in view. Otherwise it would loop off-screen.
            if (this.outOfView) return;
            player.play().catch((error) => {
              if (error.name === 'NotAllowedError') {
                this.setAttribute('suspended', '');
                if (!this.tapToggle) player.controls = true;
                const replacementImageSrc = player.previousElementSibling?.currentSrc;
                if (replacementImageSrc) {
                  player.poster = replacementImageSrc;
                }
              }
            });
          });
        }
      }, { once: true });

      return player;
    }
  }

  playerHandler(target, prop) {
    if (this.getAttribute('host') === 'youtube') {
      if (target && target.playVideo) {
        prop === 'play' ? target.playVideo() : target.pauseVideo();
      }
    }
    else {
      if (prop === 'play' && !this.hasAttribute('host')) {
        target.play().catch((error) => {
          if (error.name === 'NotAllowedError') {
            this.setAttribute('suspended', '');
            if (!this.tapToggle) target.controls = true;
            const replacementImageSrc = target.previousElementSibling?.currentSrc;
            if (replacementImageSrc) {
              target.poster = replacementImageSrc;
            }
          }
        });
      }
      else {
        target[prop]();
      }
    }
  }

  setLoadedState() {
    this.setAttribute('loaded', '');
    this.closest('.media')?.classList.remove('loading');
  }
}
customElements.define('video-media', VideoMedia);

class ModelMedia extends DeferredMedia {
  constructor() {
    super();

    this.player;

    if (this.closeElement) {
      this.closeElement.addEventListener('click', this.onCloseClick.bind(this));
    }
  }

  get closeElement() {
    return this.querySelector('[id^="DeferredPosterClose-"]');
  }

  onCloseClick(event) {
    event.preventDefault();
    event.stopPropagation();

    if (this.modelViewerUI) {
      this.modelViewerUI.pause();
    }
  }

  playerTarget() {
    return new Promise(() => {
      this.setAttribute('loaded', '');

      Shopify.loadFeatures([
        {
          name: 'shopify-xr',
          version: '1.0',
          onLoad: this.setupShopifyXR.bind(this),
        },
        {
          name: 'model-viewer-ui',
          version: '1.0',
          onLoad: this.setupModelViewerUI.bind(this),
        },
      ]);
    });
  }

  playerHandler(target, prop) {
    target[prop]();
  }

  async setupShopifyXR() {
    if (!window.ShopifyXR) {
      document.addEventListener('shopify_xr_initialized', this.setupShopifyXR.bind(this));
      return;
    }

    document.querySelectorAll('[id^="ProductJSON-"]').forEach((modelJSON) => {
      window.ShopifyXR.addModels(JSON.parse(modelJSON.textContent));
      modelJSON.remove();
    });
    window.ShopifyXR.setupXRElements();
  }

  setupModelViewerUI(errors) {
    if (errors) return;

    const modelViewer = this.querySelector('model-viewer');
    if (modelViewer && !modelViewer.hasAttribute('loaded')) {
      modelViewer.setAttribute('loaded', '');
      modelViewer.addEventListener('shopify_model_viewer_ui_toggle_play', this.modelViewerPlayed.bind(this));
      modelViewer.addEventListener('shopify_model_viewer_ui_toggle_pause', this.modelViewerPaused.bind(this));

      this.modelViewerUI = new Shopify.ModelViewerUI(modelViewer);
    }
  }

  modelViewerPlayed() {
    this.setAttribute('playing', '');
    this.closeElement.removeAttribute('hidden');
    (this.controlledElement ?? this).dispatchEvent(new CustomEvent('modelViewer:play', { bubbles: true }));
  }

  modelViewerPaused() {
    this.removeAttribute('playing');
    this.closeElement.setAttribute('hidden', '');
    (this.controlledElement ?? this).dispatchEvent(new CustomEvent('modelViewer:pause', { bubbles: true }));
  }
}
customElements.define('model-media', ModelMedia);

class VariantPicker extends HTMLElement {
  constructor() {
    super();
    this.currentVariant = null;
    this.cachedVariantData = null;
    this.currentOptions = [];
    this.handleVariantChange = this.handleVariantChange.bind(this);
  }

  get selectedOptionValues() {
    return Array.from(
      this.querySelectorAll('select option[selected], fieldset input:checked')
    ).map((el) => el.getAttribute('data-option-value-id'));
  }

  connectedCallback() {
    this.addEventListener('change', this.handleVariantChange);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.handleVariantChange);
  }

  handleVariantChange(event) {
    const target = event.target;

    if (this.hasAttribute('data-dynamic-variants') && this.hasVariantData()) {
      this.syncCurrentOptions(target);
      this.findAndSetCurrentVariant(target);
      this.updateAvailableOptions(target);
    }

    this.markSelectedOption(target);
    this.publishSelectionChange(event, this.getActualInput(target));
  }

  markSelectedOption(target) {
    if (target.tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options).find((opt) => opt.hasAttribute('selected'))?.removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', '');
    }
  }

  getActualInput(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  hasVariantData() {
    return this.querySelector('[data-variants]') !== null;
  }

  getVariantData() {
    if (this.cachedVariantData) return this.cachedVariantData;
    
    const data = this.querySelector('[data-variants]')?.textContent;
    return this.cachedVariantData = data ? JSON.parse(data) : [];
  }

  findAndSetCurrentVariant(target) {
    this.currentVariant = this.findVariantByOptions(true) || 
                          this.findClosestAvailableVariant(target) || 
                          this.findVariantByOptions(false) || 
                          null;
  }

  findVariantByOptions(requireAvailability) {
    return this.getVariantData().find((variant) => {
      const matches = this.currentOptions.every((val, i) => variant[`option${i + 1}`] === val);
      return requireAvailability ? matches && variant.available : matches;
    });
  }

  findClosestAvailableVariant(lastSelectedOption) {
    if (!lastSelectedOption) return null;

    const lastIndex = parseInt(lastSelectedOption.getAttribute('data-option-index'));
    const matches = this.getVariantData().filter((variant) => 
      variant.available && 
      this.currentOptions.slice(0, lastIndex).every((val, i) => variant[`option${i + 1}`] === val)
    );

    return matches.reduce((best, variant) => {
      if (!best) return variant;
      const bestScore = this.currentOptions.reduce((sum, val, i) => 
        best[`option${i + 1}`] === val ? sum + (3 - i) : sum, 0);
      const currentScore = this.currentOptions.reduce((sum, val, i) => 
        variant[`option${i + 1}`] === val ? sum + (3 - i) : sum, 0);
      return currentScore > bestScore ? variant : best;
    }, null);
  }

  syncCurrentOptions(target) {
    this.currentOptions = Array.from(this.querySelectorAll('select, fieldset')).map((el) => {
      if (el.tagName === 'SELECT') return el.value;
      return Array.from(el.querySelectorAll('input')).find((radio) => 
        radio === target || radio.checked
      )?.value;
    });
  }

  updateAvailableOptions(lastSelectedOption) {
    const firstValue = this.querySelector(':checked')?.value;
    if (!firstValue) return;

    const matchingVariants = this.getVariantData().filter((v) => v.option1 === firstValue);
    const groups = [...this.querySelectorAll('fieldset, .variant-wrapper')];

    groups.forEach((group, i) => {
      if (i === 0 || lastSelectedOption.parentElement === group) return;

      const inputs = [...group.querySelectorAll('input[type="radio"], option')];
      const prevValue = groups[i - 1].querySelector(':checked')?.value;

      const available = matchingVariants
        .filter((v) => v.available && v[`option${i}`] === prevValue)
        .map((v) => v[`option${i + 1}`]);

      this.setInputStates(inputs, available);
    });
  }

  setInputStates(inputs, availableValues) {
    inputs.forEach((input) => {
      const index = input.getAttribute('data-option-index');
      const value = input.getAttribute('value');
      const isAvailable = availableValues.includes(value);
      const isCurrent = this.currentVariant?.[`option${index}`] === value;

      if (input.tagName === 'INPUT') {
        input.classList.toggle('disabled', !isAvailable);
        if (isCurrent) input.checked = true;
      } else {
        isCurrent ? input.setAttribute('selected', '') : input.removeAttribute('selected');
      }
    });
  }

  publishSelectionChange(event, target) {
    theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange, {
      data: { event, target, selectedOptionValues: this.selectedOptionValues }
    });
  }
}
customElements.define('variant-picker', VariantPicker);

class ProductInfo extends HTMLElement {
  onVariantChangeUnsubscriber = undefined;
  cartUpdateUnsubscriber = undefined;
  abortController = undefined;
  pendingRequestUrl = null;
  preProcessHtmlCallbacks = [];
  postProcessHtmlCallbacks = [];
  
  constructor() {
    super();
  }

  get sectionId() {
    return this.hasAttribute('data-original-section-id') ? this.getAttribute('data-original-section-id') : this.getAttribute('data-section-id');
  }

  get productId() {
    return this.getAttribute('data-product-id');
  }

  get productForm() {
    return document.forms[this.getAttribute('form')];
  }

  get productStickyForm() {
    return document.getElementById(`ProductStickyForm-${this.sectionId}-${this.productId}`);
  }

  get pickupAvailability() {
    return this.querySelector('pickup-availability');
  }

  get variantSelectors() {
    return this.querySelector('variant-picker');
  }

  get quantityInput() {
    return this.querySelector('quantity-input input');
  }

  connectedCallback() {
    this.initProductAnimation();

    this.onVariantChangeUnsubscriber = theme.pubsub.subscribe(
      theme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
      this.handleOptionValueChange.bind(this)
    );

    this.initQuantityHandlers();
    this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
  }

  disconnectedCallback() {
    this.onVariantChangeUnsubscriber();
    this.cartUpdateUnsubscriber?.();
    this.quantityRulesAbortController?.abort();
  }

  initProductAnimation() {
    if (theme.config.motionReduced) return;

    if (this.hasAttribute('data-stagger-info')) {
      this.initStaggeredProductAnimation();
      return;
    }

    this.animation = new theme.Animation(this);
    this.animation.beforeLoad();

    Motion.inView(this, async () => {
      if (!this.immediate && this.media) await theme.utils.imageLoaded(this.media);
      this.animation.load();
    });
  }

  // Reveals each of this element's own direct children (badge, title,
  // price, description, buttons, rating - whichever blocks a merchant has
  // configured) as its own staggered fade-up, instead of the single
  // fade-up theme.Animation applies to the whole container above. Opt-in
  // via data-stagger-info so the same product-info markup used by the
  // quick-view modal and the featured-product section (neither asked for
  // this) keeps its existing single-block animation untouched.
  initStaggeredProductAnimation() {
    const items = Array.from(this.children);
    if (!items.length) return;

    const compact = theme.config.mqlSmall;
    const distance = compact ? 14 : 18;
    const duration = compact ? 0.5 : 0.6;
    const staggerDelay = compact ? 0.05 : 0.07;
    const easing = [0.16, 1, 0.3, 1];

    Motion.animate(items, { opacity: 0, transform: `translateY(${distance}px)` }, { duration: 0 });

    // No cleanup pass removing the inline transform once this settles (the
    // sitewide theme.Animation fade-up doesn't do that either) - forcing
    // that extra style recalc right as the animation ends is what made the
    // rating stars' background-clip: text gradient flash to solid black for
    // a frame before repainting, a known WebKit quirk with that technique
    // under a transform. translateY(0) is visually identical to no
    // transform, so leaving it in place avoids the extra repaint entirely.
    //
    // Motion's inView() keeps observing and can re-fire onStart on a later
    // entry (it only stops on its own when onStart returns a leave-handler,
    // which this doesn't need) - every other Motion.inView(this, this.init...)
    // call sitewide guards against exactly this by having its own init()
    // bail out if it already ran, which this lacked. A layout shift shortly
    // after load (a web font swap, the gallery image finishing decode) can
    // nudge product-info across the intersection threshold and re-trigger
    // it, replaying the whole stagger - rating included - a second time.
    let revealed = false;
    Motion.inView(this, () => {
      if (revealed) return;
      revealed = true;
      // Marks this element once the reveal has genuinely finished writing
      // its final opacity/transform as each item's resting inline style -
      // see adobe-product-rating-scroll-v1.css's use of product-info.
      // stagger-revealed, which needs this to not exist until that point.
      Motion.animate(
        items,
        { opacity: [0, 1], transform: [`translateY(${distance}px)`, 'translateY(0)'] },
        { duration, easing, delay: Motion.stagger(staggerDelay) }
      ).finished.then(() => this.classList.add('stagger-revealed'));
    });
  }

  handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
    if (!this.contains(event.target)) return;

    this.resetProductFormState();

    const productUrl = target.getAttribute('data-product-url') || this.pendingRequestUrl || this.getAttribute('data-product-url');
    this.pendingRequestUrl = productUrl;
    const shouldSwapProduct = this.getAttribute('data-product-url') !== productUrl;

    if (shouldSwapProduct) {
      window.location.href = productUrl;
      return;
    }

    this.renderProductInfo({
      requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues),
      targetId: target.tagName === 'OPTION' ? target.parentElement.id : target.id,
      callback: this.handleUpdateProductInfo()
    });
  }

  resetProductFormState() {
    this.productForm?.handleErrorMessage();
    this.productForm?.toggleSubmitButton(true, '');
    this.productStickyForm?.handleErrorMessage();
    this.productStickyForm?.toggleSubmitButton(true, '');
  }

  renderProductInfo({ requestUrl, targetId, callback }) {
    this.abortController?.abort();
    this.abortController = new AbortController();

    fetch(requestUrl, { signal: this.abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.pendingRequestUrl = null;
        if (callback !== undefined) {
          const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
          callback(parsedHTML);
        }
      })
      .then(() => {
        // set focus to last clicked option value
        const activeElement = document.getElementById(targetId);
        if (activeElement === null) return;

        if (activeElement.hasAttribute('align-selected')) {
          const scrollElement = activeElement.closest(activeElement.getAttribute('align-selected'));
          scrollElement?.scrollTo({
            left: activeElement.offsetLeft,
            behavior: 'instant'
          });
        }
        setTimeout(() => {
          activeElement.focus({ preventScroll: true });
        }, 100);
      })
      .catch((error) => {
        if (error.name !== 'AbortError') console.error(error);
      });
  }

  buildRequestUrlWithParams(url, optionValues) {
    const params = [];
    params.push(`section_id=${this.sectionId}`);

    if (optionValues.length) {
      params.push(`option_values=${optionValues.join(',')}`);
    }

    return `${url}?${params.join('&')}`;
  }

  handleUpdateProductInfo() {
    return (parsedHTML) => {
      const variant = this.getSelectedVariant(parsedHTML);
      
      this.pickupAvailability?.update(variant);
      this.updateOptionValues(parsedHTML);
      this.updateURL(variant?.id);
      this.updateVariantInputs(variant?.id);
      
      if (!variant) {
        this.setUnavailable();
        return;
      }

      this.updateSourceFromDestination(parsedHTML, 'ProductGallery');
      this.rollPrice(parsedHTML, 'Price');
      this.updateSourceFromDestination(parsedHTML, 'BuyButtonPrice');
      this.rollPrice(parsedHTML, 'StickyPrice');
      this.updateSourceFromDestination(parsedHTML, 'Sku');
      this.updateSourceFromDestination(parsedHTML, 'Badges');
      this.updateSourceFromDestination(parsedHTML, 'Inventory');
      this.updateSourceFromDestination(parsedHTML, 'Volume');
      this.updateSourceFromDestination(parsedHTML, 'PricePerItem');
      this.updateSourceFromDestination(parsedHTML, 'BackInStock');
      this.updateSourceFromDestination(parsedHTML, 'ProductBundle');

      this.updateQuantityRules(parsedHTML);
      this.updateSourceFromDestination(parsedHTML, 'QuantityRules');
      this.updateSourceFromDestination(parsedHTML, 'QuantityRulesCart');
      this.updateSourceFromDestination(parsedHTML, 'VolumeNote');

      this.productForm?.toggleSubmitButton(!variant.available, theme.variantStrings.soldOut);
      this.productStickyForm?.toggleSubmitButton(!variant.available, theme.variantStrings.soldOut);

      theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.variantChange, {
        data: {
          sectionId: this.sectionId,
          productId: this.productId,
          parsedHTML: parsedHTML,
          variant: variant
        }
      });

      (this.productForm ?? document).dispatchEvent(new CustomEvent('variant:change', {
        detail: {
          variant: variant
        }
      }));
    };
  }

  getSelectedVariant(productInfoNode) {
    const selectedVariant = productInfoNode.querySelector('[data-selected-variant]')?.textContent;
    return !!selectedVariant ? JSON.parse(selectedVariant) : null;
  }

  updateOptionValues(parsedHTML) {
    const variantPicker = parsedHTML.getElementById(`VariantPicker-${this.sectionId}-${this.productId}`);
    if (variantPicker) {
      theme.HTMLUpdateUtility.viewTransition(this.variantSelectors, variantPicker, this.preProcessHtmlCallbacks);
    }
  }

  updateURL(variantId) {
    if (!variantId || this.getAttribute('data-update-url') === 'false') return;

    const newUrl = new URL(window.location.href);
    newUrl.searchParams.set('variant', variantId);
    window.history.replaceState({ path: newUrl.toString() }, '', newUrl.toString());
  }

  updateVariantInputs(variantId) {
    if (!variantId) return;
    
    const productForms = document.querySelectorAll(`#ProductForm-${this.sectionId}-${this.productId}, #ProductFormInstallment-${this.sectionId}-${this.productId}`);
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = variantId ?? '';
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  updateSourceFromDestination(parsedHTML, id) {
    const source = parsedHTML.getElementById(`${id}-${this.sectionId}-${this.productId}`);
    const destination = document.querySelector(`#${id}-${this.sectionId}-${this.productId}`);
    if (source && destination) {
      // Versions that share their pictures (Flow's Standard and Pro) would
      // redraw the same gallery and load every picture again: keep it.
      if (id === 'ProductGallery' && this.sameMedia(source, destination)) return;
      destination.innerHTML = source.innerHTML;
      destination.removeAttribute('hidden');
    }
  }

  // The price rolls to its new value digit by digit, with the same spring
  // as the cart's total (cart.js, updateSubtotal). Anything else about the
  // price changing (a sale price showing up, another currency layout) is a
  // plain swap.
  rollPrice(parsedHTML, id) {
    const source = parsedHTML.getElementById(`${id}-${this.sectionId}-${this.productId}`);
    const destination = document.querySelector(`#${id}-${this.sectionId}-${this.productId}`);
    if (!source || !destination) return;
    const digits = (node) => Array.from(node.textContent).filter((character) => /\p{Nd}/u.test(character));
    const mask = (node) => node.textContent.replace(/\p{Nd}/gu, '#').replace(/\s+/g, ' ');
    // Mid-roll the old and new digits are both in the text, so read what
    // the last roll left rather than the text, while that is still there.
    const last = destination.priceSnapshot?.child === destination.firstElementChild ? destination.priceSnapshot : null;
    const before = last ? last.digits : digits(destination);
    const after = digits(source);
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    destination.priceRolls?.forEach((animation) => animation.cancel());
    destination.priceRolls = null;
    destination.priceSnapshot = null;
    if (reducedMotion || typeof destination.animate !== 'function' || (last ? last.mask : mask(destination)) !== mask(source) || before.join('') === after.join('')) {
      this.updateSourceFromDestination(parsedHTML, id);
      return;
    }

    const content = source.cloneNode(true);
    const walker = document.createTreeWalker(content, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    const cells = [];
    for (const node of nodes) {
      if (!/\p{Nd}/u.test(node.textContent)) continue;
      const fragment = document.createDocumentFragment();
      for (const character of node.textContent) {
        if (!/\p{Nd}/u.test(character)) {
          fragment.append(character);
          continue;
        }
        const cell = document.createElement('span');
        cell.style.cssText = 'display:inline-block;position:relative';
        const glyph = document.createElement('span');
        glyph.textContent = character;
        cell.append(glyph);
        fragment.append(cell);
        cells.push({ cell, glyph, value: character, old: before[cells.length] });
      }
      node.replaceWith(fragment);
    }
    destination.replaceChildren(...content.childNodes);
    destination.removeAttribute('hidden');

    const direction = Number(after.join('')) > Number(before.join('')) ? 1 : -1;
    const spring = (from, target, time) => {
      const frequency = 2 * Math.PI / 0.45;
      const damping = 0.67 * frequency;
      const oscillation = frequency * Math.sqrt(1 - 0.67 ** 2);
      const displacement = from - target;
      const sineScale = damping * displacement / oscillation;
      return target + Math.exp(-damping * time) * (displacement * Math.cos(oscillation * time) + sineScale * Math.sin(oscillation * time));
    };
    const frames = (from, target, opacityFrom, opacityTo) => Array.from({ length: 121 }, (_, index) => ({
      transform: `translateY(${index === 120 ? target : spring(from, target, index / 120)}%)`,
      opacity: opacityFrom + (opacityTo - opacityFrom) * (1 - (1 - Math.min(index / 120 / 0.18, 1)) ** 3)
    }));
    const changed = cells.filter((entry) => entry.old !== entry.value).reverse();
    const stagger = Math.min(35, 180 / Math.max(changed.length - 1, 1));
    const rolls = [];
    changed.forEach(({ cell, glyph, value, old }, index) => {
      const timing = { duration: 1000, delay: index * stagger, easing: 'linear', fill: 'both' };
      const roll = document.createElement('span');
      roll.setAttribute('aria-hidden', 'true');
      roll.style.cssText = 'position:absolute;inset:0;clip-path:inset(0 -0.5em);pointer-events:none';
      const outgoing = document.createElement('span');
      const incoming = document.createElement('span');
      outgoing.textContent = old || '';
      incoming.textContent = value;
      outgoing.style.cssText = incoming.style.cssText = 'position:absolute;inset:0;text-align:center';
      roll.append(outgoing, incoming);
      cell.append(roll);
      glyph.style.opacity = '0';
      const animations = [
        outgoing.animate(frames(0, -direction * 100, 1, 0), timing),
        incoming.animate(frames(direction * 100, 0, 0, 1), timing)
      ];
      animations[1].onfinish = () => {
        roll.remove();
        glyph.style.opacity = '';
      };
      rolls.push(...animations);
    });
    destination.priceRolls = rolls;
    destination.priceSnapshot = { child: destination.firstElementChild, mask: mask(source), digits: after };
  }

  sameMedia(source, destination) {
    const ids = (root) => Array.from(root.querySelectorAll('[data-media-id]'), (media) => media.getAttribute('data-media-id')).join(',');
    const before = ids(destination);
    return before !== '' && before === ids(source);
  }

  setUnavailable() {
    this.productForm?.toggleSubmitButton(true, theme.variantStrings.unavailable, true);
    this.productStickyForm?.toggleSubmitButton(true, theme.variantStrings.unavailable, true);

    const selectors = ['Price', 'BuyButtonPrice', 'StickyPrice', 'Inventory', 'Sku', 'Badges', 'PricePerItem', 'BackInStock', 'ProductBundle', 'VolumeNote', 'Volume', 'QuantityRules', 'QuantityRulesCart']
      .map((id) => `#${id}-${this.sectionId}-${this.productId}`)
      .join(', ');
    document.querySelectorAll(selectors).forEach((selector) => selector.setAttribute('hidden', ''));
  }

  initQuantityHandlers() {
    if (!this.quantityInput) return;

    this.setQuantityBoundries();
    if (!this.hasAttribute('data-original-section-id')) {
      this.cartUpdateUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
    }
  }

  setQuantityBoundries() {
    theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.quantityBoundries, {
      data: {
        sectionId: this.sectionId,
        productId: this.productId
      }
    });
  }

  fetchQuantityRules() {
    const currentVariantId = this.productForm?.variantIdInput?.value;
    if (!currentVariantId) return;

    this.quantityRulesAbortController?.abort();
    const controller = new AbortController();
    this.quantityRulesAbortController = controller;
    this.querySelector('label[is="quantity-label"]')?.setAttribute('aria-busy', 'true');
    return fetch(`${this.getAttribute('data-product-url')}?variant=${currentVariantId}&section_id=${this.sectionId}`, { signal: controller.signal })
      .then((response) => response.text())
      .then((responseText) => {
        if (controller.signal.aborted || this.productForm?.variantIdInput?.value !== currentVariantId) return;
        const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
        this.updateQuantityRules(parsedHTML);
      })
      .catch((error) => {
        if (error.name !== 'AbortError') console.error(error);
      })
      .finally(() => {
        if (this.quantityRulesAbortController === controller) {
          this.querySelector('label[is="quantity-label"]')?.removeAttribute('aria-busy');
        }
      });
  }

  updateQuantityRules(parsedHTML) {
    if (!this.quantityInput) return;
    
    theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.quantityRules, {
      data: {
        sectionId: this.sectionId,
        productId: this.productId,
        parsedHTML
      }
    });

    this.setQuantityBoundries();
  }
}
customElements.define('product-info', ProductInfo);

class ProductForm extends HTMLFormElement {
  // Non-null (an array, possibly empty) while a simple add-to-cart request
  // is in flight - any further simple adds queue into it instead of firing
  // their own request. Shared across every <product-form> instance (a
  // collection grid renders one per card) since they all still serialize
  // through the same global cart mutation queue anyway.
  static pendingAddQueue = null;

  constructor() {
    super();

    this.variantIdInput.disabled = false;
    this.addEventListener('submit', this.onSubmitHandler);
  }

  get cartDrawer() {
    return document.querySelector('cart-drawer');
  }

  get submitButton() {
    return this._submitButton = this._submitButton || this.querySelector('[type="submit"]');
  }

  get submitButtons() {
    return this._submitButtons = this._submitButtons || document.querySelectorAll(`[type="submit"][form="${this.getAttribute('id')}"]`);
  }

  get hideErrors() {
    return this.getAttribute('data-hide-errors') === 'true';
  }

  get variantIdInput() {
    return this.querySelector('[name="id"]');
  }

  get bundles() {
    return Array.from(document.querySelectorAll(`[form="${this.getAttribute('id')}"] input[name="bundles"]:checked`));
  }

  prepareFormData(formData) {
    const bundlesLength = this.bundles.length;
    const itemsArray = new Array(bundlesLength + 1);
    
    for (let i = 0; i < bundlesLength; i++) {
      const reverseIndex = bundlesLength - 1 - i;
      itemsArray[i] = {
        id: this.bundles[reverseIndex].value,
        quantity: 1
      };
    }
    
    const allFormData = { items: itemsArray.slice(0, bundlesLength) };
    
    const json = {};
    const formEntries = Array.from(formData.entries());
    
    for (const [name, value] of formEntries) {
      if (name === 'id' || name === 'quantity' || name.includes('properties')) {
        if (name === 'id' || name === 'quantity') {
          json[name] = value;
        } else {
          const regex = /(?:^(properties\[))(.*?)(?:\])/;
          const property = regex.exec(name)[2];
          json.properties = json.properties || {};
          json.properties[property] = value;
        }
      } else {
        allFormData[name] = value;
      }
    }
    
    allFormData.items.push(json);
    return allFormData;
  }

  getCartFromBundledSections(sections) {
    return theme.utils.getCartFromBundledSections(sections);
  }

  getCartState(parsedState) {
    return theme.utils.getCartState(parsedState);
  }

  // A collection grid's quick-add buttons are independent, single-variant,
  // no-properties adds - safe to combine into one /cart/add.js `items` call
  // when several are clicked in a burst (see the batching below). A form
  // carrying variant options, line-item properties, or a bundle selection
  // keeps going through the plain single-request path unconditionally, so
  // this only ever changes behavior for the simple/rapid-click case.
  isSimpleAdd(formData) {
    if (this.bundles.length > 0) return false;
    if (!formData.get('id')) return false;
    for (const name of formData.keys()) {
      if (name !== 'id' && name !== 'quantity') return false;
    }
    return true;
  }

  onSubmitHandler(event) {
    const hasBundles = this.bundles.length > 0;

    if (!Shopify.designMode) {
      if (document.body.classList.contains('template-cart') || theme.settings.cartType === 'page') {
        if (hasBundles) {
          theme.utils.postLink2(theme.routes.cart_add_url, {
            parameters: {
              ...this.prepareFormData(new FormData(this))
            }
          });
          event.preventDefault();
        }
        return;
      }
    }

    event.preventDefault();
    if (this.submitButton.hasAttribute('aria-disabled')) {
      // hover-button marks itself aria-busy on every submit; a sold-out
      // button (this.error) has no request in flight to clear that again,
      // so its spinner would otherwise keep turning next to "Sold out".
      if (this.error) setTimeout(() => this.submitButton.removeAttribute('aria-busy'));
      return;
    }
    this.activeElement = event.submitter || event.currentTarget;

    this.handleErrorMessage();

    const formData = new FormData(this);
    const isSimple = !hasBundles && this.isSimpleAdd(formData);

    theme.cartFeedback?.pending(this.submitButton);
    this.submitButton.setAttribute('aria-disabled', 'true');
    this.submitButton.setAttribute('aria-busy', 'true');

    // Only a simple add can be folded into someone else's batch, and only
    // while one is actually in flight (a non-null queue means "a request
    // owns the batch window right now, add yours to it"); otherwise this
    // click is the one that opens (or doesn't need) that window.
    if (isSimple && ProductForm.pendingAddQueue) {
      ProductForm.pendingAddQueue.push({ owner: this, formData, activeElement: this.activeElement });
      return;
    }
    if (isSimple) ProductForm.pendingAddQueue = [];

    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }));

    const config = theme.utils.fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    formData.append('sections', [...new Set(sectionsToBundle)]);
    formData.append('sections_url', window.location.pathname);

    config.body = formData;

    if (hasBundles) {
      const allFormData = this.prepareFormData(formData);
      config.body = JSON.stringify(allFormData);
      config.headers['Content-Type'] = 'application/json';
    }

    theme.utils.queueCartMutation(() => fetch(theme.routes.cart_add_url, config).then((response) => response.json())
      .then(async (parsedState) => {
        if (parsedState.status) {
          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartError, {
            source: 'product-form',
            productVariantId: formData.get('id'),
            errors: parsedState.errors || parsedState.description,
            message: parsedState.message
          });
          theme.cartFeedback?.settle(this.submitButton);
          this.handleErrorMessage(parsedState.description);
          document.dispatchEvent(new CustomEvent('ajaxProduct:error', {
            detail: {
              errorMessage: parsedState.description
            }
          }));
          
          const submitButtonText = this.submitButton.querySelector('.btn-text>span');
          if (submitButtonText && submitButtonText.hasAttribute('data-sold-out')) {
            submitButtonText.innerText = submitButtonText.getAttribute('data-sold-out');
            this.submitButton.setAttribute('aria-disabled', 'true');
            this.error = true;
          }

          const cartJson = await this.getCartState(parsedState);
          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: formData.get('id'), cart: cartJson });

          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: false } }));
          return;
        }

        if (Shopify.designMode) {
          if (document.body.classList.contains('template-cart') || theme.settings.cartType === 'page') {
            window.location.href = theme.routes.cart_url;
            return;
          }
        }

        const cartJson = await this.getCartState(parsedState);
        cartJson['sections'] = parsedState['sections'];

        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: formData.get('id'), cart: cartJson });
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
          detail: {
            product: parsedState
          }
        }));

        // A free add (theme.utils.isFreeCartAdd) leaves the drawer closed
        // from a quick add - a card or the quick view doesn't need it
        // popping open for a $0 line. The product page's own form opts back
        // in (data-free-add-drawer, set by buy-buttons.liquid for
        // main-product): there the shopper chose this one product, so they
        // see it land in the cart just as a paid add does.
        const opensDrawer = this.getAttribute('data-free-add-drawer') === 'true' || !theme.utils.isFreeCartAdd(parsedState);
        if (opensDrawer) {
          this.cartDrawer?.show(this.activeElement);
        }
        theme.cartFeedback?.added(this.submitButton, { drawer: opensDrawer && !!this.cartDrawer });
      }))
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        theme.cartFeedback?.settle(this.submitButton);
        this.submitButton.removeAttribute('aria-busy');
        this.submitButtons.forEach(submitButton => submitButton.removeAttribute('aria-busy'));

        if (!this.error) {
          this.submitButton.removeAttribute('aria-disabled');
          this.submitButtons.forEach(submitButton => submitButton.removeAttribute('aria-disabled'));
        }

        if (isSimple) ProductForm.drainPendingAdds(this);
      });
  }

  // Fires everything that queued up behind this request as a single
  // /cart/add.js call with an `items` array instead of one request per
  // click - a burst of N simple quick-add clicks is now 2 requests (the
  // first one solo, the rest combined) instead of N in series.
  static drainPendingAdds(owner) {
    const queue = ProductForm.pendingAddQueue;
    if (!queue || !queue.length) {
      ProductForm.pendingAddQueue = null;
      return;
    }
    ProductForm.pendingAddQueue = [];
    owner.runBatchedAdd(queue);
  }

  runBatchedAdd(queue) {
    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }));

    const last = queue[queue.length - 1];
    const config = theme.utils.fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    config.body = JSON.stringify({
      items: queue.map(op => ({ id: op.formData.get('id'), quantity: Number(op.formData.get('quantity')) || 1 })),
      sections: [...new Set(sectionsToBundle)],
      sections_url: window.location.pathname
    });

    theme.utils.queueCartMutation(() => fetch(theme.routes.cart_add_url, config).then((response) => response.json())
      .then(async (parsedState) => {
        if (parsedState.status) {
          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartError, {
            source: 'product-form',
            productVariantId: last.formData.get('id'),
            errors: parsedState.errors || parsedState.description,
            message: parsedState.message
          });
          queue.forEach(op => {
            theme.cartFeedback?.settle(op.owner.submitButton);
            op.owner.handleErrorMessage(parsedState.description);
            const submitButtonText = op.owner.submitButton.querySelector('.btn-text>span');
            if (submitButtonText && submitButtonText.hasAttribute('data-sold-out')) {
              submitButtonText.innerText = submitButtonText.getAttribute('data-sold-out');
              op.owner.submitButton.setAttribute('aria-disabled', 'true');
              op.owner.error = true;
            }
          });
          document.dispatchEvent(new CustomEvent('ajaxProduct:error', {
            detail: {
              errorMessage: parsedState.description
            }
          }));

          const cartJson = await last.owner.getCartState(parsedState);
          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: last.formData.get('id'), cart: cartJson });

          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: false } }));
          return;
        }

        const cartJson = await last.owner.getCartState(parsedState);
        cartJson['sections'] = parsedState['sections'];

        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: last.formData.get('id'), cart: cartJson });
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
          detail: {
            product: parsedState
          }
        }));

        // Only simple quick adds are batched, but should a form that opts in
        // to free adds opening the drawer (see onSubmitHandler) be among
        // them, it still gets its drawer.
        const opensDrawer = !theme.utils.isFreeCartAdd(parsedState) || queue.some(op => op.owner.getAttribute('data-free-add-drawer') === 'true');
        if (opensDrawer) {
          last.owner.cartDrawer?.show(last.activeElement);
        }
        queue.forEach(op => theme.cartFeedback?.added(op.owner.submitButton, { drawer: opensDrawer && !!last.owner.cartDrawer }));
      }))
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        queue.forEach(op => {
          theme.cartFeedback?.settle(op.owner.submitButton);
          op.owner.submitButton.removeAttribute('aria-busy');
          op.owner.submitButtons.forEach(submitButton => submitButton.removeAttribute('aria-busy'));
          if (!op.owner.error) {
            op.owner.submitButton.removeAttribute('aria-disabled');
            op.owner.submitButtons.forEach(submitButton => submitButton.removeAttribute('aria-disabled'));
          }
        });

        ProductForm.drainPendingAdds(this);
      });
  }

  handleErrorMessage(errorMessage = false) {
    if (this.hideErrors) return;
    
    this.errorMessage = this.errorMessage || this.querySelector('.product-form__error-message');
    if (!this.errorMessage) return;

    this.errorMessage.toggleAttribute('hidden', !errorMessage);
    this.errorMessage.innerText = errorMessage;
  }

  toggleSubmitButton(disable = true, text, unavailable = false) {
    if (!this.submitButton) return;

    this.submitButton.removeAttribute('loading');
    this.submitButton.removeAttribute('unavailable');

    const submitButtonText = this.submitButton.querySelector('.btn-text');
    const submitButtonTextChild = this.submitButton.querySelector('.btn-text>span');

    if (disable) {
      this.submitButton.setAttribute('disabled', '');
      if (unavailable) this.submitButton.setAttribute('unavailable', '');
      if (text) {
        (submitButtonTextChild || submitButtonText).textContent = text;
      }
      else {
        this.submitButton.setAttribute('loading', '');
      }
    }
    else {
      this.submitButton.removeAttribute('disabled');
      (submitButtonTextChild || submitButtonText).textContent = this.submitButton.hasAttribute('data-pre-order') ? theme.variantStrings.preOrder : theme.variantStrings.addToCart;
    }
  }
}
customElements.define('product-form', ProductForm, { extends: 'form' });

class ProductStickyForm extends HTMLElement {
  constructor() {
    super();

    this.scopeFrom = document.querySelector('.quick-order-list') || document.getElementById(this.getAttribute('form'));
    this.scopeTo = document.querySelector('.footer-group');
    this.onVariantChangedListener = this.onVariantChanged.bind(this);

    if (!this.scopeFrom || !this.scopeTo) {
      return;
    }

    this.intersectionObserver = new IntersectionObserver(this.handleIntersection.bind(this));
  }

  get productForm() {
    return document.forms[this.getAttribute('form')];
  }

  get submitButton() {
    return this._submitButton = this._submitButton || this.querySelector('[type="submit"]');
  }

  get productOptions() {
    return this._productOptions = this._productOptions || this.querySelector('[data-sticky-product-options]');
  }

  get productMedia() {
    return this._productMedia = this._productMedia || this.querySelector('[data-sticky-product-media]');
  }

  handleIntersection(entries) {
    let scopeFromChanged = false;
    entries.forEach((entry) => {
      if (entry.target === this.scopeFrom) {
        this.scopeFromVisible = entry.isIntersecting;
        this.scopeFromPassed = entry.boundingClientRect.bottom < 0;
        scopeFromChanged = true;
      }
      if (entry.target === this.scopeTo) {
        this.scopeToReached = entry.isIntersecting;
      }
    });

    const shouldShow = this.scopeFromPassed && !this.scopeToReached;
    const applyVisibility = () => {
      if (shouldShow === this.stickyVisible) return;
      this.stickyVisible = shouldShow;
      this.animateStickyCard(this.firstElementChild, shouldShow);
    };

    clearTimeout(this.stickyVisibilityTimer);
    if (scopeFromChanged) {
      // The real add-to-cart button itself just crossed into or out of
      // view - a deliberate result of the user's own scrolling, not
      // subject to the footer's late-loading-section layout shift below
      // (see the debounced branch). React immediately: debouncing this
      // one left the sticky bar still visible for a stretch after
      // scrolling back up fast enough that the real button was already
      // on screen again.
      applyVisibility();
    }
    else {
      // Sections below the buy box that load their content asynchronously
      // (recently-viewed, product-recommendations - both fetch and inject
      // their markup well after initial load, see the RecentlyViewed and
      // ProductRecommendations classes) push the footer further down the
      // page once they render. While one is still loading, the page is
      // shorter than its final height, so the footer can briefly land
      // inside the viewport - scopeToReached goes true and hides this bar -
      // only to flip back false moments later once that section finishes
      // loading and pushes the footer back out of view, showing the bar
      // again. That read as the bar disappearing and reappearing on its
      // own. Debouncing this footer-only path absorbs that: if the state
      // flips back within the delay, the pending change is cancelled and
      // no animation ever plays; only a change that's still true after
      // sitting stable for a moment goes through.
      this.stickyVisibilityTimer = setTimeout(applyVisibility, 250);
    }
  }

  animateStickyCard(card, shouldShow) {
    if (shouldShow) {
      if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        // Grow from a small pill and settle into the existing card using
        // real spring physics (Motion.spring), not hand-placed overshoot/
        // undershoot keyframes. Motion's spring easing only drives a single
        // animatable number per property - passed a composite `transform`
        // string like 'translateY(12px) scale(0.38, 0.78)' it silently
        // parses just the leading number, so the spring has nothing real
        // to act on and the motion falls flat. Animating y/scaleX/scaleY
        // as their own numeric properties (Motion's independent-transform
        // keys) gives the spring an actual number to move for each, so
        // each one overshoots and settles on its own - which is what
        // produces a visible, natural bounce. scaleX is kept on a tighter
        // spring than y/scaleY: the card is full-width on mobile, so any
        // horizontal overshoot pushes it past the viewport edges, while
        // vertical motion has room to bounce more freely - so the livelier
        // spring goes on y/scaleY to make the bounce feel bigger without
        // the width ever overflowing. Used at every viewport width, so
        // desktop's sticky bar gets the identical entrance/exit as mobile's
        // rather than its own separate tuning.
        //
        // Motion ends each spring by forcing its last sample to the target.
        // Loose scale thresholds cut it off near a turning point: scaleX
        // previously stopped at 290ms while still 0.76% oversized, causing
        // a visible width snap. Keep the same spring forces and initial
        // bounce, but let the residual motion decay below a visible pixel
        // before committing the final sample (also for the y spring).
        card.style.transformOrigin = 'center bottom';
        const stickyYBounce = Motion.spring({ stiffness: 340, damping: 17, mass: 1, restDistance: 0.01, restSpeed: 0.05 });
        const stickyScaleBounce = Motion.spring({ stiffness: 340, damping: 12, mass: 1, restDistance: 0.0001, restSpeed: 0.001 });
        const stickySettle = Motion.spring({ stiffness: 340, damping: 30, mass: 1, restDistance: 0.0001, restSpeed: 0.001 });
        Motion.animate(card, {
          opacity: [0, 1],
          visibility: 'visible',
          y: [18, 0],
          scaleX: [0.38, 1],
          scaleY: [0.7, 1]
        }, {
          y: { easing: stickyYBounce },
          scaleY: { easing: stickyScaleBounce },
          scaleX: { easing: stickySettle },
          opacity: { duration: 0.35, easing: [0.16, 1, 0.3, 1] }
        });
      }
      else {
        Motion.animate(card, { opacity: 1, visibility: 'visible', transform: ['translateY(15px)', 'translateY(0)'] }, { duration: 0, easing: [0.16, 1, 0.3, 1] });
      }
    }
    else {
      if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        // Not a literal reverse of the grow-and-overshoot entrance (that
        // would read as a bounce on the way out too) - a quicker, calmer
        // shrink-and-fade back down toward the same small pill the
        // entrance grew from, settling out of view instead of bouncing.
        card.style.transformOrigin = 'center bottom';
        Motion.animate(card, {
          opacity: 0,
          visibility: 'hidden',
          transform: ['translateY(0) scale(1)', 'translateY(6px) scale(0.9)']
        }, { duration: 0.18, easing: [0.4, 0, 1, 1] });
      }
      else {
        Motion.animate(card, { opacity: 0, visibility: 'hidden', transform: 'translateY(15px)' }, { duration: 0, easing: [0.16, 1, 0.3, 1] });
      }
    }
  }

  connectedCallback() {
    if (!this.scopeFrom || !this.scopeTo) return;

    this.intersectionObserver.observe(this.scopeFrom);
    this.intersectionObserver.observe(this.scopeTo);
    (this.productForm ?? document).addEventListener('variant:change', this.onVariantChangedListener);
  }

  disconnectedCallback() {
    if (!this.scopeFrom || !this.scopeTo) return;

    clearTimeout(this.stickyVisibilityTimer);
    this.intersectionObserver.disconnect();
    (this.productForm ?? document).removeEventListener('variant:change', this.onVariantChangedListener);
  }

  onVariantChanged(event) {
    this.updateProductMedia(event.detail.variant);
    this.updateProductOptions(event.detail.variant);
  }

  updateProductMedia(currentVariant) {
    if (!currentVariant || !currentVariant.featured_media) return;

    const image = this.productMedia.querySelector('img');
    const path = (src) => new URL(src, window.location.href).pathname.split('/').pop();
    if (image && path(image.currentSrc || image.src) === path(currentVariant.featured_media.preview_image.src)) return;
    const newImage = new Image(currentVariant.featured_media.preview_image.width, currentVariant.featured_media.preview_image.height);

    newImage.alt = currentVariant.featured_media.alt;
    newImage.src = currentVariant.featured_media.preview_image.src;
    newImage.srcset = this.generateSrcset(currentVariant.featured_media.preview_image);
    newImage.sizes = image.sizes;

    this.productMedia.replaceChildren(newImage);
  }

  updateProductOptions(currentVariant) {
    if (!currentVariant) return;

    this.productOptions.innerText = currentVariant.title;
  }

  handleErrorMessage(errorMessage = false) {
    if (this.productForm?.hideErrors) return;
    
    this.errorMessage = this.errorMessage || this.querySelector('.product-form__error-message');
    if (!this.errorMessage) return;

    this.errorMessage.toggleAttribute('hidden', !errorMessage);
    this.errorMessage.innerText = errorMessage;
  }

  toggleSubmitButton(disable = true, text) {
    if (!this.submitButton) return;

    const submitButtonText = this.submitButton.querySelector('.btn-text');
    const submitButtonTextChild = this.submitButton.querySelector('.btn-text>span');

    if (disable) {
      this.submitButton.setAttribute('disabled', '');
      if (text) {
        (submitButtonTextChild || submitButtonText).textContent = text;
      }
    }
    else {
      this.submitButton.removeAttribute('disabled');
      (submitButtonTextChild || submitButtonText).textContent = this.submitButton.hasAttribute('data-pre-order') ? theme.variantStrings.preOrder : theme.variantStrings.addToCart;
    }
  }

  generateSrcset(image) {
    const widths = this.productMedia.getAttribute('data-widths').split(',').map((width) => parseInt(width));
    return widths.filter((width) => width <= image.width).map((width) => {
      return `${image.src}&width=${width} ${width}w`;
    }).join(', ');
  }
}
customElements.define('product-sticky-form', ProductStickyForm);

// Touch press feedback for the add-to-cart buttons (the product form's and
// the sticky bar's). A quick tap is over before :active shows anything (and
// iOS Safari skips :active without a touch listener), so a touch marks the
// button data-pressed for at least 160ms; the CSS (apple-product.css) darkens
// and shrinks it for that long, then lets it spring back. Mouse clicks keep
// their :hover/:active look.
document.addEventListener('pointerdown', (event) => {
  if (event.pointerType === 'mouse' || !event.isPrimary) return;
  const button = event.target.closest?.('.product-form__submit, .product-sticky-form button[type="submit"]');
  if (!button || button.disabled || button.getAttribute('aria-disabled') === 'true') return;
  const pressedAt = performance.now();
  button.setAttribute('data-pressed', '');
  const release = () => {
    window.removeEventListener('pointerup', release);
    window.removeEventListener('pointercancel', release);
    setTimeout(() => button.removeAttribute('data-pressed'), Math.max(0, 160 - (performance.now() - pressedAt)));
  };
  window.addEventListener('pointerup', release);
  window.addEventListener('pointercancel', release);
}, { passive: true });

class ProductBundleDetails extends AccordionDetails {
  constructor() {
    super();
  }

  cartUpdateUnsubscriber = undefined;

  get productForm() {
    return document.forms[this.getAttribute('form')];
  }

  get bundles() {
    return Array.from(this.querySelectorAll('input[name="bundles"]:checked'));
  }

  connectedCallback() {
    this.cartUpdateUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
    this.onBundleChangeListener = this.onBundleChange.bind(this);
    this.addEventListener('change', this.onBundleChangeListener);
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
    this.removeEventListener('change', this.onBundleChangeListener);
  }

  onCartUpdate(event) {
    if (this.bundles.length === 0) return;

    if (event.source === 'product-form') {
      this.bundles.forEach((input) => {
        if (!input.disabled) input.checked = false;
      });
      this.onBundleChange();
    }
  }

  onBundleChange() {
    (this.productForm ?? document).dispatchEvent(new CustomEvent('bundle:change', {
      detail: {
        items: this.bundles.map(input => ({
          id: input.value,
          quantity: 1,
          price: input.getAttribute('data-price')
        }))
      }
    }));
  }
}
customElements.define('product-bundle-details', ProductBundleDetails, { extends: 'details' });

class ProductBundlePicker extends HTMLLabelElement {
  constructor() {
    super();
    this.handleVariantChange = this.handleVariantChange.bind(this);
  }

  connectedCallback() {
    this.addEventListener('change', this.handleVariantChange);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.handleVariantChange);
  }

  handleVariantChange(event) {
    const target = event.target;

    if (target.tagName !== 'SELECT') return;

    const option = target.selectedOptions?.[0];
    if (!option) return;

    const value = option.value;
    const price = option.getAttribute('data-price');
    const imgSrc = option.getAttribute('data-img-src');
    const imgSrcset = option.getAttribute('data-img-srcset');

    this.updateCheckboxInput(value, price);
    this.updatePrice(price);
    this.updateImage(imgSrc, imgSrcset);
  }

  updateCheckboxInput(value, price) {
    const input = this.querySelector('input[type="checkbox"]');
    if (!input) return;

    input.value = value;
    input.setAttribute('data-price', price);
  }

  updatePrice(price) {
    const priceElement = this.querySelector('.price__regular');
    if (!priceElement || !price) return;

    const format = theme.settings.currencyCodeEnabled
      ? theme.settings.moneyWithCurrencyFormat
      : theme.settings.moneyFormat;

    priceElement.innerHTML = theme.Currency.formatMoney(price, format);
  }

  updateImage(src, srcset) {
    const img = this.querySelector('img');
    if (!img) return;

    if (src) img.src = src;
    if (srcset) img.srcset = srcset;
  }
}
customElements.define('product-bundle-picker', ProductBundlePicker, { extends: 'label' });

class ProductBuyPrice extends HTMLElement {
  constructor() {
    super();
    this.onBundleChangedListener = this.onBundleChanged.bind(this);
  }

  get productForm() {
    const formId = this.getAttribute('form');
    return formId ? document.forms[formId] : null;
  }

  get basePrice() {
    return parseInt(this.getAttribute('data-price')) || 0;
  }

  connectedCallback() {
    (this.productForm ?? document).addEventListener('bundle:change', this.onBundleChangedListener);
  }

  disconnectedCallback() {
    (this.productForm ?? document).removeEventListener('bundle:change', this.onBundleChangedListener);
  }

  onBundleChanged(event) {
    const { items } = event.detail;

    const subtotal = items.reduce((total, item) => {
      const price = parseInt(item.price) || 0;
      const quantity = parseInt(item.quantity) || 0;
      return total + (price * quantity);
    }, this.basePrice);

    this.updatePrice(subtotal);
  }

  updatePrice(amount) {
    const format = theme.settings.currencyCodeEnabled
      ? theme.settings.moneyWithCurrencyFormat
      : theme.settings.moneyFormat;
    
    this.innerHTML = theme.Currency.formatMoney(amount, format);
  }

}
customElements.define('product-buy-price', ProductBuyPrice);

class MediaGallery extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, () => {
      requestAnimationFrame(() => this.pauseAllMedia());
      //setTimeout(() => this.pauseAllMedia(), 500);
    });

    (this.productForm ?? document).addEventListener('variant:change', this.onVariantChanged.bind(this));
    
    this.addEventListener('lightbox:open', (event) => this.openZoom(event.detail.index));
    this.warmLightboxListener = this.warmLightbox.bind(this);
    this.addEventListener('pointerdown', this.warmLightboxListener, { passive: true });
    this.sliderGallery.addEventListener('slider:change', this.onSlideChange.bind(this));
    this.sliderGallery.addEventListener('slider:target', (event) => this.showSlide(event.detail.currentElement));

    this.countMediaGallery();
    this.initReveal();
    this.warmSlidesWhenIdle();
  }

  // Product-page entrance for the gallery, matched to product-info's own
  // staggered fade-up on the right (data-stagger-info, same easing): the
  // main image eases up into place with a barely-there scale settle, then
  // the thumbnail rail (or the mobile dots) follows in a short stagger.
  // Opt-in via data-reveal, so quick view and the featured-product section
  // keep their galleries as they are. Inline styles are cleared once it
  // settles, so the main image frame isn't left carrying a transform.
  initReveal() {
    if (!this.hasAttribute('data-reveal')) return;
    // The thumbnail rail (MediaDots) otherwise only initializes on the
    // first click/mousemove/key/touch/wheel or after 5s (initWhenVisible),
    // and until then its scrollbar stays [hidden] - so with a still mouse
    // the bar skipped the reveal and just popped in on the first movement.
    // Initialize it as the gallery comes into view instead (init() is
    // idempotent, so the deferred call later is a no-op).
    const initRail = () => this.querySelector('media-dots')?.init?.();
    // premiere-product.css holds the previews hidden from the first paint
    // until this attribute is set (they used to show, vanish when this ran
    // and then reveal: a second flash)
    const arm = () => this.setAttribute('data-reveal-armed', '');
    if (theme.config.motionReduced) {
      arm();
      Motion.inView(this, initRail);
      return;
    }

    const main = this.sliderGallery?.parentElement;
    if (!main) {
      arm();
      return;
    }
    const rail = this.querySelector('media-dots');
    const thumbnails = rail ? Array.from(rail.children).filter((item) => !item.hidden) : [];
    const scrollbar = this.querySelector('.product__thumbnails-scrollbar');
    // On a slow load this script can run after premiere-product.css has
    // already let the previews in on its own (3s); hiding them again here
    // to play the entrance made them flash a second time, so previews that
    // are already showing are left as they are.
    const previewsShown = thumbnails.length > 0 && getComputedStyle(thumbnails[0]).opacity !== '0';
    const trailing = previewsShown ? [] : scrollbar ? [...thumbnails, scrollbar] : thumbnails;
    const railReady = previewsShown ? Promise.resolve() : this.prepareRevealThumbnails(thumbnails);

    const compact = theme.config.mqlSmall;
    const distance = compact ? 14 : 18;
    const easing = [0.16, 1, 0.3, 1];
    const clear = (elements) => elements.forEach((element) => {
      element.style.removeProperty('opacity');
      element.style.removeProperty('transform');
    });

    Motion.animate(main, { opacity: 0, transform: `translateY(${distance}px) scale(0.985)` }, { duration: 0 });
    if (trailing.length) Motion.animate(trailing, { opacity: 0, transform: 'translateY(10px)' }, { duration: 0 });
    arm();

    let revealed = false;
    Motion.inView(this, () => {
      if (revealed) return;
      revealed = true;
      initRail();
      const startedAt = performance.now();
      Motion.animate(
        main,
        { opacity: [0, 1], transform: [`translateY(${distance}px) scale(0.985)`, 'translateY(0) scale(1)'] },
        { duration: compact ? 0.6 : 0.8, easing }
      ).finished.then(() => clear([main]));
      if (!trailing.length) return;
      // The rail follows once its thumbnail images are in (see
      // prepareRevealThumbnails), keeping its usual offset behind the main
      // image when they already are.
      railReady.then(() => {
        const start = Math.max(0, (compact ? 0.1 : 0.16) - (performance.now() - startedAt) / 1000);
        Motion.animate(
          trailing,
          { opacity: [0, 1], transform: ['translateY(10px)', 'translateY(0)'] },
          { duration: 0.6, easing, delay: Motion.stagger(compact ? 0.03 : 0.04, { start }) }
        ).finished.then(() => {
          clear(trailing);
          thumbnails.forEach((thumbnail) => thumbnail.removeAttribute('easing'));
        });
      });
    });
  }

  // The thumbnails are lazy images: each tile used to fade in with the
  // rail while its image was still loading (held at opacity 0 under
  // .media.loading), and then the image faded in again on its own - twice
  // over, via apple-image-skeletons.js and the .media > img opacity
  // transition - so every preview visibly "refreshed" right after
  // appearing. They're on screen with the main image anyway, so load them
  // straight away, hold the rail's stagger until they're in (capped, so a
  // slow network never holds it back), and switch off the images' own CSS
  // fade (.media[easing] > img) while the tiles reveal: each preview fades
  // in once, image included.
  prepareRevealThumbnails(thumbnails) {
    const settled = (thumbnail) => {
      const image = thumbnail.querySelector('img');
      return !image || (image.complete && !thumbnail.classList.contains('loading'));
    };
    thumbnails.forEach((thumbnail) => {
      thumbnail.setAttribute('easing', '');
      const image = thumbnail.querySelector('img');
      if (image && !image.complete) image.loading = 'eager';
    });

    const pending = thumbnails.filter((thumbnail) => !settled(thumbnail));
    if (!pending.length) return Promise.resolve();
    return new Promise((resolve) => {
      let timer;
      const observer = new MutationObserver(() => {
        if (pending.every(settled)) done();
      });
      const done = () => {
        observer.disconnect();
        clearTimeout(timer);
        resolve();
      };
      timer = setTimeout(done, 900);
      pending.forEach((thumbnail) => observer.observe(thumbnail, { attributes: true, attributeFilter: ['class'], subtree: true }));
    });
  }

  get viewInSpaceButton() {
    return this.querySelector('[data-shopify-xr]');
  }

  get productForm() {
    return document.forms[this.getAttribute('form')];
  }

  get sliderGallery() {
    return this.querySelector('slider-element');
  }

  get mediaPreview() {
    return this.querySelector('.product__preview .product__media');
  }

  get photoswipe() {
    if (this._photoswipe) return this._photoswipe;

    const lightbox = new PhotoSwipeLightbox({
      arrowPrevSVG: '<svg class="pswp__icn icon" stroke="currentColor" fill="none" viewBox="0 0 30 30"><path d="M17.5 7.5L10 15L17.5 22.5"/></svg>',
      arrowNextSVG: '<svg class="pswp__icn icon" stroke="currentColor" fill="none" viewBox="0 0 30 30"><path d="M17.5 7.5L10 15L17.5 22.5"/></svg>',
      closeSVG: '<svg class="pswp__icn icon" stroke="currentColor" fill="none" viewBox="0 0 30 30"><path d="m7.5 22.5 15-15m-15 0 15 15"/></svg>',
      bgOpacity: 1,
      pswpModule: () => import(theme.settings.pswpModule),

      arrowPrevTitle: theme.strings.previous,
      arrowNextTitle: theme.strings.next,
      closeTitle: theme.strings.close,

      // Recommended options for this plugin
      allowPanToNext: false,
      allowMouseDrag: true,
      wheelToZoom: false,
      returnFocus: true,
      zoom: false,
    });

    lightbox.addFilter('thumbEl', (_thumbEl, data) => data['thumbnailElement']);

    lightbox.on('contentLoad', (event) => {
      const { content } = event;

      if (content.type === 'video' || content.type === 'external_video' || content.type === 'model') {
        event.preventDefault();

        // Create a container for video and assign it to the `content.element` property
        content.element = document.createElement('div');
        content.element.className = 'pswp__video-container';
        content.element.appendChild(content.data.domElement.cloneNode(true));
      }
    });

    lightbox.on('change', () => {
      this.sliderGallery.select(lightbox.pswp.currIndex + 1, true);
    });

    lightbox.on('close', () => {
      const slideSelected = this.sliderGallery.selected(lightbox.pswp.currIndex);
      lightbox.pswp._lastActiveElement = slideSelected ? slideSelected.querySelector('button') : document.activeElement;
    });

    lightbox.init();
    return this._photoswipe = lightbox;
  }

  // The PhotoSwipe core is only imported once the lightbox opens, on click.
  // Pressing a zoom button that opens it starts that same import() already
  // (pointerdown lands 100-200 ms before click on a tap), so the click finds
  // the module loaded or on its way; nothing else about the open changes.
  warmLightbox(event) {
    if (!event.target.closest('button')?.opensLightbox) return;
    this.removeEventListener('pointerdown', this.warmLightboxListener);
    import(theme.settings.pswpModule).catch(() => {});
  }

  onVariantChanged(event) {
    const currentVariant = event.detail.variant;
    if (!currentVariant.featured_media) return;

    this.countMediaGallery();
    this.setActiveMedia(currentVariant.featured_media.id);
  }

  onSlideChange(event) {
    const activeMedia = event.detail.currentElement;
    this.showSlide(activeMedia);
    this.playActiveMedia(activeMedia);

    if (this.viewInSpaceButton) {
      if (activeMedia.getAttribute('data-media-type') === 'model') {
        this.viewInSpaceButton.setAttribute('data-shopify-model3d-id', activeMedia.getAttribute('data-media-id'));
      } else {
        this.viewInSpaceButton.setAttribute('data-shopify-model3d-id', this.viewInSpaceButton.getAttribute('data-shopify-model3d-default-id'));
      }
    }
  }

  // A selected slide always shows its image, promptly. Slides past the
  // first are lazy images in a horizontally clipped slider, so the browser
  // only started fetching a thumbnail's slide once the glide had scrolled it
  // near the view, at no particular priority, and the frame showed nothing
  // but its dark stage until then; and the image stays at opacity 0 under
  // .media.loading / the skeleton until a load event clears it, which never
  // comes for an image that finished before anyone listened. So as soon as
  // a slide is headed to (slider:target: thumbnail click or hover, arrow,
  // swipe, lightbox) or landed on (slider:change), its image and its
  // neighbours' load eagerly, the selected one first, and each is revealed
  // once in and decoded - even if it was already complete, and even if
  // decode() rejects. A failed request is retried once.
  showSlide(media) {
    const items = this.sliderGallery?.itemsToShow;
    if (!media || !items) return;
    const index = items.indexOf(media);
    [media, items[index + 1], items[index - 1]].forEach((slide, position) => {
      if (!slide || (position > 0 && index < 0)) return;
      slide.querySelectorAll('img').forEach((image) => {
        if (image.closest('.media') === null) return;
        if (image.getAttribute('loading') === 'lazy') image.setAttribute('loading', 'eager');
        if (position === 0 && !image.complete && image.getAttribute('fetchpriority') !== 'high') image.setAttribute('fetchpriority', 'high');
        this.revealWhenReady(image);
      });
    });
  }

  revealWhenReady(image) {
    if (this.watchedImages?.has(image)) return;
    if (image.complete && image.naturalWidth > 0) {
      this.revealImage(image);
      return;
    }
    if (image.complete && (image.currentSrc || image.getAttribute('src'))) {
      this.retryImage(image);
      return;
    }
    this.watchImage(image);
  }

  watchImage(image) {
    this.watchedImages ??= new WeakSet();
    if (this.watchedImages.has(image)) return;
    this.watchedImages.add(image);
    const done = (event) => {
      image.removeEventListener('load', done);
      image.removeEventListener('error', done);
      this.watchedImages.delete(image);
      event.type === 'load' ? this.revealImage(image) : this.retryImage(image);
    };
    image.addEventListener('load', done);
    image.addEventListener('error', done);
  }

  retryImage(image) {
    if (image.hasAttribute('data-gallery-retried')) return;
    image.setAttribute('data-gallery-retried', '');
    // Setting srcset/src again, even to the same value, starts a new request.
    const attribute = image.hasAttribute('srcset') ? 'srcset' : 'src';
    image.setAttribute(attribute, image.getAttribute(attribute));
    this.watchImage(image);
  }

  // LazyImage and apple-image-skeletons.js normally clear the loading state
  // on the load event (with their own fade); this only steps in where they
  // did not, so nothing changes when they already have.
  revealImage(image) {
    const reveal = () => {
      if (!image.isConnected || !(image.naturalWidth > 0)) return;
      if (image.getAttribute('is') === 'lazy-image' && !image.classList.contains('loaded')) image.classList.add('loaded');
      if (image.dataset.imageLoadState && image.dataset.imageLoadState !== 'loaded') image.dataset.imageLoadState = 'loaded';
      const media = image.closest('.media');
      // .loading + .pointer-events-none: the hover zoom's spinner, which toggles itself off.
      if (!media || media.classList.contains('pointer-events-none')) return;
      const pending = Array.from(media.querySelectorAll('img')).some((other) => other.closest('.media') === media && !(other.complete && other.naturalWidth > 0));
      if (pending) return;
      ['loading', 'image-skeleton-active', 'image-load-error'].forEach((name) => {
        if (media.classList.contains(name)) media.classList.remove(name);
      });
    };
    if (typeof image.decode !== 'function') {
      reveal();
      return;
    }
    image.decode().then(reveal, reveal);
  }

  // Slides past the first only started downloading once they were on their
  // way (see showSlide) or, with a mouse, once the pointer reached the
  // thumbnails (MediaDots.addRailWarming) - a moment before the click - so a
  // picked picture was often still coming in behind the skeleton. Once the
  // page has loaded and the browser is idle, with the gallery in view, the
  // slides either side of the current one start loading, and with a mouse or
  // trackpad (thumbnails to hover) every shown slide does.
  warmSlidesWhenIdle() {
    let warmed = false;
    const warm = () => {
      const items = this.sliderGallery?.itemsToShow;
      if (warmed || !this.isConnected || !items?.length) return;
      warmed = true;
      const index = Math.max(0, (this.sliderGallery.currentPage || 1) - 1);
      const slides = matchMedia('(hover: hover) and (pointer: fine)').matches ? items : [items[index - 1], items[index + 1]];
      slides.forEach((slide) => {
        slide?.querySelectorAll('img[loading="lazy"]').forEach((image) => { image.loading = 'eager'; });
      });
    };
    const idle = () => (window.requestIdleCallback ? requestIdleCallback(warm, { timeout: 2000 }) : setTimeout(warm, 200));
    // (a callback that returns nothing runs once: inView stops watching)
    Motion.inView(this, () => {
      if (document.readyState === 'complete') idle();
      else window.addEventListener('load', idle, { once: true });
    });
  }

  setActiveMedia(mediaId) {
    let activeMedia = this.sliderGallery.querySelector(`[data-media-id="${mediaId}"]`);
    if (activeMedia?.checkVisibility() === false) {
      activeMedia = activeMedia.nextElementSibling;
    }
    if (activeMedia === null) return;

    this.showSlide(activeMedia);
    activeMedia.scrollIntoView({
      block: 'start',
      behavior: theme.config.motionReduced ? 'auto' : 'smooth'
    });
  }

  playActiveMedia(activeMedia) {
    if (typeof activeMedia === 'undefined') return;
    
    const deferredMedia = activeMedia.querySelector('.deferred-media');

    this.sliderGallery.querySelectorAll('.deferred-media').forEach((media) => {
      deferredMedia === media ? media.play() : media.pause();
    });
  }

  pauseAllMedia() {
    this.sliderGallery.querySelectorAll('[data-media-id]').forEach((media, index) => {
      if (index > 0) {
        const deferredMedia = media.querySelector('.deferred-media');
        if (deferredMedia && typeof deferredMedia.pause === 'function') deferredMedia.pause();
      }
    });
  }

  openZoom(index = 0) {
    let dataSource = this.sliderGallery.itemsToShow.map((media) => {
      const image = media.querySelector('img');

      if (media.getAttribute('data-media-type') === 'image') {
        return {
          thumbnailElement: image,
          src: image.src,
          srcset: image.srcset,
          msrc: image.currentSrc || image.src,
          width: parseInt(image.getAttribute('width')),
          height: parseInt(image.getAttribute('height')),
          alt: image.alt,
          thumbCropped: true
        };
      }
      
      if (media.getAttribute('data-media-type') === 'video' || media.getAttribute('data-media-type') === 'external_video' || media.getAttribute('data-media-type') === 'model') {
        const video = media.querySelector('.deferred-media');
        return {
          thumbnailElement: image,
          domElement: video,
          type: media.getAttribute('data-media-type'),
          src: image.src,
          srcset: image.srcset,
          msrc: image.currentSrc || image.src,
          width: 800,
          height: 800 / video.getAttribute('data-aspect-ratio'),
          alt: image.alt,
          thumbCropped: true
        };
      }
    });
    
    if (this.mediaPreview && this.mediaPreview.offsetParent) {
      if (this.mediaPreview.getAttribute('data-media-type') === 'image') {
        const image = this.mediaPreview.querySelector('img');
        dataSource.push({
          thumbnailElement: image,
          src: image.src,
          srcset: image.srcset,
          msrc: image.currentSrc || image.src,
          width: parseInt(image.getAttribute('width')),
          height: parseInt(image.getAttribute('height')),
          alt: image.alt,
          thumbCropped: true
        });

        if (index === -1) {
          index = dataSource.length - 1;
        }
      }
    }

    this.photoswipe.loadAndOpen(index, dataSource);
  }

  countMediaGallery() {
    let mediaCount = 0;
    this.sliderGallery.querySelectorAll('[data-media-id]').forEach((media) => {
      if (!media.classList.contains('xl:hidden') && !media.hasAttribute('hidden')) {
        mediaCount++;
      }
    });

    if (this.mediaPreview) {
      mediaCount++;
    }

    mediaCount > 1 ? this.classList.remove('with-only1') : this.classList.add('with-only1');
  }
}
customElements.define('media-gallery', MediaGallery);

class MediaLightboxButton extends HTMLButtonElement {
  constructor() {
    super();

    this.addEventListener('click', this.onButtonClick);
  }

  // Read by MediaGallery.warmLightbox.
  get opensLightbox() {
    return true;
  }

  onButtonClick() {
    if (this.closest('slider-element')?.navigating) return;
    const media = this.closest('[data-media-id]');
    const sliderGallery = this.closest('slider-element');
    const openIndex = sliderGallery ? sliderGallery.itemsToShow.indexOf(media) : -1;

    this.dispatchEvent(new CustomEvent('lightbox:open', {
      bubbles: true,
      detail: {
        index: openIndex
      }
    }));
  }
}
customElements.define('media-lightbox-button', MediaLightboxButton, { extends: 'button' });

class MediaHoverButton extends MediaLightboxButton {
  constructor() {
    super();
  }

  get zoomRatio() {
    return 2;
  }

  // Only touch opens the lightbox (onButtonClick); a mouse click magnifies in place.
  get opensLightbox() {
    return theme.config.isTouch;
  }

  onButtonClick(event) {
    if (this.closest('slider-element')?.navigating) return;
    if (theme.config.isTouch) {
      super.onButtonClick();
    }
    else {
      const media = this.closest('[data-media-type="image"]');
      if (media) {
        const image = media.querySelector('img');
        this.gallery = this.closest('slider-element');
  
        this.magnify(image);
        this.moveWithHover(image, event);
      }
    }
  }

  // The overlay draws the image at zoomRatio times its rendered width, so it
  // only needs that many device pixels: the smallest srcset candidate that
  // covers them shows the same detail as image.src, the full original upload
  // (a URL the gallery never displays, so never cached). Falls back to
  // image.src when no candidate is wide enough. srcset is split on comma +
  // whitespace, as image_tag writes it, since a URL may contain a comma.
  zoomSource(image) {
    const src = image.src;
    const needed = image.width * this.zoomRatio * (window.devicePixelRatio || 1) * Math.max(1, window.visualViewport?.scale || 1);
    let best = null;
    (image.getAttribute('srcset') || '').split(/,\s+/).forEach((candidate) => {
      const [url, descriptor] = candidate.trim().split(/\s+/);
      if (!url || !descriptor || !descriptor.endsWith('w')) return;
      const width = parseInt(descriptor, 10);
      if (Number.isFinite(width) && width >= needed && (!best || width < best.width)) best = { url, width };
    });
    if (!best) return src;
    try { return new URL(best.url, document.baseURI).href; } catch (e) { return src; }
  }

  createOverlay(image) {
    const overlayImage = document.createElement('img');
    overlayImage.setAttribute('src', `${this.zoomSource(image)}`);
    const overlay = document.createElement('media-hover-overlay');
    this.prepareOverlay(overlay, overlayImage);
  
    this.toggleLoadingSpinner(image);
  
    overlayImage.onload = () => {
      this.toggleLoadingSpinner(image);
      image.parentElement.insertBefore(overlay, image);
    };

    if (this.gallery) this.gallery.classList.add('magnify');
  
    return overlay;
  }

  prepareOverlay(container, image) {
    container.setAttribute('class', 'media z-10 absolute top-0 left-0 w-full h-full');
    container.setAttribute('aria-hidden', 'true');
    container.style.backgroundImage = `url('${image.src}')`;
    container.style.cursor = 'zoom-out';
  }
  
  toggleLoadingSpinner(image) {
    const loadingSpinner = image.parentElement;
    loadingSpinner.classList.toggle('loading');
    loadingSpinner.classList.toggle('pointer-events-none');
  }

  moveWithHover(image, event) {
    // calculate mouse position
    const ratio = image.height / image.width;
    const container = event.target.getBoundingClientRect();
    const xPosition = event.clientX - container.left;
    const yPosition = event.clientY - container.top;
    const xPercent = `${xPosition / (image.clientWidth / 100)}%`;
    const yPercent = `${yPosition / ((image.clientWidth * ratio) / 100)}%`;

    // determine what to show in the frame
    this.overlay.style.backgroundPosition = `${xPercent} ${yPercent}`;
    this.overlay.style.backgroundSize = `${image.width * this.zoomRatio}px`;
  }

  magnify(image) {
    this.overlay = this.createOverlay(image);
    this.overlay.onclick = () => this.reset();
    this.overlay.onmousemove = (event) => this.moveWithHover(image, event);
    this.overlay.onmouseleave = () => this.reset();
  }

  reset() {
    this.overlay.remove();

    if (this.gallery) this.gallery.classList.remove('magnify');
  }
}
customElements.define('media-hover-button', MediaHoverButton, { extends: 'button' });

class MediaDots extends SliderDots {
  constructor() {
    super();
  }

  init() {
    if (this.initialized) return;
    super.init();
    this.resetIndexes();
    this.addProgressListener();
    this.addRailScrollbar();
    this.addHoverSelection();
  }

  connectedCallback() {
    this.addRailWarming();
    if (this.initialized) {
      this.addProgressListener();
      this.addRailScrollbar();
      this.addHoverSelection();
    }
  }

  disconnectedCallback() {
    this.progressSlider?.removeEventListener('scroll', this.progressListener);
    this.progressSlider = null;
    cancelAnimationFrame(this.progressFrame);
    this.progressFrame = null;
    cancelAnimationFrame(this.railFrame);
    this.railFrame = null;
    this.removeEventListener('scroll', this.railScrollListener);
    this.railResizeObserver?.disconnect();
    this.railMutationObserver?.disconnect();
    this.railScrollbar?.removeEventListener('pointerdown', this.railPointerDown);
    this.railScrollbar?.removeEventListener('pointermove', this.railPointerMove);
    this.railScrollbar?.removeEventListener('pointerup', this.railPointerUp);
    this.railScrollbar?.removeEventListener('pointercancel', this.railPointerUp);
    this.railScrollbar?.removeEventListener('keydown', this.railKeyDown);
    this.railScrollbar?.removeAttribute('data-dragging');
    this.railScrollbar = null;
    this.railDrag = null;
    this.items.forEach((item) => item.removeEventListener('mouseenter', this.hoverSelectionListener));
    this.removeEventListener('pointerover', this.railWarmListener);
  }

  // SliderDots.onChange also re-centers this element on the newly active
  // item (transitionTo) - fine for a slider's own pagination dots, but for
  // this rail it meant clicking/hovering a thumbnail, using the main
  // slider's prev/next arrows, or swiping the main image directly could
  // all shift the rail out from under the cursor right after. The rail
  // now only ever scrolls from a visitor's own direct interaction with it
  // (drag, wheel, touch, or the scrollbar) - selecting an image never
  // moves it. Still updates aria-current so the right thumbnail is marked
  // selected even when it's off-screen.
  onChange(event) {
    this.itemsToShow.forEach((item) => {
      item.setAttribute('aria-current', parseInt(item.getAttribute('data-index')) === parseInt(event.detail.currentPage) ? 'true' : 'false');
    });
  }

  // Deliberately not onButtonClick (which the click path still uses
  // unchanged): that smooth-scrolls the main slider to the target, so
  // sweeping the cursor across the rail visibly scrolled through every
  // image in between rather than just showing the one under the cursor.
  // select(index, true) jumps straight there instead.
  addHoverSelection() {
    this.hoverSelectionListener ??= (event) => {
      if (!matchMedia('(hover: hover) and (pointer: fine)').matches) return;
      const target = event.currentTarget;
      if (target.getAttribute('aria-current') === 'true') return;
      this.itemsToShow.forEach((item) => {
        item.setAttribute('aria-current', item === target ? 'true' : 'false');
      });
      this.controlledElement.select(parseInt(target.getAttribute('data-index')), true);
    };
    this.items.forEach((item) => item.addEventListener('mouseenter', this.hoverSelectionListener));
  }

  addRailScrollbar() {
    const bar = this.parentElement?.querySelector('.product__thumbnails-scrollbar');
    if (!bar || this.railScrollbar === bar) return;
    this.railScrollbar = bar;
    this.railScrollListener ??= () => this.scheduleRailScrollbar();
    this.addEventListener('scroll', this.railScrollListener, { passive: true });
    this.railResizeObserver ??= new ResizeObserver(() => this.scheduleRailScrollbar());
    this.railResizeObserver.observe(this);
    this.railMutationObserver ??= new MutationObserver(() => this.scheduleRailScrollbar());
    this.railMutationObserver.observe(this, { childList: true, attributes: true, subtree: true, attributeFilter: ['hidden'] });

    // Press anywhere on the bar (its hit area is much taller than the
    // line) and drag: a press on the handle keeps hold of it where it was
    // grabbed; a press beside it glides the handle's middle to the pointer
    // and the drag carries on from there, so sliding starts at once.
    this.railPointerDown ??= (event) => {
      if (event.button !== 0 || this.railScrollbar.hidden) return;
      event.preventDefault();
      const max = this.scrollWidth - this.clientWidth;
      const rect = this.railScrollbar.getBoundingClientRect();
      const barWidth = rect.width;
      const thumbWidth = barWidth * Math.min(1, this.clientWidth / this.scrollWidth);
      const direction = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
      const at = (clientX) => direction === 1 ? clientX - rect.left : rect.right - clientX;
      const thumbStart = barWidth > thumbWidth ? Math.abs(this.scrollLeft) / max * (barWidth - thumbWidth) : 0;
      const position = at(event.clientX);
      const onThumb = position >= thumbStart && position <= thumbStart + thumbWidth;
      this.railDrag = { at, grab: onThumb ? position - thumbStart : thumbWidth / 2, max, range: barWidth - thumbWidth, direction };
      this.railScrollbar.setAttribute('data-dragging', '');
      this.railScrollbar.setPointerCapture(event.pointerId);
      if (!onThumb && this.railDrag.range > 0) {
        this.scrollTo({
          left: direction * Math.max(0, Math.min(max, (position - thumbWidth / 2) * max / this.railDrag.range)),
          behavior: theme.config.motionReduced ? 'auto' : 'smooth'
        });
      }
    };
    this.railPointerMove ??= (event) => {
      if (!this.railDrag || !this.railScrollbar.hasPointerCapture(event.pointerId)) return;
      const { at, grab, max, range, direction } = this.railDrag;
      if (range <= 0) return;
      // setting scrollLeft also takes over from a press's glide
      this.scrollLeft = direction * Math.max(0, Math.min(max, (at(event.clientX) - grab) * max / range));
    };
    this.railPointerUp ??= () => {
      this.railDrag = null;
      this.railScrollbar?.removeAttribute('data-dragging');
    };
    this.railKeyDown ??= (event) => {
      const max = this.scrollWidth - this.clientWidth;
      const step = this.itemsToShow[0]?.clientWidth || 50;
      const direction = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
      let next;
      if (event.key === 'ArrowRight') next = this.scrollLeft + step;
      else if (event.key === 'ArrowLeft') next = this.scrollLeft - step;
      else if (event.key === 'PageDown') next = this.scrollLeft + direction * this.clientWidth;
      else if (event.key === 'PageUp') next = this.scrollLeft - direction * this.clientWidth;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = direction * max;
      else return;
      event.preventDefault();
      this.scrollTo({
        left: Math.max(0, Math.min(max, next * direction)) * direction,
        behavior: theme.config.motionReduced ? 'auto' : 'smooth'
      });
    };
    bar.addEventListener('pointerdown', this.railPointerDown);
    bar.addEventListener('pointermove', this.railPointerMove);
    bar.addEventListener('pointerup', this.railPointerUp);
    bar.addEventListener('pointercancel', this.railPointerUp);
    bar.addEventListener('keydown', this.railKeyDown);
    this.updateRailScrollbar();
  }

  scheduleRailScrollbar() {
    if (this.railFrame != null) return;
    this.railFrame = requestAnimationFrame(() => {
      this.railFrame = null;
      this.updateRailScrollbar();
    });
  }

  updateRailScrollbar() {
    const bar = this.railScrollbar;
    if (!bar) return;
    const max = this.scrollWidth - this.clientWidth;
    bar.hidden = max <= 1 || matchMedia('(max-width: 1023px)').matches;
    if (bar.hidden) return;
    const width = Math.max(12, Math.min(100, this.clientWidth / this.scrollWidth * 100));
    const progress = Math.min(1, Math.abs(this.scrollLeft) / max);
    bar.style.setProperty('--thumb-scale', `${width}%`);
    bar.style.setProperty('--thumb-offset', `${progress * (100 - width)}%`);
    bar.setAttribute('aria-valuenow', String(Math.round(progress * 100)));
  }

  addProgressListener() {
    const slider = this.controlledElement;
    if (!slider || this.progressSlider === slider) return;
    this.progressSlider?.removeEventListener('scroll', this.progressListener);
    this.progressListener ??= () => {
      if (this.progressFrame != null) return;
      this.progressFrame = requestAnimationFrame(() => {
        this.progressFrame = null;
        this.updateProgress();
      });
    };
    this.progressSlider = slider;
    slider.addEventListener('scroll', this.progressListener, { passive: true });
  }

  updateProgress() {
    const slider = this.progressSlider;
    if (!slider) return;
    const offset = Math.abs(slider.itemOffset);
    if (!(offset > 0)) return;
    const items = this.itemsToShow;
    const index = Math.max(0, Math.min(items.length - 1, Math.round(Math.abs(slider.scrollLeft) / offset)));
    items.forEach((item, itemIndex) => {
      const current = itemIndex === index ? 'true' : 'false';
      if (item.getAttribute('aria-current') !== current) item.setAttribute('aria-current', current);
    });
  }

  resetIndexes() {
    let newIndex = 1;
    this.itemsToShow.forEach((item, index) => {
      item.setAttribute('data-index', newIndex);
      item.setAttribute('aria-current', index === 0 ? 'true' : 'false');

      newIndex++;
    });
  }

  // Hover-select jumps straight to a slide, and slides past the first few
  // are lazy, so hovering a later thumbnail waited on that image's whole
  // download behind the skeleton. Once a hover-capable pointer (the same
  // test as hover-select) reaches the rail, start loading every shown slide
  // instead; itemsToShow leaves out hidden variant/gang media. pointerover,
  // not pointerenter: it also fires when a pointer already inside the rail
  // moves onto a thumbnail, just before that thumbnail's mouseenter.
  addRailWarming() {
    if (this.railWarmed) return;
    this.railWarmListener ??= () => {
      if (!matchMedia('(hover: hover) and (pointer: fine)').matches) return;
      this.railWarmed = true;
      this.removeEventListener('pointerover', this.railWarmListener);
      this.controlledElement?.itemsToShow?.forEach((media) => {
        media.querySelectorAll('img[loading="lazy"]').forEach((image) => { image.loading = 'eager'; });
      });
    };
    this.addEventListener('pointerover', this.railWarmListener);
  }
}
customElements.define('media-dots', MediaDots);

class XModal extends ModalElement {
  constructor() {
    super();
  }

  get shouldLock() {
    return true;
  }

  get shouldAppendToBody() {
    return true;
  }
}
customElements.define('x-modal', XModal);

class LogoList extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' });
  }

  get childElement() {
    return this.firstElementChild;
  }

  get speed() {
    return this.hasAttribute('data-speed') ? parseInt(this.getAttribute('data-speed')) : 16;
  }

  get maximum() {
    return this.hasAttribute('data-maximum') ? parseInt(this.getAttribute('data-maximum')) : Math.ceil(this.parentWidth / this.childElementWidth) + 2;
  }

  get direction() {
    return this.hasAttribute('data-direction') ? this.getAttribute('data-direction') : 'left';
  }

  get parentWidth() {
    return this.getWidth(this);
  }

  get childElementWidth() {
    return this.getWidth(this.childElement);
  }

  disconnectedCallback() {
    this.visibilityObserver?.disconnect();
    if (this.marquee) {
      this.pause();
      this.marquee.destroy();
    }
  }

  init() {
    if (this.childElementCount === 1) {
      this.childElement.classList.add('animated');
      
      const maximum = this.maximum;
      for (let index = 0; index < maximum; index++) {
        this.clone = this.childElement.cloneNode(true);
        this.clone.setAttribute('aria-hidden', true);
        this.appendChild(this.clone);
        this.clone.querySelectorAll('.media').forEach((media) => media.classList.remove('loading'));
      }

      if (theme.config.isTouch) {
        this.style.setProperty('--duration', `${33 - this.speed}s`);
      }
      else {
        this.marquee = new Flickity(this, {
          prevNextButtons: false,
          pageDots: false,
          wrapAround: true,
          freeScroll: true,
          rightToLeft: this.direction === 'right',
        });
  
        // Set initial position to be 0
        this.marquee.x = 0;
  
        // Start the marquee animation
        this.play();
  
        // Hovering eases the logos to a stop and leaving eases them back
        // up to speed (see play) instead of freezing them mid-frame.
        this.addEventListener('mouseenter', () => { this.targetSpeedFactor = 0; });
        this.addEventListener('mouseleave', () => {
          this.targetSpeedFactor = 1;
          this.play();
        });

        // Like marquee-element: no per-frame loop while the list is out of view
        // (the same 200px margin that initialises it). A loop that finds the list
        // away with its speed settled parks instead of queuing another frame (see
        // play) and is restarted here when the list comes back.
        this.visibilityObserver = new IntersectionObserver((entries) => {
          this.offscreen = !entries[entries.length - 1].isIntersecting;
          if (!this.offscreen && this.parked) {
            this.parked = false;
            this.play();
          }
        }, { rootMargin: '200px 0px 200px 0px' });
        this.visibilityObserver.observe(this);
      }
    }
  }

  play() {
    // One loop at a time: a mouseleave with no mouseenter before it (the
    // pointer already over the logos when they loaded) used to start a
    // second loop and double the speed for good. With reduced motion the
    // logos stay put (still draggable), as the touch/CSS version does.
    if (this.requestId || theme.config.motionReduced) return;

    this.speedFactor ??= 1;
    this.targetSpeedFactor ??= 1;
    let previous;
    const step = (now) => {
      // Ease the speed toward its target - about 0.4s to stop or resume on
      // any refresh rate. The per-frame distance itself is unchanged.
      const frames = previous === undefined ? 1 : Math.min(4, (now - previous) / (1000 / 60));
      previous = now;
      this.speedFactor += (this.targetSpeedFactor - this.speedFactor) * (1 - Math.pow(0.88, frames));
      if (Math.abs(this.targetSpeedFactor - this.speedFactor) < 0.005) this.speedFactor = this.targetSpeedFactor;
      if (this.speedFactor === 0) {
        this.requestId = undefined;
        return;
      }

      // Out of view (see init): park instead of queuing another frame, but only
      // once the speed has settled, so it comes back at the speed it would have
      // eased to on screen.
      if (this.offscreen && this.speedFactor === this.targetSpeedFactor) {
        this.parked = true;
        this.requestId = undefined;
        return;
      }

      // Set the decrement of position x
      this.marquee.x -= this.speed * 0.1 * this.speedFactor;

      // Settle position into the slider
      this.marquee.settle(this.marquee.x);

      // Set the requestId to the local variable
      this.requestId = requestAnimationFrame(step);
    };
    this.requestId = requestAnimationFrame(step);
  }

  pause() {
    if (this.requestId) {
      // Cancel the animation
      cancelAnimationFrame(this.requestId);

      // Reset the requestId for the next animation.
      this.requestId = undefined;
    }
    this.parked = false;
  }

  getWidth(element) {
    const rect = element.getBoundingClientRect();
    return rect.right - rect.left;
  }
}
customElements.define('logo-list', LogoList);

class TextScrolling extends HTMLElement {
  constructor() {
    super();

    this.items.forEach((item) => {
      const header = item.querySelector('.heading');

      // Linear so the symmetric keyframes land where they read: fully visible
      // through the middle of the viewport, fading in below and out above.
      // Motion's default 'ease' skewed the whole timeline - a heading crossing
      // viewport center was almost invisible and only reached full opacity
      // near the top of the screen.
      Motion.scroll(
        Motion.animate(header, { opacity: [0, 0, 1, 1, 1, 0, 0] }, { easing: 'linear' }),
        { target: header, offsets: ['33vh', '66vh'] }
      );
    });
  }

  get items() {
    return this._items = this._items || Array.from(this.children);
  }
}
customElements.define('text-scrolling', TextScrolling);

class TabsElement extends HTMLElement {
  constructor() {
    super();

    this.selectedIndex = this.selectedIndex;
    this.buttons.forEach((button, index) => button.addEventListener('click', () => this.selectedIndex = index));

    if (Shopify.designMode) {
      this.addEventListener('shopify:block:select', (event) => this.selectedIndex = this.buttons.indexOf(event.target));
    }
  }

  static get observedAttributes() {
    return ['selected-index'];
  }

  get selectedIndex() {
    return parseInt(this.getAttribute('selected-index')) || 0;
  }

  set selectedIndex(index) {
    this.setAttribute('selected-index', Math.min(Math.max(index, 0), this.buttons.length - 1).toString());
  }

  get buttons() {
    return this._buttons = this._buttons || Array.from(this.querySelectorAll('button[role="tab"]'));
  }

  get indicators() {
    return this._indicators = this._indicators || Array.from(this.querySelectorAll('.indicators'));
  }

  load() {
    const toButton = this.buttons[parseInt(this.selectedIndex)];
    if (toButton === undefined) return;

    const toPanel = document.getElementById(toButton.getAttribute('aria-controls'));
    Motion.animate(toPanel, { transform: ['translateY(1rem)', 'translateY(0)'], opacity: [0, 1] }, { duration: theme.config.motionReduced ? 0 : 0.42, easing: [0.22, 1, 0.36, 1] });
    
    const motionList = toPanel.querySelector('motion-list');
    if (motionList && motionList.initialized) {
      motionList.load();
    }
  }

  unload() {
    const fromButton = this.buttons[parseInt(this.selectedIndex)];
    if (fromButton === undefined) return;

    const fromPanel = document.getElementById(fromButton.getAttribute('aria-controls'));
    Motion.animate(fromPanel, { transform: ['translateY(0)', 'translateY(2rem)'], opacity: [1, 0] }, { duration: theme.config.motionReduced ? 0 : 0.15 });

    const motionList = fromPanel.querySelector('motion-list');
    if (motionList && motionList.initialized) {
      motionList.unload();
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    this.buttons.forEach((button, index) => {
      button.classList.toggle('button--primary', index === parseInt(newValue));
      button.classList.toggle('button--secondary', index !== parseInt(newValue));
      button.disabled = index === parseInt(newValue);
    });

    this.indicators.forEach((indicators, index) => {
      indicators.hidden = index !== parseInt(newValue);
      if (index === parseInt(newValue)) {
        indicators.querySelectorAll('button').forEach((button) => {
          button.unload();
          button.load();
        });
      }
    });

    if (name === 'selected-index' && oldValue !== null && oldValue !== newValue) {
      const fromButton = this.buttons[parseInt(oldValue)];
      const toButton = this.buttons[parseInt(newValue)];
      this.transition(document.getElementById(fromButton.getAttribute('aria-controls')), document.getElementById(toButton.getAttribute('aria-controls')));
    }
  }

  // A calm crossfade: the outgoing panel fades out quickly from wherever it
  // is (so a fast second click never flashes it back to full opacity), then
  // the new one fades up a short distance on an ease-out - rather than both
  // dropping/rising a full 2rem on the default curve.
  async transition(fromPanel, toPanel) {
    if (fromPanel) {
      await Motion.animate(fromPanel, { opacity: 0 }, { duration: theme.config.motionReduced ? 0 : 0.16, easing: [0.25, 0.1, 0.25, 1] }).finished;
    }
    
    fromPanel.hidden = true;
    toPanel.hidden = false;
    Motion.animate(toPanel, { transform: ['translateY(1rem)', 'translateY(0)'], opacity: [0, 1] }, { duration: theme.config.motionReduced ? 0 : 0.42, easing: [0.22, 1, 0.36, 1] });

    const motionList = toPanel.querySelector('motion-list');
    if (motionList && motionList.initialized) {
      motionList.load();
    }
    
    theme.a11y.trapFocus(this, toPanel.querySelector('slider-element') || toPanel.querySelector('a'));
  }
}
customElements.define('tabs-element', TabsElement);

class CountdownTimer extends HTMLElement {
  constructor() {
    super();

    this.calculateHandler = this.calculate.bind(this);
    Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' });
  }

  get date() {
    return this._date = this._date || new Date(this.getAttribute('data-expires'));
  }

  get isCompact() {
    return this.getAttribute('data-compact') === 'true' || (this.getAttribute('data-compact') === 'mobile' && theme.config.mqlSmall);
  }

  init() {
    this.initialized = true;
    this.start();
  }

  connectedCallback() {
    if (this.initialized) this.start();
  }

  disconnectedCallback() {
    clearInterval(this.timerInterval);
    this.timerInterval = null;
  }

  start() {
    if (this.timerInterval || this.hidden) return;

    this.calculate();
    if (!this.hidden) this.timerInterval = setInterval(this.calculateHandler, 1000);
  }

  calculate() {
    const now = new Date(),
      countTo = new Date(this.date),
      timeDifference = (countTo - now);

    if (timeDifference < 0) {
      this.complete();
      return;
    }

    const secondsInADay = 60 * 60 * 1000 * 24,
      secondsInAHour = 60 * 60 * 1000;

    const days = Math.floor(timeDifference / (secondsInADay) * 1);
    const hours = Math.floor((timeDifference % (secondsInADay)) / (secondsInAHour) * 1);
    const mins = Math.floor(((timeDifference % (secondsInADay)) % (secondsInAHour)) / (60 * 1000) * 1);
    const secs = Math.floor((((timeDifference % (secondsInADay)) % (secondsInAHour)) % (60 * 1000)) / 1000 * 1);

    if (this.isCompact) {
      const dayHTML = days > 0 ? `<div class="countdown__item"><p>${days}${theme.dateStrings.d}</p></div>` : '';
      const hourHTML = `<div class="countdown__item"><p>${hours}${theme.dateStrings.h}</p></div>`;
      const minHTML = `<div class="countdown__item"><p>${mins}${theme.dateStrings.m}</p></div>`;
      const secHTML = `<div class="countdown__item"><p>${secs}${theme.dateStrings.s}</p></div>`;

      this.innerHTML = dayHTML + hourHTML + minHTML + secHTML;
    }
    else {
      const dayHTML = days > 0 ? `<div class="countdown__item"><p>${days}</p><span>${days === 1 ? theme.dateStrings.day : theme.dateStrings.days}</span></div>` : '';
      const hourHTML = `<div class="countdown__item"><p>${hours}</p><span>${hours === 1 ? theme.dateStrings.hour : theme.dateStrings.hours}</span></div>`;
      const minHTML = `<div class="countdown__item"><p>${mins}</p><span>${mins === 1 ? theme.dateStrings.minute : theme.dateStrings.minutes}</span></div>`;
      const secHTML = `<div class="countdown__item"><p>${secs}</p><span>${secs === 1 ? theme.dateStrings.second : theme.dateStrings.seconds}</span></div>`;

      this.innerHTML = dayHTML + hourHTML + minHTML + secHTML;
    }
  }

  complete() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }

    this.hidden = true;
  }
}
customElements.define('countdown-timer', CountdownTimer);

class ImageComparison extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, async () => {
      await theme.utils.imageLoaded(this.media);
      this.init();
    });
  }

  get button() {
    return this.querySelector('button');
  }

  get bounding() {
    return this.getBoundingClientRect();
  }

  get horizontal() {
    return this.getAttribute('data-layout') === 'horizontal';
  }

  get media() {
    return Array.from(this.querySelectorAll('img, svg'));
  }

  init() {
    this.active = false;

    this.button.addEventListener('touchstart', this.startHandler.bind(this), theme.supportsPassive ? { passive: false } : false);
    this.button.addEventListener('mousedown', this.startHandler.bind(this));

    this.endListener = this.endHandler.bind(this);
    this.moveListener = this.onHandler.bind(this);
    this.addBodyListeners();

    setTimeout(() => this.animate(), 1e3);
  }

  // The drag listeners live on <body>; drop them while this element is out of
  // the document (e.g. replaced by a Theme Editor re-render) so they don't
  // outlive it, and restore them if it is inserted again.
  addBodyListeners() {
    if (this.bodyListeners) return;
    this.bodyListeners = new AbortController();
    const signal = this.bodyListeners.signal;

    document.body.addEventListener('touchend', this.endListener, theme.supportsPassive ? { passive: true, signal } : { signal });
    document.body.addEventListener('touchmove', this.moveListener, theme.supportsPassive ? { passive: true, signal } : { signal });
    document.body.addEventListener('mouseup', this.endListener, { signal });
    document.body.addEventListener('mousemove', this.moveListener, { signal });
  }

  connectedCallback() {
    if (this.endListener) this.addBodyListeners();
  }

  disconnectedCallback() {
    this.bodyListeners?.abort();
    this.bodyListeners = null;
  }

  animate() {
    this.setAttribute('animate', '');

    this.classList.add('animated');
    setTimeout(() => {
      this.classList.remove('animated');
    }, 1e3);
  }

  startHandler(event) {
    event.preventDefault();
    this.active = true;
    this.classList.add('scrolling');
  }

  endHandler() {
    this.active = false;
    this.classList.remove('scrolling');
  }

  onHandler(e) {
    if (!this.active) return;
    
    const event = (e.touches && e.touches[0]) || e;
    let x = this.horizontal
                ? event.pageX - (this.bounding.left + window.scrollX)
                : event.pageY - (this.bounding.top + window.scrollY);
                
    this.scrollIt(x);
  }

  scrollIt(x) {
    const distance = this.horizontal ? this.clientWidth : this.clientHeight;
    
    const max = distance - 20;
    const min = 20;
    const mouseX = Math.max(min, (Math.min(x, max)));
    const mousePercent = (mouseX * 100) / distance;
    this.style.setProperty('--percent', mousePercent + '%');
  }
}
customElements.define('image-comparison', ImageComparison);

class LookbookElement extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, async () => {
      await theme.utils.imageLoaded(this.media);
      this.init();
    });
  }

  get media() {
    return Array.from(this.querySelectorAll('img, svg'));
  }

  get items() {
    return this._items = this._items || Array.from(this.querySelectorAll('.hotspot'));
  }

  init() {
    this.items.forEach((item) => {
      item.addEventListener('mouseenter', (event) => {
        this.select(this.items.indexOf(event.target));
      });
    });

    this.items.forEach((item) => {
      item.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.select(this.items.indexOf(event.target));
        }
      });
    });

    if (Shopify.designMode) {
      const section = this.closest('.shopify-section');
      section.addEventListener('shopify:section:select', this.animate.bind(this));
      section.addEventListener('shopify:section:deselect', this.closeAll.bind(this));
      this.addEventListener('shopify:block:select', (event) => this.open(this.items.indexOf(event.target)));
    }

    setTimeout(() => this.animate(), 1e3);
  }

  animate() {
    this.openAll();

    this.timer = setTimeout(() => this.closeAll(), 3e3);
  }

  open(selectedIndex) {
    this.items.forEach((item, index) => item.classList.toggle('active', selectedIndex === index));
  }

  openAll() {
    this.items.forEach((item) => item.classList.add('active'));
  }

  closeAll() {
    this.items.forEach((item) => item.classList.remove('active'));
    clearTimeout(this.timer);
  }

  select(selectedIndex) {
    const selectedItem = this.items[selectedIndex];
    if (!selectedItem || selectedItem.getAttribute('aria-current') === 'true') {
      return;
    }

    this.items.forEach((item, index) => item.setAttribute('aria-current', selectedIndex === index ? 'true' : 'false'));
    this.dispatchEvent(new CustomEvent('lookbook:change', { bubbles: true, detail: { index: selectedIndex } }));
  }
}
customElements.define('lookbook-element', LookbookElement);

class ShopTheLook extends HTMLElement {
  constructor() {
    super();

    this.lookbook.addEventListener('lookbook:change', (event) => this.carousel.select(event.detail.index));
    this.carousel.addEventListener('carousel:change', (event) => this.lookbook.select(event.detail.index));
  }

  get lookbook() {
    return this.querySelector('lookbook-element');
  }

  get carousel() {
    return this.querySelector('carousel-element');
  }
}
customElements.define('shop-the-look', ShopTheLook);

class SpinningText extends HTMLElement {
  constructor() {
    super();

    if (!theme.config.isTouch || Shopify.designMode) {
      Motion.inView(this, this.init.bind(this), { margin: '600px 0px 600px 0px' });
    }
    else {
      new theme.initWhenVisible(this.init.bind(this));
    }
  }

  get string() {
    let string = this.getAttribute('data-string');
    string = string.replace(/  +/g, ' ');
    return '•' + string.replace(/ +/g, '•')
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;

    const canTrig = CSS.supports('(top: calc(sin(1) * 1px))');
    const OPTIONS = {
      TEXT: this.string,
      SIZE: 2,
      SPACING: 2,
    };

    const HEADING = document.createElement('div');
    const text = OPTIONS.TEXT;

    // Take the text and split it into spans...
    const chars = text.split('');
    this.style.setProperty('--char-count', chars.length);

    for (let c = 0; c < chars.length; c++) {
      HEADING.innerHTML += `<span aria-hidden="true" class="split-char" style="--char-index: ${c};">${chars[c]}</span>`;
    }
    HEADING.innerHTML += `<span class="sr-only">${OPTIONS.TEXT}</span>`;
    HEADING.classList = 'split-chars';

    // Set the styles
    this.style.setProperty('--font-size', OPTIONS.SIZE);
    this.style.setProperty('--character-width', OPTIONS.SPACING);
    this.style.setProperty(
      '--radius',
      canTrig
        ? 'calc((var(--character-width) / sin(var(--inner-angle))) * -1ch)'
        : `calc(
        (${OPTIONS.SPACING} / ${Math.sin(
            360 / this.children.length / (180 / Math.PI)
          )})
        * -1ch
      )`
    );

    // Append
    this.appendChild(HEADING);
  }
}
customElements.define('spinning-text', SpinningText);

class SlideshowElement extends HTMLElement {
  constructor() {
    super();

    this.selectedIndex = this.selectedIndex;

    if (!theme.config.isTouch || Shopify.designMode) {
      Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' });
    }
    else {
      new theme.initWhenVisible(theme.utils.throttle(this.init.bind(this)));
    }
  }

  static get observedAttributes() {
    return ['selected-index'];
  }

  get selectedIndex() {
    return parseInt(this.getAttribute('selected-index')) || 0;
  }

  set selectedIndex(index) {
    this.setAttribute('selected-index', Math.min(Math.max(index, 0), this.items.length - 1).toString());
  }

  get items() {
    return this._items = this._items || Array.from(this.children);
  }

  get autoplay() {
    return this.hasAttribute('autoplay');
  }

  get speed() {
    return this.hasAttribute('autoplay') ? parseInt(this.getAttribute('autoplay-speed')) * 1000 : 5000;
  }

  get adaptiveHeightProperty() {
    return false;
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;

    const that = this;
    if (this.items.length > 1) {
      this.slider = new Flickity(this, {
        accessibility: false,
        pageDots: false,
        prevNextButtons: false,
        wrapAround: true,
        rightToLeft: theme.config.rtl,
        autoPlay: this.autoplay ? this.speed : false,
        adaptiveHeightProperty: this.adaptiveHeightProperty,
        on: {
          ready: function() {
            const { selectedElement } = this;
            that.onReady(selectedElement);
          }
        }
      });

      this.slider.on('change', this.onChange.bind(this));
      this.addEventListener('slider:previous', () => this.slider.previous());
      this.addEventListener('slider:next', () => this.slider.next());
      this.addEventListener('slider:play', () => this.slider.playPlayer());
      this.addEventListener('slider:pause', () => this.slider.pausePlayer());
  
      if (Shopify.designMode) {
        this.addEventListener('shopify:block:select', (event) => this.slider.select(this.items.indexOf(event.target)));
      }
    }
    else if (this.items.length === 1) {
      const selectedElement = this.firstChild;
      that.onReady(selectedElement);
    }
  }

  disconnectedCallback() {
    if (this.slider) this.slider.destroy();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'selected-index' && oldValue !== null && oldValue !== newValue) {
      const fromElement = this.items[parseInt(oldValue)];
      const toElement = this.items[parseInt(newValue)];

      const animateElement = toElement.querySelector('animate-element');
      animateElement?.refresh();

      if (fromElement.getAttribute('data-type') === 'video') {
        const fromVideoElement = theme.utils.visibleMedia(fromElement.querySelectorAll('video-media'));
        fromVideoElement?.pause();
      }

      if (toElement.getAttribute('data-type') === 'video') {
        const toVideoElement = theme.utils.visibleMedia(toElement.querySelectorAll('video-media'));
        toVideoElement?.play();
      }
    }
  }

  onChange() {
    this.selectedIndex = this.slider.selectedIndex;
    this.dispatchEvent(new CustomEvent('slider:change', { bubbles: true, detail: { currentPage: this.slider.selectedIndex } }));
  }

  select(selectedIndex) {
    this.slider.select(selectedIndex);
  }

  onReady(selectedElement) {
    if (selectedElement.getAttribute('data-type') === 'video') {
      const videoElement = theme.utils.visibleMedia(selectedElement.querySelectorAll('video-media'));
      videoElement?.play();
    }

    if (!theme.config.isTouch && document.body.hasAttribute('data-page-transition')) {
      const animateElement = selectedElement.querySelector('animate-element');
      animateElement?.refresh();
    }
  }
}
customElements.define('slideshow-element', SlideshowElement);

class SlideshowHero extends SlideshowElement {
  constructor() {
    super();

    this.scrollHandler = theme.utils.throttle(this.onScrollHandler.bind(this));
  }

  connectedCallback() {
    window.addEventListener('scroll', this.scrollHandler, false);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('scroll', this.scrollHandler, false);
  }

  get adaptiveHeightProperty() {
    return true;
  }

  get heroSection() {
    return this.closest('.hero-section');
  }

  get nextSection() {
    let nextSibling = this.heroSection.nextElementSibling;
    for (let i = 0; i < 20; i++) {
      if (nextSibling.classList.contains('shopify-section') && !nextSibling.classList.contains('header-section')) break;
      nextSibling = nextSibling.nextElementSibling;
    }

    if (nextSibling && nextSibling.nodeType != 1) {
      nextSibling = document.getElementById('PageContainer');
    }

    return nextSibling;
  }

  onScrollHandler() {
    const bounding = this.nextSection.getBoundingClientRect();
    this.heroSection.classList.toggle('hero-scrolled', bounding.y < -30);
  }
}
customElements.define('slideshow-hero', SlideshowHero);

class SlideshowWords extends HTMLElement {
  constructor() {
    super();

    this.selectedIndex = this.selectedIndex;

    if (this.controlledElement) {
      this.controlledElement.addEventListener('slider:change', (event) => this.selectedIndex = event.detail.currentPage);
    }
  }

  static get observedAttributes() {
    return ['selected-index'];
  }

  get selectedIndex() {
    return parseInt(this.getAttribute('selected-index')) || 0;
  }

  set selectedIndex(index) {
    this.setAttribute('selected-index', Math.min(Math.max(index, 0), this.items.length - 1).toString());
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }

  get items() {
    return this._items = this._items || Array.from(this.children);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'selected-index' && oldValue !== null && oldValue !== newValue) {
      this.transition(this.items[parseInt(oldValue)], this.items[parseInt(newValue)]);
    }
  }

  transition(fromElement, toElement) {
    const fromWords = Array.from(fromElement.querySelectorAll('animate-element'));
    const toWords = Array.from(toElement.querySelectorAll('animate-element'));

    fromWords.forEach((element) => element.reset());

    setTimeout(() => {
      this.items.forEach((item) => {
        item.setAttribute('aria-current', parseInt(item.getAttribute('data-index')) === parseInt(this.selectedIndex) ? 'true' : 'false');
      });
      
      toWords.forEach((element) => element.refresh());
    }, 500 + (30 * fromWords.length));
  }
}
customElements.define('slideshow-words', SlideshowWords, { extends: 'nav' });

class SlideshowParallax extends HTMLDivElement {
  constructor() {
    super();

    this.load();
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }

  get mobileDisabled() {
    return false;
  }

  load() {
    if (theme.config.motionReduced) return;
    if (this.mobileDisabled && (theme.config.isTouch || theme.config.mqlSmall)) return;

    this.motion();
  }

  motion() {
    this.animation = Motion.scroll(
      Motion.animate(this, { transform: [`translateY(${this.getAttribute('data-start')})`, `translateY(${this.getAttribute('data-stop')})`] }),
      { target: this.controlledElement ?? document.body, offset: Motion.ScrollOffset.Exit }
    );
  }
}
customElements.define('slideshow-parallax', SlideshowParallax, { extends: 'div' });

class ControlButton extends HTMLButtonElement {
  constructor() {
    super();

    this.addEventListener('click', this.onClick);

    this.pauseLabel = this.getAttribute('data-pause-label');
    this.playLabel = this.getAttribute('data-play-label');
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  static get observedAttributes() {
    return ['paused'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'paused') {
      this.updateAriaLabel();

      if (oldValue === null && newValue === '') {
        return (this.controlledElement ?? this).dispatchEvent(new CustomEvent('slider:pause', { bubbles: true }));
      }

      if (newValue === null) {
        return (this.controlledElement ?? this).dispatchEvent(new CustomEvent('slider:play', { bubbles: true }));
      }
    }
  }

  onClick() {
    this.paused ? this.removeAttribute('paused') : this.setAttribute('paused', '');
  }

  updateAriaLabel() {
    this.setAttribute('aria-label', this.paused ? this.playLabel : this.pauseLabel);
  }
}
customElements.define('control-button', ControlButton, { extends: 'button' });

class QuickView extends XModal {
  constructor() {
    super();
  }

  get selector() {
    return '.quick-view__content';
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.cartUpdateUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, this.hide.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  beforeShow() {
    super.beforeShow();
    this.quickview();
  }

  afterShow() {
    super.afterShow();

    document.dispatchEvent(new CustomEvent('quickview:open', {
      detail: {
        productUrl: this.getAttribute('data-product-url')
      }
    }));
  }

  afterHide() {
    super.afterHide();

    const drawerContent = this.querySelector(this.selector);
    drawerContent.innerHTML = '';
  }

  quickview() {
    const drawerContent = this.querySelector(this.selector);
    const productUrl = this.getAttribute('data-product-url').split('?')[0];
    const sectionUrl = `${productUrl}?view=modal`;

    fetch(sectionUrl)
      .then(response => response.text())
      .then(responseText => {
        setTimeout(() => {
          const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
          const productElement = parsedHTML.querySelector(this.selector);
          this.setInnerHTML(drawerContent, productElement.innerHTML);
          theme.a11y.trapFocus(this, this.focusElement);

          if (window.Shopify && Shopify.PaymentButton) {
            Shopify.PaymentButton.init();
          }

          document.dispatchEvent(new CustomEvent('quickview:loaded', {
            detail: {
              productUrl: this.getAttribute('data-product-url')
            }
          }));
        }, 200);
      })
      .catch(e => {
        console.error(e);
      });
  }

  setInnerHTML(element, innerHTML) {
    element.innerHTML = innerHTML;

    // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
    element.querySelectorAll('script').forEach(oldScriptTag => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach(attribute => {
        newScriptTag.setAttribute(attribute.name, attribute.value)
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}
customElements.define('quick-view', QuickView);

class FiltersToggle extends HoverButton {
  constructor() {
    super();

    this.addEventListener('click', this.onClick);

    if (theme.config.hasLocalStorage) {
      const expanded = window.localStorage.getItem(`${theme.settings.themeName}:filters-toggle`) || 'false';
      this.onToggle(expanded === 'true');
    }
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }

  get expanded() {
    return this.getAttribute('aria-expanded') === 'true';
  }

  onClick() {
    this.onToggle(this.expanded);
  }

  onToggle(expanded) {
    this.setAttribute('aria-expanded', expanded ? 'false' : 'true');
    if (this.controlledElement) this.controlledElement.classList.toggle('xl:block', !expanded);

    if (theme.config.hasLocalStorage) {
      window.localStorage.setItem(`${theme.settings.themeName}:filters-toggle`, expanded);
    }
  }
}
customElements.define('filters-toggle', FiltersToggle, { extends: 'button' });

class RevealBanner extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, this.init.bind(this), { margin: '600px 0px 600px 0px' });
  }

  init() {
    const tracker = this.querySelector('.reveal-banner__tracker');
    // Reduced motion: show the image full-bleed rather than growing it out
    // of a small window as the page scrolls. The overlay/text fade below
    // still runs.
    if (!theme.config.motionReduced) {
      Motion.scroll(
        Motion.animate(this.querySelectorAll('.banner__media'), { clipPath: ['inset(20% 35% 0% 35% round var(--rounded-block))', 'inset(0% 0%)'] }, { easing: 'linear' }),
        { target: tracker, offset: Motion.ScrollOffset.Enter }
      );
    }
    
    const tracker2nd = this.querySelector('.reveal-banner__tracker2nd');
    if (tracker2nd) {
      Motion.scroll(Motion.timeline([
        [this.querySelector('.banner__overlay'), { opacity: [0, 1] }, { easing: 'linear' }],
        [this.querySelector('.banner__content'), { opacity: [0, 1], visibility: ['hidden', 'visible'], transform: ['translateY(10%)', 'translateY(0)'] }, { easing: 'linear', at: '<' }],
      ]), { target: tracker2nd, offset: Motion.ScrollOffset.Enter });
    }
  }
}
customElements.define('reveal-banner', RevealBanner);

class TestimonialParallax extends ParallaxElement {
  constructor() {
    super();
  }

  get mobileDisabled() {
    return true;
  }
}
customElements.define('testimonial-parallax', TestimonialParallax, { extends: 'div' });

class SplittingBanner extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, this.init.bind(this), { margin: '600px 0px 600px 0px' });
  }

  init() {
    const splitting = Splitting({ target: this.querySelector('.splitting'), by: 'chars', whitespace: true });

    const wrapper = this.querySelector('.splitting-wrapper');
    const tracker = this.querySelector('.reveal-banner__tracker');
    Motion.scroll(Motion.timeline([
      [splitting[0].chars, { opacity: [0.3, 1] }, { delay: theme.config.motionReduced ? 0 : Motion.stagger(0.1) }],
      //[wrapper, { transform: ['translateY(-20vh)', 'translateY(0)'] }, { easing: 'linear', at: '<' }]
    ]), { target: tracker, offset: Motion.ScrollOffset.Enter });

    Motion.scroll(
      Motion.animate(wrapper, { opacity: theme.config.mqlSmall ? [1, 0, 0] : [1, 1, 0] }, { easing: 'linear' }),
      { target: tracker, offset: Motion.ScrollOffset.Exit }
    );

    const tracker2nd = this.querySelector('.reveal-banner__tracker2nd');
    if (tracker2nd) {
      Motion.scroll(
        Motion.animate(this.querySelector('.button-wrapper'), { opacity: [0, 1, 1], transform: ['translateY(100%)', 'translateY(0)'] }, { easing: 'linear' }),
        { target: tracker2nd, offset: Motion.ScrollOffset.Enter }
      );
    }
  }
}
customElements.define('splitting-banner', SplittingBanner);

class ProductCardInfo extends ProductInfo {
  constructor() {
    super();

    this._sectionId = 'ProductCard-template';
  }

  get pickerType() {
    return this.getAttribute('data-picker-type');
  }

  buildRequestUrlWithParams(url, optionValues) {
    const params = [];
    params.push('view=bundle-card');

    if (optionValues.length) {
      params.push(`option_values=${optionValues.join(',')}`);
    }

    return `${url}?${params.join('&')}`;
  }

  updateSourceFromDestination(parsedHTML, id) {
    const source = parsedHTML.getElementById(`${id}-${this._sectionId}-${this.productId}`);
    const destination = document.querySelector(`#${id}-${this.sectionId}-${this.productId}`);
    if (source && destination) {
      destination.innerHTML = source.innerHTML;
      destination.removeAttribute('hidden');
    }
  }

  updateOptionValues(parsedHTML) {
    const variantPicker = parsedHTML.getElementById(`VariantPicker-${this._sectionId}-${this.productId}-${this.pickerType}`);
    if (variantPicker) {
      theme.HTMLUpdateUtility.viewTransition(this.variantSelectors, variantPicker, this.preProcessHtmlCallbacks);
    }
  }

  getSelectedVariant(productInfoNode) {
    const selectedVariant = productInfoNode.querySelector(`product-card-info[data-product-id="${this.productId}"] [data-selected-variant]`)?.textContent;
    return !!selectedVariant ? JSON.parse(selectedVariant) : null;
  }
}
customElements.define('product-card-info', ProductCardInfo);

class ProductBundleForm extends HTMLFormElement {
  constructor() {
    super();

    this.querySelector('[name="id"]').disabled = false;
    this.addEventListener('submit', this.onSubmitHandler);
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }

  get submitButton() {
    return this._submitButton = this._submitButton || this.querySelector('[type="submit"]');
  }

  getSelectedVariant() {
    const selectedVariant = this.querySelector('[data-selected-variant]')?.textContent;
    return !!selectedVariant ? JSON.parse(selectedVariant) : null;
  }

  onSubmitHandler(event) {
    event.preventDefault();
    if (this.submitButton.hasAttribute('aria-disabled')) return;

    const product = this.closest('.product-card');
    const variant = this.getSelectedVariant();
    variant.default_featured_media = this.hasAttribute('data-product-image') ? this.getAttribute('data-product-image') : '';

    this.submitButton.removeAttribute('aria-busy');
    (this.controlledElement ?? this).dispatchEvent(new CustomEvent('bundle:added', { bubbles: true, detail: { product: product, variant: variant } }));
  }

  handleErrorMessage() {
    // todo
  }

  toggleSubmitButton(disable = true, text) {
    if (!this.submitButton) return;

    const submitButtonText = this.submitButton.querySelector('.btn-text');
    const submitButtonTextChild = this.submitButton.querySelector('.btn-text>span');

    if (disable) {
      this.submitButton.setAttribute('disabled', '');
      if (text) {
        (submitButtonTextChild || submitButtonText).textContent = text;
      }
    }
    else {
      this.submitButton.removeAttribute('disabled');
      (submitButtonTextChild || submitButtonText).textContent = theme.variantStrings.addToBundle;
    }
  }
}
customElements.define('product-bundle-form', ProductBundleForm, { extends: 'form' });

class ProductBundleRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      const variant = event.target.closest('[data-product-bundle-variant]');
      (this.controlledElement ?? this).dispatchEvent(new CustomEvent('bundle:removed', { bubbles: true, detail: { variant: variant } }));
    });
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }
}
customElements.define('product-bundle-remove-button', ProductBundleRemoveButton);

class ProductBundleToggleButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', () => {
      this.controlledElement.classList.toggle('active');
    });
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }
}
customElements.define('product-bundle-toggle-button', ProductBundleToggleButton);

class ProductBundle extends HTMLElement {
  constructor() {
    super();

    this.template;
    this.bundleCount = 0;

    this.addEventListener('bundle:added', this.onAddHandler.bind(this));
    this.addEventListener('bundle:removed', this.onRemoveHandler.bind(this));
    this.addEventListener('change', theme.utils.debounce(this.onChangeHandler.bind(this), 300));
    this.submitButton.addEventListener('click', this.onSubmitHandler.bind(this));
  }

  get bundleMin() {
    return this.hasAttribute('data-minimum') ? parseInt(this.getAttribute('data-minimum')) : 3;
  }

  get bundleMax() {
    const minimum = this.bundleMin;
    const maximum = this.hasAttribute('data-maximum') ? parseInt(this.getAttribute('data-maximum')) : 3;
    return maximum > minimum ? maximum : minimum;
  }

  get variants() {
    return Array.from(this.querySelectorAll('[data-product-bundle-variant]'));
  }

  get submitButton() {
    return this.querySelector('[data-product-bundle-submit]');
  }

  get cartDrawer() {
    return document.querySelector('cart-drawer');
  }

  get preventDuplicate() {
    return this.hasAttribute('data-prevent-duplicate');
  }

  get template() {
    return this._template = this._template || this.querySelector('[data-product-bundle-variant][available]').cloneNode(true);
  }

  onChangeHandler() {
    this.updateTotal();
    this.updateTotalWithCurrency();
    this.updateProgressBar();
  }

  onSubmitHandler(event) {
    const validateQuantity = this.variants.every((variant) => {
      const target = variant.querySelector('quantity-input').input;
      const inputValue = parseInt(target.value);
      let message = '';

      if (inputValue < parseInt(target.getAttribute('data-min'))) {
        message = theme.quickOrderListStrings.minError.replace('[min]', target.getAttribute('data-min'));
      }
      else if (inputValue > parseInt(target.max)) {
        message = theme.quickOrderListStrings.maxError.replace('[max]', target.max);
      }
      else if (inputValue % parseInt(target.step) !== 0) {
        message = theme.quickOrderListStrings.stepError.replace('[step]', target.step);
      }

      if (message) {
        target.setCustomValidity(message);
        target.reportValidity();
        target.value = target.defaultValue;
        target.select();

        return false;
      }

      return true;
    });
    if (!validateQuantity) return;

    const data = {
      items: this.variants.map(variant => ({
        id: variant.getAttribute('data-variant-id'),
        quantity: variant.querySelector('quantity-input').value || 1
      }))
    };

    if (document.body.classList.contains('template-cart') || theme.settings.cartType === 'page') {
      theme.utils.postLink2(theme.routes.cart_add_url, {
        parameters: {
          ...data
        }
      });
      return;
    }
    
    event.preventDefault();
    if (this.submitButton.hasAttribute('aria-disabled')) return;
    this.activeElement = event.submitter || event.currentTarget;

    this.handleErrorMessage();

    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }));
    
    const body = JSON.stringify({
      ...data,
      sections: [...new Set(sectionsToBundle)],
      sections_url: window.location.pathname
    });

    theme.cartFeedback?.pending(this.submitButton);
    this.submitButton.setAttribute('aria-disabled', 'true');
    this.submitButton.setAttribute('aria-busy', 'true');

    theme.utils.queueCartMutation(() => fetch(theme.routes.cart_add_url, { ...theme.utils.fetchConfig('javascript'), body })
      .then((response) => response.json())
      .then(async (parsedState) => {
        if (parsedState.status) {
          theme.cartFeedback?.settle(this.submitButton);
          this.handleErrorMessage(parsedState.description);
          document.dispatchEvent(new CustomEvent('ajaxProduct:error', {
            detail: {
              errorMessage: parsedState.description
            }
          }));
          
          const submitButtonText = this.submitButton.querySelector('.btn-text>span');
          if (submitButtonText && submitButtonText.hasAttribute('data-sold-out')) {
            submitButtonText.innerText = submitButtonText.getAttribute('data-sold-out');
            this.submitButton.setAttribute('aria-disabled', 'true');
            this.error = true;
          }

          const cartJson = await theme.utils.getCartState(parsedState);
          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'product-bundle', cart: cartJson });
          
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: false } }));
          this.resetVariants();
          return;
        }

        const cartJson = await theme.utils.getCartState(parsedState);
        cartJson['sections'] = parsedState['sections'];

        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'product-bundle', cart: cartJson });
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
          detail: {
            product: parsedState
          }
        }));

        const opensDrawer = !theme.utils.isFreeCartAdd(parsedState);
        if (opensDrawer) {
          this.cartDrawer?.show(this.activeElement);
        }
        theme.cartFeedback?.added(this.submitButton, { drawer: opensDrawer && !!this.cartDrawer });
        this.resetVariants();
      }))
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        theme.cartFeedback?.settle(this.submitButton);
        this.submitButton.removeAttribute('aria-busy');

        if (!this.error) {
          this.submitButton.removeAttribute('aria-disabled');
        }
      });
  }

  handleErrorMessage(errorMessage = false) {
    if (this.hideErrors) return;
    
    this.errorMessage = this.errorMessage || this.querySelector('.product-form__error-message');
    if (!this.errorMessage) return;

    this.errorMessage.toggleAttribute('hidden', !errorMessage);
    this.errorMessage.innerText = errorMessage;
  }

  onAddHandler(event) {
    this.addVariant(event.detail.product, event.detail.variant);
    this.updateTotal();
    this.updateTotalWithCurrency();
    this.updateProgressBar();
  }

  onRemoveHandler(event) {
    this.clearVariant(event.detail.variant);
    this.reorderVariants();
    this.updateTotal();
    this.updateTotalWithCurrency();
    this.updateProgressBar();
  }

  updateTotal() {
    let subtotal = 0;
    this.variants.forEach((variant) => {
      if (!variant.hasAttribute('available')) {
        const price = variant.querySelector('[data-product-bundle-variant-price]');
        const quantityInput = variant.querySelector('quantity-input');
        subtotal += parseInt(price.getAttribute('data-price')) * parseInt(quantityInput.value);
      }
    });

    const element = this.querySelector('[data-product-bundle-total]');
    element.innerHTML = theme.Currency.formatMoney(subtotal, theme.settings.moneyFormat);
  }

  updateTotalWithCurrency() {
    let subtotal = 0;
    this.variants.forEach((variant) => {
      if (!variant.hasAttribute('available')) {
        const price = variant.querySelector('[data-product-bundle-variant-price]');
        const quantityInput = variant.querySelector('quantity-input');
        subtotal += parseInt(price.getAttribute('data-price')) * parseInt(quantityInput.value);
      }
    });

    const element = this.querySelector('[data-product-bundle-total-with-currency]');
    element.innerHTML = theme.Currency.formatMoney(subtotal, theme.settings.moneyWithCurrencyFormat);
  }

  updateProgressBar() {
    const percent = 100 * this.bundleCount / this.bundleMin;
    const element = this.querySelector('[data-product-bundle-progress-bar]');
    element.style.setProperty('--progress', `${percent}%`);
  }

  addVariant(product, variant) {
    this.bundleCount++;

    if (this.bundleCount >= this.bundleMin) {
      this.submitButton.disabled = false;
    }
    if (this.bundleCount >= this.bundleMax) {
      this.closest('.product-bundle-wrapper').setAttribute('locked', '');
    }

    let variantElement = this.getAvailableAvriant();
    if (variantElement === null) {
      variantElement = this.template.cloneNode(true);
      variantElement.style.order = this.bundleCount;
      this.querySelector('.product-bundle__body').appendChild(variantElement);
    }

    if (variantElement) {
      const preview_image = variant.featured_media ? variant.featured_media.preview_image.src : variant.default_featured_media;
      const media = variantElement.querySelector('[data-product-bundle-variant-media]');
      if (preview_image.length > 0) {
        media.removeAttribute('aria-hidden');
        media.innerHTML = `<img src="${this.getResizedImageSrc(preview_image, 180)}" srcset="${this.getResizedImageSrc(preview_image, 180)} 180w, ${this.getResizedImageSrc(preview_image, 360)} 360w, ${this.getResizedImageSrc(preview_image, 540)} 540w" loading="lazy" is="lazy-image" />`;
      }

      const content = variantElement.querySelector('[data-product-bundle-variant-content]');
      content.innerHTML = `
        <div class="flex flex-col gap-1">
          <p class="horizontal-product__title font-medium text-base leading-tight">${product.querySelector('[data-product-bundle-title]').textContent}</p>
          ${variant.options.length > 0 ? `
            <ul class="grid gap-1d5">
              ${variant.options.map(option => {
                if (option !== 'Default Title') {
                 return `<li class="text-xs text-opacity leading-tight">${option}</li>`; 
                }
              }).join('')}
            </ul>
          ` : ''}
        </div>
        <div class="price text-sm flex flex-wrap gap-1d5" data-price="${variant.price}" data-product-bundle-variant-price>
          ${theme.Currency.formatMoney(variant.price, theme.settings.moneyFormat)}
        </div>`;

      variantElement.product = product;
      variantElement.setAttribute('data-variant-id', variant.id);
      variantElement.removeAttribute('available');
      if (this.preventDuplicate) {
        product.setAttribute('locked', '');
      }
    }
  }

  clearVariant(variant) {
    if (this.preventDuplicate) {
      variant.product.removeAttribute('locked');
    }
    variant.setAttribute('available', '');
    variant.removeAttribute('data-variant-id');
    variant.querySelector('[data-product-bundle-variant-media]').innerHTML = '';
    variant.querySelector('[data-product-bundle-variant-media]').classList.remove('loading');
    variant.querySelector('[data-product-bundle-variant-media]').setAttribute('aria-hidden', 'true');
    variant.querySelector('[data-product-bundle-variant-content]').innerHTML = '<span class="horizontal-product__skeleton"></span><span class="horizontal-product__skeleton"></span>';
    variant.querySelector('quantity-input').reset();

    this.bundleCount--;

    if (this.bundleCount < this.bundleMin) {
      this.submitButton.disabled = true;
    }
    else {
      variant.remove();
    }
    if (this.bundleCount < this.bundleMax) {
      this.closest('.product-bundle-wrapper').removeAttribute('locked');
    }
  }

  reorderVariants() {
    let order = 0;
    let minimum = Math.max(this.bundleMin, this.bundleCount) * -1;
    this.variants.forEach((variant) => {
      variant.style.order = variant.hasAttribute('available') ? order++ : minimum++;
    });
  }

  resetVariants() {
    this.variants.forEach((variant) => {
      this.clearVariant(variant);
    });

    this.reorderVariants();
    this.updateTotal();
    this.updateTotalWithCurrency();
    this.updateProgressBar();
  }

  getAvailableAvriant() {
    return this.querySelector('[data-product-bundle-variant][available]');
  }

  getResizedImageSrc(src, size) {
    return `${src}${src.includes("?")?"&":"?"}width=${size}&height=${size}`.replace(/\n|\r|\s/g, "");
  }
}
customElements.define('product-bundle', ProductBundle);

class NumberCounter extends HTMLElement {
  constructor() {
    super();

    if (theme.config.motionReduced) return;

    // Start counting only once the number is actually on screen, on every
    // device. The old 200px look-ahead spent the fast opening of the
    // ease-out off-screen, and on touch initWhenVisible fired on the first
    // tap/scroll anywhere on the page (or after 5s) - long before a counter
    // further down came into view, so mobile visitors never saw it count.
    // No negative margin: the text is the final value until init() resets
    // it, so starting any later would show that value and then snap to 0.
    Motion.inView(this, this.init.bind(this));
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;

    const matches = this.textContent.trim().match(/\d+(?:[,. ]\d+)*/);
    const toReplace = matches[0].replace(/[,\. ]+/, "");

    Motion.animate((progress) => {
      const formattedString = Math.round(progress * parseInt(toReplace)).toString();
      const text = progress === 1 ? matches[0] : formattedString;
      if (this.textContent !== text) this.textContent = text;
    }, { duration: parseFloat(this.getAttribute('data-duration')), easing: [0.16, 1, 0.3, 1] });
  }
}
customElements.define('number-counter', NumberCounter);

class ScrollingBanner extends HTMLElement {
  constructor() {
    super();

    if (!theme.config.isTouch || Shopify.designMode) {
      Motion.inView(this, this.init.bind(this), { margin: '600px 0px 600px 0px' });
    }
    else {
      new theme.initWhenVisible(this.init.bind(this));
    }
  }

  get media() {
    return this._media = this._media || Array.from(this.querySelectorAll('.image-with-text__image>.media'));
  }

  get stickyElement() {
    return this.querySelector('sticky-element');
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;

    this.detectScrollListener = theme.utils.throttle(this.onScrollHandler.bind(this));

    this.mql = window.matchMedia('screen and (min-width: 1024px)');
    if (this.mql.matches) this.load();
    this.onMediaChange = (mql) => {
      mql.matches ? this.load() : this.unload();
    };
    this.mql.onchange = this.onMediaChange;
  }

  connectedCallback() {
    // Moved in the DOM (e.g. sections reordered in the theme editor): re-attach
    // what disconnectedCallback detached.
    if (!this.detached) return;
    this.detached = false;
    this.mql.onchange = this.onMediaChange;
    if (this.mql.matches) this.load();
  }

  disconnectedCallback() {
    // A removed banner (e.g. after a theme editor re-render) kept its window
    // scroll listener and went on toggling the text blocks of the live section
    // (same aria-controls ids) from its own, detached geometry.
    if (!this.initialized) return;
    this.detached = true;
    window.removeEventListener('scroll', this.detectScrollListener);
    this.mql.onchange = null;
  }

  setMediaHeight() {
    let height = 0;
    this.media.forEach((media) => {
      height += media.getBoundingClientRect().height;
    });
    this.style.setProperty('--scrolling-height', `${height.toFixed(1)}px`);
  }

  onScrollHandler() {
    const scrollTop = window.scrollY;

    if (this.currentScrollTop === scrollTop) return;
    this.currentScrollTop = scrollTop;
    const offsetTop = this.getBoundingClientRect().top - parseFloat(this.stickyElement.inset);

    const mediaElements = this.media;
    const mediaCount = mediaElements.length;

    if (offsetTop < 0) {
      let activeIndex = 1;
      const mediaHeights = mediaElements.map((media, index) => index > 0 ? media.getBoundingClientRect().height : 0);

      // Start from second element
      for (let i = 1; i < mediaCount; i++) {
        const media = mediaElements[i];
        const mediaHeight = mediaHeights[i];
        const mediaScrollTop = mediaHeight * (i - 1);

        if (Math.abs(offsetTop) > mediaScrollTop) {
          activeIndex = i;
          // Continuous, sub-pixel wipe. Flooring to whole percents moved the
          // edge in 1%-of-image steps (7-9px on a full-height image), and the
          // old 5% snap at each end made the wipe jump open and jump shut.
          // The 5%/95% thresholds now only decide when the text block swaps.
          const progress = Math.min(100, Math.max(0, 100 - (Math.abs(offsetTop) - mediaScrollTop) / mediaHeight * 100));

          media.style.clipPath = `polygon(0 ${progress.toFixed(2)}%, 100% ${progress.toFixed(2)}%, 100% 100%, 0 100%)`;
          this.updateControlledElement(media, progress > 95 ? 100 : (progress < 5 ? 0 : progress));
        }
        else {
          media.style.clipPath = '';
        }
      }

      // Update remaining elements
      for (let i = activeIndex + 1; i < mediaCount; i++) {
        const media = mediaElements[i];
        this.updateControlledElement(media, 100);
      }
    }
    else {
      // Reset all elements when scrolled to top
      for (let i = 1; i < mediaCount; i++) {
        const media = mediaElements[i];
        media.style.clipPath = '';
        this.updateControlledElement(media, 100);
      }
    }
  }

  updateControlledElement(media, progress) {
    const controlledElement = media.hasAttribute('aria-controls') ? document.getElementById(media.getAttribute('aria-controls')) : null;
    if (controlledElement) {
      if (progress === 100) controlledElement.classList.add('opacity-0', 'pointer-events-none');
      if (progress === 0) controlledElement.classList.remove('opacity-0', 'pointer-events-none');
    }
  }

  load() {
    this.setMediaHeight();
    window.addEventListener('scroll', this.detectScrollListener, false);
  }

  unload() {
    window.removeEventListener('scroll', this.detectScrollListener);
    this.style.removeProperty('--scrolling-height');
    this.media.forEach((media) => {
      media.style.clipPath = '';
    });
  }
}
customElements.define('scrolling-banner', ScrollingBanner);

class SecondaryVideo extends HTMLDivElement {
  constructor() {
    super();

    if (theme.config.motionReduced || theme.config.isTouch) return;

    if (this.videoMedia) {
      this.addEventListener('mouseenter', this.onEnterHandler);
      this.addEventListener('mouseleave', this.onLeaveHandler);
    }
  }

  get videoMedia() {
    return this.querySelector('video-media');
  }

  onEnterHandler() {
    const media = this.videoMedia;
    if (media && media.hasAttribute('autoplay')) {
      media.play();
    }
  }

  onLeaveHandler() {
    const media = this.videoMedia;
    if (media) {
      media.pause();
    }
  }
}
customElements.define('secondary-video', SecondaryVideo, { extends: 'div' });

class SocialFeed extends XModal {
  constructor() {
    super();

    this.onKeyup = this.onKeyup.bind(this);
  }

  get shouldCloseAll() {
    return true;
  }

  get lookbook() {
    return this.querySelector('lookbook-element');
  }

  get previousButton() {
    return this.querySelector('button[data-button="previous"]');
  }

  get nextButton() {
    return this.querySelector('button[data-button="next"]');
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.onKeyup);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.onKeyup);
  }

  beforeShow() {
    super.beforeShow();
    this.lookbook.closeAll();
  }

  async afterShow() {
    super.afterShow();
    Motion.inView(this.lookbook, async () => {
      await theme.utils.imageLoaded(this.lookbook.media);
      this.lookbook.animate();
    });
  }

  onKeyup(event) {
    if (event.code === 'ArrowLeft' || event.code === 'ArrowRight') {
      const button = event.code === 'ArrowLeft' ? this.previousButton : this.nextButton;
      if (button && !button.disabled) {
        event.preventDefault();
        button.dispatchEvent(new Event('click'));
      }
    }
  }
}
customElements.define('social-feed', SocialFeed);

class SocialFeedButton extends HTMLButtonElement {
  constructor() {
    super();

    this.addEventListener('click', this.onButtonClick);
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }

  onButtonClick() {
    if (this.controlledElement) this.controlledElement.show();
  }
}
customElements.define('social-feed-button', SocialFeedButton, { extends: 'button' });

class IconsCarousel extends HTMLDivElement {
  constructor() {
    super();

    this.onUpdateTabindex();

    this.onUpdateTabindex = this.onUpdateTabindex.bind(this);
    this.onEnterListener = this.onEnterHandler.bind(this);
    this.onLeaveListener = this.onLeaveHandler.bind(this);
  }

  get isScrollable() {
    const style = window.getComputedStyle(this);
    const hasScrollableOverflow = style.overflow === 'scroll' || style.overflow === 'auto' || style.overflowY === 'scroll' || style.overflowY === 'auto' || style.overflowX === 'scroll' || style.overflowX === 'auto';
    const hasScrollableContent = this.scrollHeight > this.clientHeight || this.scrollWidth > this.clientWidth;
    
    return hasScrollableOverflow && hasScrollableContent;
  }

  connectedCallback() {
    if (theme.config.isTouch) return;

    this.addEventListener('mouseenter', this.onEnterListener);
    this.addEventListener('mouseleave', this.onLeaveListener);
    document.addEventListener('matchSmall', this.onUpdateTabindex);
    document.addEventListener('unmatchSmall', this.onUpdateTabindex);
  }

  disconnectedCallback() {
    if (theme.config.isTouch) return;
    
    this.removeEventListener('mouseenter', this.onEnterListener);
    this.removeEventListener('mouseleave', this.onLeaveListener);
    document.removeEventListener('matchSmall', this.onUpdateTabindex);
    document.removeEventListener('unmatchSmall', this.onUpdateTabindex);
  }

  onEnterHandler() {
    const scrollWidth = this.scrollWidth * (theme.config.rtl ? -1 : 1);
    this.scrollTo({
      left: scrollWidth,
      behavior: theme.config.motionReduced ? 'auto' : 'smooth'
    });
  }

  onLeaveHandler() {
    this.scrollTo({
      left: 0,
      behavior: theme.config.motionReduced ? 'auto' : 'smooth'
    });
  }

  onUpdateTabindex() {
    this.removeAttribute('tabindex');

    if (this.isScrollable) {
      this.setAttribute('tabindex', '0');
    }
  }
}
customElements.define('icons-carousel', IconsCarousel, { extends: 'div' });

class ComparisonTable extends HTMLElement {
  constructor() {
    super();

    this.highlighted = false;
    this.tableSticky = this.querySelector('.comparison-table__sticky');
    if (this.tableSticky) {
      Motion.inView(this, this.init.bind(this));
    }
  }

  get items() {
    return Array.from(this.querySelectorAll('.comparison-table__row'));
  }

  init() {
    this.addEventListener('scroll', theme.utils.throttle(this.onScrollHandler.bind(this)));
    this.tableSticky.addEventListener('scroll', theme.utils.throttle(this.onStickyScrollHandler.bind(this)));

    Motion.scroll(({ y }) => 
      {
        if (y.current >= y.targetOffset + this.tableSticky.clientHeight / 2 && y.progress < 0.85) {
          this.tableSticky.classList.add('active');
        }
        else {
          this.tableSticky.classList.remove('active');
        }
      },
      { target: this, offset: ['0 start', 'end 0'] }
    );
  }

  onScrollHandler(event) {
    this.tableSticky.scrollTo({
      left: event.target.scrollLeft
    });
  }

  onStickyScrollHandler(event) {
    this.scrollTo({
      left: event.target.scrollLeft
    });
  }

  highlight() {
    this.items.forEach((item) => {
      const classesToCheck = ['thead', 'tfoot', 'sticky'];
      if (classesToCheck.every(className => !item.classList.contains(className))) {
        if (this.highlighted) {
          const columns = Array.from(item.querySelectorAll('.comparison-table__column'));
          const first_column = columns[0];
          
          let highlighted = false;
          columns.forEach((column) => {
            if (first_column.innerHTML != column.innerHTML) {
              highlighted = true;
            }
          });

          item.classList.toggle('info', highlighted);
        }
        else {
          item.classList.remove('info');
        }
      }
    });
  }
}
customElements.define('comparison-table', ComparisonTable);

class ProductComparison extends HTMLSelectElement {
  constructor() {
    super();

    this.oldValue = this.value;
    this.addEventListener('change', this.onChange);
  }

  get sectionId() {
    return this.getAttribute('data-section-id');
  }

  get column() {
    return this.getAttribute('data-column');
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }
  
  get items() {
    return Array.from(this.controlledElement.querySelectorAll('select[is="product-comparison"]'));
  }

  get selectedArr() {
    return this.items.map((item) => item !== this ? item.value : this.oldValue);
  }

  onChange() {
    const index = this.selectedArr.indexOf(this.value);
    if (index !== -1) {
      this.changeSelected(this.value);

      const sibling = this.items[index];
      if (sibling) {
        sibling.value = this.oldValue;
        sibling.dispatchEvent(new Event('change'));
      }
    }
    else {
      this.changeSelected(this.value);
    }
    this.oldValue = this.value;
  }

  changeSelected(productId) {
    const blockJSON = theme.ProductComparisonJSON[this.sectionId]?.[productId];
    if (typeof blockJSON === 'undefined') return;

    const column = this.items.indexOf(this) + 1;

    for (let index = 1; index <= 25; index++) {
      const element = this.controlledElement.querySelector(`[data-column="${column}"][data-index="${index}"]`);
      if (element) {
        const heading = blockJSON[`heading_${index}`];
        const value = blockJSON[`value_${index}`];
        element.innerHTML = heading.length ? `<p class="h6 leading-tight tracking-none">${heading}</p>${this.unescape(value)}` : this.unescape(value);
      }
    }

    const product = this.controlledElement.querySelector(`[data-product="${column}"]`);
    if (product) {
      product.innerHTML = document.getElementById(`ProductComparisonInfo-${this.sectionId}-${productId}`).innerHTML;
    }

    const productSticky = this.controlledElement.querySelector(`[data-product-sticky="${column}"]`);
    if (productSticky) {
      productSticky.innerHTML = document.getElementById(`ProductComparisonInfo-${this.sectionId}-${productId}`).innerHTML;
    }

    const productAction = this.controlledElement.querySelector(`[data-product-action="${column}"]`);
    if (productAction) {
      productAction.innerHTML = document.getElementById(`ProductComparisonAction-${this.sectionId}-${productId}`).innerHTML;
    }

    if (this.controlledElement) {
      this.controlledElement.highlight();
    }
  }

  unescape(input) {
    const doc = new DOMParser().parseFromString(input, 'text/html');
    return doc.documentElement.textContent;
  }
}
customElements.define('product-comparison', ProductComparison, { extends: 'select' });

class ComparisonHighlight extends HTMLInputElement {
  constructor() {
    super();

    this.addEventListener('change', this.onChange);
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }

  get items() {
    return Array.from(this.controlledElement.querySelectorAll('.comparison-table__row'));
  }

  onChange() {
    if (this.controlledElement) {
      this.controlledElement.highlighted = this.checked;
      this.controlledElement.highlight();
    }
  }
}
customElements.define('comparison-highlight', ComparisonHighlight, { extends: 'input' });

class ScrollSpy extends HTMLElement {
  constructor() {
    super();
  }

  get scrollspyLinks() {
    return Array.from(this.querySelectorAll('.scrollspy-link'));
  }

  get backToTopButton() {
    return this.querySelector('.back-to-top');
  }

  get headerHeight() {
    let header = document.querySelector('.header-section header[is="sticky-header"]');
    if (this.hasAttribute('aria-controls')) {
      header = document.getElementById(this.getAttribute('aria-controls'));
    }
    return header ? (header.getBoundingClientRect().height + this.topGap) : (this.offsetHeight < window.innerHeight ? this.topGap : 0);
  }

  get topGap() {
    return this.hasAttribute('data-gap') ? parseInt(this.getAttribute('data-gap')) : 20;
  }

  get showHighlight() {
    return this.hasAttribute('data-highlight');
  }

  connectedCallback() {
    this.listenerController?.abort();
    this.listenerController = new AbortController();
    const options = { signal: this.listenerController.signal };

    this.scrollspyLinks.forEach(link => {
      link.addEventListener('click', this.handleScrollspyLinkClick.bind(this), options);
    });
    
    if (this.backToTopButton) {
      this.backToTopButton.addEventListener('click', this.handleBackToTopClick.bind(this), options);
    }

    if (this.showHighlight) {
      window.addEventListener('scroll', this.updateActiveLink.bind(this), options);
    }
  }

  disconnectedCallback() {
    this.listenerController?.abort();
    this.listenerController = null;
  }

  handleScrollspyLinkClick(event) {
    event.preventDefault();
    const link = event.currentTarget;

    const targetIds = link.hasAttribute('data-target') ? link.getAttribute('data-target').split(',') : [];
    const section = document.getElementById(targetIds[0]);
    
    if (section) {
      const sectionTop = window.scrollY + section.getBoundingClientRect().top - this.headerHeight;
      window.scrollTo({
        top: sectionTop,
        behavior: theme.config.motionReduced ? 'auto' : 'smooth'
      });
    }
  }

  handleBackToTopClick() {
    window.scrollTo({
      top: 0,
      behavior: theme.config.motionReduced ? 'auto' : 'smooth'
    });
  }

  updateActiveLink() {
    let current = '';
      
    this.scrollspyLinks.forEach(link => {
      const targetIds = link.hasAttribute('data-target') ? link.getAttribute('data-target').split(',') : [];

      targetIds.forEach((targetId) => {
        const section = document.getElementById(targetId);
        
        if (section) {
          const offset = 0;
          const sectionTop = section.offsetTop - offset;
          const sectionBottom = sectionTop + section.offsetHeight;
          
          if (window.pageYOffset >= sectionTop && window.pageYOffset < sectionBottom) {
            current = targetId;
          }
        }
      });
    });
    
    this.scrollspyLinks.forEach(link => {
      const targetIds = link.hasAttribute('data-target') ? link.getAttribute('data-target').split(',') : [];
      let active = false;

      targetIds.forEach((targetId) => {
        if (targetId !== '' && targetId === current) {
          active = true;
        }
      });

      if (active) {
        link.setAttribute('aria-current', 'location');
        link.classList.add('active');
      }
      else {
        link.removeAttribute('aria-current');
        link.classList.remove('active');
      }
    });
  }
};
customElements.define('scroll-spy', ScrollSpy, { extends: 'nav' });


// Stages whose text animates in pieces (a letter or a word each) carry
// their size in px: --stage-w and --stage-h are a hundredth of the width
// and height, like 1cqw and 1cqh. iPhone Safari can lay out pieces whose
// font size comes from container units as if they had no width, which
// piles the letters on top of each other in the middle; sized from these,
// the pieces are laid out like any other text. Until a stage has a size
// (off screen, not yet laid out) its text waits at 0px, so the first real
// size is a change of font size Safari picks up.
(() => {
  const sized = new WeakSet();
  const set = (stage, width, height) => {
    stage.style.setProperty('--stage-w', `${width / 100}px`);
    stage.style.setProperty('--stage-h', `${height / 100}px`);
  };
  const observer = 'ResizeObserver' in window
    ? new ResizeObserver((entries) => entries.forEach(({ target, contentRect }) => {
        if (contentRect.width) set(target, contentRect.width, contentRect.height);
      }))
    : null;
  const arm = (root) => root.querySelectorAll('[data-stage-size]').forEach((stage) => {
    if (sized.has(stage)) return;
    sized.add(stage);
    const style = getComputedStyle(stage);
    const width = stage.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
    const height = stage.clientHeight - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom);
    if (width > 0 || observer) set(stage, Math.max(width, 0), Math.max(height, 0));
    observer?.observe(stage);
  });
  arm(document);
  document.addEventListener('shopify:section:load', (event) => arm(event.target));
})();
